import {
  Component,
  OnInit,
  EventEmitter,
  Output,
  Input,
  OnDestroy,
  HostListener,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ChangeDetectorRef } from '@angular/core';
import { JwtHelperService } from '@auth0/angular-jwt';
import { TooltipComponent } from '../../../shared/tooltip/tooltip.component';
import { AreaQuantityInputComponent } from '../../../shared/area-quantity-input/area-quantity-input.component';

import {
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
  Validators,
  FormsModule,
} from '@angular/forms';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import {
  firstValueFrom,
  forkJoin,
  from,
  Observable,
  of,
  throwError,
} from 'rxjs';
import {
  catchError,
  map,
  shareReplay,
  switchMap,
  take,
  tap,
} from 'rxjs/operators';
import { environment } from '../../../../../environments/environment';
import { AuthService } from '../../../shared/services/auth.service';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { setThrowInvalidWriteToSignalError } from '@angular/core/primitives/signals';
import { CanDeactivate, NavigationStart, Router } from '@angular/router';
import { AutoSaveService } from '../auto-save-service';
import { CostItemEditData } from '../mobile-calc-view/cost-item-offcanvas/cost-item-offcanvas.component';

interface OperationResponse {
  operationID: number;
  operationTypeID: number;
  // add any other properties returned by the API
}
interface BOVOComponentDto {
  bovoComponentID: number;
  bovoHeaderID: number;
  variableOverheadItemID: number;
  usageQuantity: number;
  batchSize: number;
  uomid: number;
  notes: string;
}
export interface UnitOfMeasure {
  uomid: number;
  uomCode: string;
  uomName: string;
}
interface BOVOHeaderDto {
  bovoHeaderID: number;
  finishedGoodItemID: number;
  bovoVersion: string | null;
  description: string;
  isActive: boolean;
  validFrom: string;
  validTo: string | null;
  createdAt: string;
  userIDCreated: number | null;
}

interface SupplierDTO {
  supplierID: number;
  companyName: string;
}
interface CategoryDTO {
  categoryID: number;
  categoryName: string;
}
interface ItemDTO {
  itemID: number;
  itemCode: string;
  itemName: string;
  categoryID: number;
  description: string;
  itemTypeID: number;
  purchaseQuantity: number;
  purchaseUOMID: number;
  supplierID: number | null;
  lowStockThreshold: number;
  isActive: boolean;
  createdAt: string;
  createdBy: string;
  updatedAt: string;
  sellingPrice: number;
  profitMarginPercent: number;
  purchasePrice: number;
  valuationMethodCode: string;
}

export interface FixedOverheadItem {
  fixedOverheadID?: number; // Optional, indicates if it's an existing item
  overhead: string;
  customOverhead?: string;
  cost: number | null; // <-- CHANGE: Allow cost to be null
  periodTypeID: number | null;
  isEditing: boolean;
  invalidOverhead: boolean;
  invalidCost: boolean;
  invalidperiodTypeID: boolean;
  overheadAllocationRateID: number | null;
  isNew?: boolean; // Mark as new when added
  isModified?: boolean; // Mark as modified when changes occur
  originalItem?: FixedOverheadItem; // Already allows undefined, so no change needed here
  createdAt: string;
  validTo: string;
  validFrom: string;
}

export interface PeriodType {
  periodTypeID: number | null; // Make it number or null
  description: string;
}
export interface OverheadCostItemDTO {
  overheadCostItemID: number | null;
  overheadName: string;
  description?: string;
  createdAt?: string;
  updatedAt?: string;
  // Enrichment fields (pulled from allocation rate + operating schedule on load)
  costPerMonth?: number;
  hoursOpenPerMonth?: number;
  ratePerMinute?: number;
}
interface OverheadAllocationRateDTO {
  overheadAllocationRateID: number;
  overheadCostItemID: number;
  periodTypeID: number;
  rateValue: number;
  currencyCode: string;
  validFrom: string;
  validTo: string;
  createdAt: string;
  updatedAt: string;
}
interface LabourRateResponse {
  labourRateID: number;
  // add others if needed
}
interface ItemOrderDuration {
  itemOrderDurationID: number;
  itemID: number;
  orderQuantity: number;
  totalTimeMinutes: number;
  recordedAt: string;
  recordedBy: string | null;
  batchSize: number;
}

export interface OverheadCostPool {
  createdAt: string;
  updatedAt: string;
  overheadCostPoolID: number;
  poolName: string;
  finishedGoodID: number;
  description: string;
  costType: string;
  isActive: boolean;
  overheadCostItems: OverheadCostItem[]; // ADDED THIS NESTED PROPERTY
}

interface OperatingSchedule {
  operatingScheduleID: number;
  dayOfWeek: number;
  openTime: string | null;
  closeTime: string | null;
  isClosed: boolean;
  businessID: number;
}

export interface OverheadCostItem {
  overheadCostItemID: number;
  overheadCostPoolID: number;
  overheadName: string;
  description: string | null;
  overheadAllocationRates: OverheadAllocationRate[]; // ADDED THIS NESTED PROPERTY
}

export interface OverheadAllocationRate {
  overheadAllocationRateID: number;
  overheadCostItemID: number;

  rateValue: number;
  currencyCode: string;
  validFrom: string;
  validTo: string | null;
  createdAt: string;
}
// Item Operation coming from /api/ItemOperation
interface EmployeeSalaryDTO {
  salaryID: number;
  employeeID: number;
  startDate: string; // or Date
  endDate?: string; // or Date|null
  amount: number;
  hoursWorked: number;
  currency: string;
  periodTypeID: number;
  // possibly effectiveFromDate/effectiveToDate synonyms; adjust if needed
}
const itemType = 2; //for finished\
export interface CombinedCostItem {
  productId: number;
  productName: string;

  /** Optional selling price pulled from Items table */
  sellingPrice?: number;

  /** e.g. “Raw Material”, “Direct Labor”, “Indirect Labor”, “Fixed Overhead”, “Variable Overhead”, etc. */
  category: string;

  /**
   * For raw/variable/fixed, this is the itemName.
   * For labor, this is the laborRole (or Activity).
   */
  itemOrRole: string;

  /** The unitCost for that item (already calculated). */
  unitCost: number;
}
export interface RawMaterial {
  // ── “Identity” ──
  inventoryId: number; // = component.rawMaterialItemID
  itemName: string;

  // ── Purchase/history enrichment (filled later) ──
  purchaseQuantity: number;
  purchasePrice: number;
  purchasePricePerUnit: number;

  // ── Usage/production info (from BOMComponent) ──

  // Units and cost fields (filled later) ──
  purchaseUnit: string;
  usageUnit: string;
  unitCost: number;

  // ── “Flags” ──
  isNew: boolean;
  isEditing: boolean;

  // ── Save-ready fields (copied from `Item`) ──
  itemCode: string;
  description: string;
  itemTypeID: number;
  baseUOMID: number;
  lowStockThreshold: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  sellingPrice: number;
  profitMarginPercent: number;
  targetCost: number;
  valuationMethodCode: string;
  categoryID: number;
  sku: string;

  // ── BOMComponent fields (exactly matches BOMComponentsDTO) ──
  bomComponentID: number; // = component.BOMComponentID
  bomHeaderID: number; // = component.BOMHeaderID
  componentUOMID: number; // = component.ComponentUOMID
  usageQuantity: number; // = component.QuantityRequired
  batchSize: number; // = component.UnitsProduced
  scrapFactor: number | null; // = component.ScrapFactor
  notes: string; // = component.Notes

  // ── Previously added history fields (populated later) ──
  supplierPriceHistoryId?: number;
  currencyCode?: string;
  purchaseUOMID?: number;
  uomConversionFactor?: number;
  supplierItemCode?: string;
  supplierLocationID?: number | null;
  leadTimeDays?: number | null;
  validFrom?: string;
  validTo?: string | null;
  historyCreatedAt?: string | null;
  userIDEntered?: number | null;
}

export interface Operation {
  operationID: number;
  operationCode: string;
  operationName: string;
  operationTypeID: number;
  description: string | null;
  workCenterID: number | null;
  isActive: boolean;
  operationType: any | null; // You can replace `any` with a specific type if you have one
  itemOperations: ItemOperation[]; // Replace with actual interface if you have it
}

export interface BOVOComponent {
  bovoComponentID: number;
  bovoHeaderID: number;
  variableOverheadItemID: number;

  purchasePrice: number;
  purchaseQuantity: number;
  usageQuantity: number;
  batchSize: number;

  uomid: number;
  notes: string;

  // optional if API provides these
  monthlyCost?: number;
  estimatedSales?: number;
}

export interface BOVOHeader {
  bovoHeaderID: number;
  finishedGoodItemID: number;
  bovoVersion: string;
  description: string;
  isActive: boolean;
  validFrom: string; // or Date if you parse it
  validTo: string | null;
  createdAt: string; // or Date
  userIDCreated: number;
}

interface ApiItemDTO {
  itemID: number;
  itemCode: string;
  itemName: string;
  sellingPrice?: number;
  /* …other props… */
}
export interface LabourRate {
  labourRateID: number;

  /** Employee ID linked to the labour rate */
  employeeID: number;

  /** Type of rate (e.g., hourly, monthly) */
  rateType: string;

  /** Actual rate value in the specified currency */
  rateValue: number;

  /** Total hours worked per period (likely per month) */
  hoursWorked: number;

  /** Currency code (e.g., ZAR, USD) */
  currency: string;

  /** Effective start date for the rate */
  effectiveFromDate: string;

  /** Optional effective end date for the rate */
  effectiveToDate: string | null;

  /** Timestamp of record creation */
  createdAt: string | null;

  /** Timestamp of last update */
  updatedAt: string | null;

  /** Operation type ID linked to the labour rate */
  operationTypeID: number;

  /** Rate description for UI display (optional, retained from original interface) */
  rateDescription?: string;

  /** Computed hourly rate (optional, retained from original interface) */
  ratePerHour?: number;

  /** Optional UI field, if still needed */
  currencyCode?: string;

  /** Optional UI field, if still needed */
  validFrom?: string;

  /** Optional UI field, if still needed */
  validTo?: string | null;
}

export interface SupplierPriceHistory {
  itemSupplierPriceID: number;
  itemID: number;
  supplierLocationID: number;
  purchasePrice: number;
  purchaseQuantity: number;
  currencyCode: string;
  purchaseUOMID: number;
  uomConversionFactor: number;
  supplierItemCode: string;
  leadTimeDays: number | null;
  validFrom: string; // ISO date
  validTo: string; // ISO date or null
  createdAt: string; // ISO date
  userIDEntered: number | null;
}

interface ItemTargetCostHistory {
  costHistoryID: number;
  itemID: number;
  standardCost: number;
  validFrom: string; // ISO date string
  validTo: string | null;
  // …other props…
}

interface BOMHeader {
  bomHeaderID: number;
  finishedGoodItemID: number;
}
interface ItemOperation {
  itemOperationID: number;
  finishedGoodItemID: number;
  operationID: number;
  sequenceNumber: number;
  standardSetupTimeMinutes: number;
  standardRunTimeMinutesPerUnit: number;
  labourRateIDDefault: number;
  activityIDDefault: number;
  batchSize: number;
  notes?: string | null;
  operationName: string;
}

interface BOMComponent {
  componentUOMID: number;
  scrapFactor: number;
  notes: string;
  bomComponentID: number;
  bomHeaderID: number;
  rawMaterialItemID: number;
  quantityRequired: number;
  batchSize: number;
}
interface Item {
  itemID: number;
  itemName: string;
  inventoryId: number; // maps to itemID
  purchasePrice: number;
  purchaseQuantity: number;
  purchasePricePerUnit: number;
  unitCost: number;
  usageQuantity: number;
  usageUnit: string;
  purchaseUnit: string;
  batchSize: number;
  isNew: boolean;
  isEditing: boolean;

  // For saving to Items table

  itemCode: string;

  categoryID: number;
  description: string;
  itemTypeID: number;

  purchaseUOMID: number;
  supplierID: number;
  lowStockThreshold: number;
  isActive: boolean;
  createdAt: string;
  createdBy: string;
  updatedAt: string;
  sellingPrice: number;
  profitMarginPercent: number;

  valuationMethodCode: string;
}
export interface ItemTargetHistoryDTO {
  itemTargetHistoryID: number;
  itemID: number | null;
  targetSellingPrice: number;
  targetProfitMarginPercent: number;
  targetCost: number | null;
  validFrom: string; // ISO string
  validTo?: string | null;
  reasonForChange?: string;
  userIDEntered?: number | null;
  createdAt: string; // ISO string
}
interface PeriodTypeDTO {
  periodTypeID: number;
  description: string;
}
export interface Competitor {
  competitorID: number;
  itemCompetitorID: number;
  itemID: number;
  name: string;
  price: number;
  isNew?: boolean;
  isEditing?: boolean;
  website?: string;
  isActive: boolean; // Changed to non-optional as per the DTO
  createdAt: string; // ISO string, Changed to non-optional as per the DTO
  updatedAt: string; // ISO string, Added as per the DTO
  targetMarketSegment?: string; // Added as per the DTO
  primarySalesChannel?: string; // Added as per the DTO
  geographicFocus?: string; // Added as per the DTO
  competitorTier?: string;
  notes?: string; // Kept this from the original DTO, assuming it might be used elsewhere
  // For index 0 (company target), include:
  fullTarget?: ItemTargetHistoryDTO;
}

export interface BillofMaterialsDTO {
  bomId: number;
  productId: number;
  rawMaterialId: number;
  quantityRequired: number;
}
interface ApiItem {
  inventoryId: number;
  itemName: string;
}

interface ItemTargetHistory {
  inventoryId: number;
  targetPrice: number;
  recordedAt: string;
}
interface Product {
  itemID: number;
  itemName: string;
  profitMarginPercent: number;
  sellingPrice?: number;
  /** 2 = Finished Good, 4 = Service (spec §2). */
  itemTypeID?: number;
  /** Raw material purchased and resold as-is (spec §4). */
  isBuyout?: boolean;
}
export interface LabourItem {
  operationname: any;
  inventoryId: number;

  /** Monthly salary in R */
  monthlySalary: number;
  activity: string;

  /** Total hours worked per month */
  hoursWorkedPm: number;

  /** Computed salary rate in R per minute (passed in as a prop) */
  ratePerMinute: number;

  /** Number of minutes required to make one unit (editable) */
  minutesPerJob: number;

  /** Computed unit cost = ratePerMinute * minutesPerJob (passed in as a prop) */
  unitCost: number;

  /** Full name of the employee doing the operation */
  employeeName: string;

  /** Role of the employee */
  roleDescription: string;

  labourRateID: number;
  itemOperationID: number;
  employeeID: number | null;
  roleID: number | null;
  batchSize: number;

  /** New required fields from LabourRates DTO */
  rateType: string;
  currency: string;
  effectiveFromDate: string;
  effectiveToDate: string | null;
  createdAt: string;
  updatedAt: string;
  operationTypeID: number;
  operationID: number;

  isNew: boolean;
  isEditing?: boolean;
}
export interface ItemSupplierPriceHistoryDTO {
  itemSupplierPriceID: number;
  itemID: number;
  supplierLocationID: number;
  purchasePrice: number;
  purchaseQuantity: number;
  currencyCode: string;
  purchaseUOMID: number; // This is the uomID you need
  uomConversionFactor: number;
  supplierItemCode?: string;
  leadTimeDays?: number | null;
  userIDEntered: number | null;
  validFrom: string;
  validTo: string | null; // Allow null here
  createdAt?: string | null; // Consider allowing null if your backend can send it
  updatedAt?: string | null; //
}

export interface IndirectLabourItem {
  operationname: any;
  inventoryId: number;
  batchSize: number;
  /** Monthly salary in R */
  monthlySalary: number;
  activity: string;

  /** Total hours worked per month */
  hoursWorkedPm: number;

  /** Computed salary rate in R per minute (passed in as a prop) */
  ratePerMinute: number;

  /** Number of minutes required to make one unit (editable) */
  minutesPerActivity: number;
  /** Computed unit cost = ratePerMinute * minutesPerJob (passed in as a prop) */
  unitCost: number;

  /** Full name of the employee doing the operation */
  employeeName: string;

  /** Role of the employee */
  roleDescription: string;

  labourRateID: number;
  itemOperationID: number;
  employeeID: number | null;
  roleID: number | null;

  /** New required fields from LabourRates DTO */
  rateType: string;
  currency: string;
  effectiveFromDate: string;
  effectiveToDate: string | null;
  createdAt: string;
  updatedAt: string;
  operationTypeID: number;
  operationID: number;

  isNew: boolean;
  isEditing?: boolean;
}

export interface Employee {
  employeeID: number;
  firstName: string;
  lastName: string;
  roleID: number;
  contactInfo?: string | null;
  hireDate: string; // ISO date string, e.g. "2025-06-18T00:00:00Z"
  isActive: boolean;
}

export interface Role {
  roleID: number;
  roleName: string;
  description: string;
}
export interface VariableOverheadItem {
  inventoryId: number | null;
  invalidLowStockThreshold: boolean; // This was correctly added in a previous fix
  variableOverheadID?: number;

  // Fix 1: lowStockThreshold
  lowStockThreshold?: number | null; // Made optional to match VariableOverhead
  isLocallyEditing?: boolean; // <-- NEW FLAG
  overhead: string | null;
  customOverhead?: string | null;
  overheadName?: string;
  supplierID: number | null;
  purchasePrice: number | null;
  purchaseQuantity: number | null;

  // Fix 2: uomID - It's optional in VariableOverhead, so make it optional here too.
  uomID?: number | null; // Changed from 'uomID: number | null;' to optional

  // Fix 3: leadTimeDays
  leadTimeDays?: number | null; // Made optional to match VariableOverhead

  // Fix 4: userIDEntered (THE NEW ERROR YOU JUST REPORTED)
  userIDEntered?: number | null; // Made optional to match VariableOverhead

  overheadCostPoolID: number;
  // Fix 5: categoryID - It's optional in VariableOverhead, so make it optional here too.
  categoryID?: number | null; // Changed from 'categoryID: number | null;' to optional

  // UI-specific flags. Ensure these are consistent if they exist on VariableOverhead as well.
  isNew?: boolean; // Keep as optional if it's a UI-only flag that might be undefined
  isModified?: boolean; // Keep as optional
  isEditing?: boolean; // Keep as optional
  originalItem?: VariableOverheadItem; // Keep as optional

  // Validation flags. These are usually UI-specific and should remain optional
  invalidOverhead?: boolean;
  invalidSupplier?: boolean;
  invalidPurchasePrice?: boolean;
  invalidPurchaseQuantity?: boolean;
  invalidUOM?: boolean;
  invalidCategory?: boolean;

  validFrom?: string;
  validTo?: string | null;

  itemID?: number | null;
  itemName?: string;
  isAutoPurchasePrice?: boolean;
  isAutoPurchaseQuantity?: boolean;
  supplierLocationID?: number | null;

  purchaseUnit?: string;
  usageQuantity?: number;
  usageUnit?: string;
  batchSize?: number;
  unitCost?: number;
  purchasePricePerUnit?: number;

  bovoComponentID?: number;
  bovoHeaderID?: number;
  unitsToMake?: number;
  variableOverheadItemID: number;
  notes?: string;
  supplierPriceHistoryId?: number;
  currencyCode?: string;
  purchaseUOMID?: number | null;
  uomConversionFactor?: number;
  supplierItemCode?: string;
  historyCreatedAt?: string;

  itemCode?: string;
  description?: string;
  itemTypeID?: number;
  baseUOMID?: number;
  isActive?: boolean;
  createdAt?: string;
  updatedAt?: string;
  targetSellingPrice?: number;
  targetProfitMarginPercent?: number;
  targetCost?: number;
  valuationMethodCode?: string;
  sku?: string;
}

/** Represents one row in the Variable Overheads tab */
export interface VariableOverhead {
  inventoryId: number | null;
  itemName: string; // Ensure this is not optional if it's always there.
  isLocallyEditing?: boolean; // <-- NEW FLAG
  purchasePrice: number | null; // Made nullable to match VariableOverheadItem
  purchaseQuantity: number | null; // Made nullable to match VariableOverheadItem
  purchaseUnit?: string; // Made optional to match VariableOverheadItem

  usageQuantity?: number; // Made optional to match VariableOverheadItem
  usageUnit?: string; // Made optional to match VariableOverheadItem

  batchSize?: number; // Made optional to match VariableOverheadItem
  purchasePricePerUnit?: number; // Made optional to match VariableOverheadItem
  unitCost?: number; // Made optional to match VariableOverheadItem

  monthlyCost: any; // Consider a more specific type
  estimatedSales: any; // Consider a more specific type

  bovoComponentID?: number;
  bovoHeaderID?: number;
  unitsToMake?: number;
  variableOverheadItemID: number; // Keep this consistent
  uomid?: number | null; // Add this and make nullable if it's optional in VariableOverheadItem
  notes?: string;

  supplierPriceHistoryId?: number;
  supplierLocationID?: number;
  currencyCode?: string;
  purchaseUOMID?: number | null;
  uomConversionFactor?: number;
  supplierItemCode?: string;
  leadTimeDays?: number | null;
  validFrom?: string;
  validTo?: string | null;
  historyCreatedAt?: string;
  userIDEntered?: number | null;

  itemCode?: string;
  description?: string;
  itemTypeID?: number;
  baseUOMID?: number;
  lowStockThreshold?: number | null; // Made nullable to match VariableOverheadItem
  isActive?: boolean;
  createdAt?: string;
  updatedAt?: string;
  targetSellingPrice?: number;
  targetProfitMarginPercent?: number;
  targetCost?: number;
  valuationMethodCode?: string;
  categoryID?: number | null; // Made nullable to match VariableOverheadItem
  sku?: string;

  // Properties *missing* from your original `VariableOverhead`
  // but present in `VariableOverheadItem` and causing the error.
  // These *must* be added to `VariableOverhead` to allow assignment to `VariableOverheadItem`.
  invalidLowStockThreshold: boolean; // Crucial
  overhead: string | null; // Crucial
  supplierID: number | null; // Crucial
  uomID: number | null; // Crucial (if different from uomid)
  overheadCostPoolID: number; // Crucial

  // UI-only flags, which were in your first `VariableOverheadItem`
  isNew: boolean; // Crucial
  isEditing?: boolean; // Crucial
  isModified?: boolean; // Added from VariableOverheadItem
  originalItem?: VariableOverheadItem; // Added from VariableOverheadItem
  invalidOverhead?: boolean; // Added from VariableOverheadItem
  invalidSupplier?: boolean; // Added from VariableOverheadItem
  invalidPurchasePrice?: boolean; // Added from VariableOverheadItem
  invalidPurchaseQuantity?: boolean; // Added from VariableOverheadItem
  invalidUOM?: boolean; // Added from VariableOverheadItem
  invalidCategory?: boolean; // Added from VariableOverheadItem
  itemID?: number | null; // Added from VariableOverheadItem (check if duplicate with inventoryId)
  isAutoPurchasePrice?: boolean; // Added from VariableOverheadItem
  isAutoPurchaseQuantity?: boolean; // Added from VariableOverheadItem
  customOverhead?: string | null; // Added from VariableOverheadItem
  overheadName?: string; // Added from VariableOverheadItem
}

// fixed OH item
export interface FixedItem {
  itemId: number | null;
  itemName: string;
  finishedGoodID: number | null;
  /** Rent input (R) — total rent allocated to this FOH activity per month */
  overheadCostItemID?: number | null; // <— Add this
  costPerMonth: number;
  /** Hours open per month (e.g. 160) */
  hoursOpenPerMonth: number;
  /** Computed rate in R per minute = rentPerMonth / (hoursOpenPerMonth × 60) */
  ratePerMinute: number;
  /** Minutes required to perform this activity per unit */
  minutesPerOrder: number;
  batchSize: number;
  /** Computed unit cost (R) = ratePerMinute × minutesPerJob */
  unitCost: number;
  overheadAllocationRateID: number;
  currencyCode: string;
  itemOrderDurationID: number;
  /** Flags for adding/editing rows */
  overheadCostPoolID: number;
  createdAt: string;
  updatedAt: string;
  rateType: string;
  isNew: boolean;
  isEditing?: boolean;
  isPlaceholder?: boolean;
  description: string | null;
}

type Category =
  | 'materials'
  | 'labour'
  | 'indirect-labour'
  | 'fixed'
  | 'variable'
  | 'competitor';

interface Tab {
  id: Category;
  label: string;
  abbr: string;
  icon: string;
  active: boolean;
}
interface UnitOfMeasureDTO {
  uomID: number; // This should match your item.uomID
  uomCode: string;
  uomName: string;
  // If your backend actually sends 'uomid', you might need to adjust this DTO
  // Or, if you want to keep the DTO reflecting the backend, cast it during the map
}
export interface SupplierLocationsDTO {
  locationID: number;
  supplierID: number;
  branchName?: string;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  region?: string;
  postalCode?: string;
  country?: string;
  locationContactPerson?: string;
  locationEmail?: string;
  locationPhone?: string;
  isActive: boolean;
  isPhysical: boolean;
  createdAt?: string;
  updatedAt?: string;
}


@Component({
  selector: 'app-raw-materials',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FormsModule, TooltipComponent, AreaQuantityInputComponent],
  templateUrl: './raw-materials.component.html',
  styleUrls: ['./raw-materials.component.css'],
})
export class RawMaterialsComponent
  implements OnInit, OnDestroy, CanDeactivate<RawMaterialsComponent>
{
  @Output() productNameChange = new EventEmitter<string>();
  @Output() allProductCostsLoaded = new EventEmitter<
    Record<string, CombinedCostItem[]>
  >();
  @Output() aiSuggestionLoaded = new EventEmitter<{
    aiSuggestedSellingPrice: number;
    aiSuggestedMarkup: number;
  }>();
  @Input() aiSuggestedSellingPrice: number = 0;
  @Input() aiSuggestedMarkup: number = 0;
  deletedBovoComponentIDs: number[] = []; // List to store bovoComponentIDs of items marked for deletion
  public allProductCosts: Record<string, CombinedCostItem[]> = {};
  hasUnsavedChanges = false;
  isNavigating = false;
  private labourItemsCache = new Map<
    string,
    LabourItem[] | IndirectLabourItem[]
  >();
  constructor(
    private fb: FormBuilder,
    private sanitizer: DomSanitizer,
    private http: HttpClient,
    private cdr: ChangeDetectorRef,
    private authService: AuthService,
    private router: Router,
    private autoSaveService: AutoSaveService // 👈 Add this
  ) {
    this.totalTargetCostAlert = this.sanitizer.bypassSecurityTrustHtml(`
      <div class="text-center">
        ⚠️Please set the target total cost for the current selected product.<br>
        The <strong class="text-primary">target total cost</strong> is the maximum allowable cost
        to produce an item and still achieve your desired profit.
      </div>
    `);

    // Handle browser refresh/close
    window.addEventListener('beforeunload', (event) => {
      if (this.hasUnsavedChanges) {
        event.preventDefault();
        event.returnValue = '';
      }

    });
  }

  upsertFromMobile(data: CostItemEditData): void {
    const productId = this.selectedProductId;
    if (!productId) return;

    switch (data.category) {
      case 'Raw Material':
        this.upsertMobileRawMaterial(productId, data);
        break;
      case 'Direct Labor':
        this.upsertMobileDirectLabour(productId, data);
        break;
      case 'Indirect Labor':
        this.upsertMobileIndirectLabour(productId, data);
        break;
      case 'Fixed Overhead':
        this.upsertMobileFixedOverhead(productId, data);
        break;
      case 'Variable Overhead':
        this.upsertMobileVariableOverhead(productId, data);
        break;
      case 'Competitor':
        this.upsertMobileCompetitor(productId, data);
        break;
      case 'Product':
        this.upsertMobileProduct(data);
        break;
      default:
        break;
    }
  }

  deleteFromMobile(data: CostItemEditData): void {
    const productId = this.selectedProductId;
    if (!productId) return;
    const pname = this.selectedProduct;

    switch (data.category) {
      case 'Raw Material': {
        const idx = this.materialItems.findIndex(
          (m) => m.bomComponentID === data.bomComponentID || m.inventoryId === data.inventoryId
        );
        if (idx < 0) return;
        const item = this.materialItems[idx];
        if (item.bomComponentID) {
          this.deleteRawMaterialFromBackend(item.bomComponentID).catch((e) =>
            console.error('deleteFromMobile raw material backend error:', e)
          );
        }
        this.materialItems.splice(idx, 1);
        this.materialItemsMap[productId] = this.materialItems;
        break;
      }
      case 'Direct Labor': {
        const idx = this.labourItems.findIndex((l) => l.itemOperationID === data.itemOperationID);
        if (idx < 0) return;
        const item = this.labourItems[idx];
        if (item.itemOperationID) {
          this.deleteLabourFromBackend(item.itemOperationID).catch((e) =>
            console.error('deleteFromMobile direct labour backend error:', e)
          );
        }
        this.labourItems.splice(idx, 1);
        break;
      }
      case 'Indirect Labor': {
        const idx = this.indirectLabourItems.findIndex((l) => l.itemOperationID === data.itemOperationID);
        if (idx < 0) return;
        const item = this.indirectLabourItems[idx];
        if (item.itemOperationID) {
          this.deleteLabourFromBackend(item.itemOperationID).catch((e) =>
            console.error('deleteFromMobile indirect labour backend error:', e)
          );
        }
        this.indirectLabourItems.splice(idx, 1);
        break;
      }
      case 'Fixed Overhead': {
        const idx = this.fixedItems.findIndex((f) => f.itemId === data.itemId);
        if (idx < 0) return;
        this.fixedItems.splice(idx, 1);
        break;
      }
      case 'Variable Overhead': {
        const idx = this.variableFull.findIndex(
          (v) => (v as any).variableOverheadItemID === data.variableOverheadItemID || v.inventoryId === data.inventoryId
        );
        if (idx < 0) return;
        const vohItem = this.variableFull[idx];
        if (vohItem.bovoComponentID) {
          this.deleteFromBackend('BOVOComponent', 'BOVOComponentId', vohItem.bovoComponentID, false).catch((e) =>
            console.error('deleteFromMobile variable overhead backend error:', e)
          );
        }
        this.variableFull.splice(idx, 1);
        this.variableOverheadsMap[productId] = this.variableFull;
        break;
      }
      case 'Competitor': {
        // Use competitorPricesMap as the source of truth for IDs (competitorPrices
        // copy may be stale if the HTTP save updated only the map entry).
        const mapList = this.competitorPricesMap[productId];
        if (!mapList) return;
        const mapIdx = mapList.findIndex((c) => c.competitorID === data.competitorID);
        if (mapIdx < 0) return;
        const competitor = mapList[mapIdx];
        if (competitor.itemCompetitorID) {
          this.deleteFromBackend('ItemCompetitor', 'ItemCompetitorId', competitor.itemCompetitorID, false)
            .catch((e) => console.error('deleteFromMobile competitor backend error:', e));
        }
        mapList.splice(mapIdx, 1);
        // Keep competitorPrices in sync with the map
        this.competitorPrices[productId] = mapList.map(c => ({ ...c }));
        this.competitors = [...mapList];
        break;
      }
      default:
        return;
    }

    this.emitCostDataChange();
    if (pname) {
      const updatedList = this.getCombinedCosts();
      this.allProductCosts[pname] = updatedList;
      this.allProductCosts = { ...this.allProductCosts };
      this.allProductCostsLoaded.emit(this.allProductCosts);
    }
  }

  upsertMobileProduct(data: CostItemEditData): void {
    const itemId = Number(data.inventoryId ?? 0);
    if (!itemId) return;

    const prod = this.products.find((p: any) => p.itemID === itemId);
    if (!prod) return;

    const token = localStorage.getItem('authToken') || '';
    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    });

    this.http.get<ItemDTO>(`${environment.apiUrl}/api/Items/GetById?ItemsId=${itemId}`, { headers })
      .subscribe({
        next: (full) => {
          const newName = (data.itemName || '').trim() || full.itemName;
          const newPrice = data.sellingPrice ?? full.sellingPrice;
          const newDesc = (data.description || '').trim() || full.description || '';

          const dto: ItemDTO = {
            ...full,
            itemName: newName,
            sellingPrice: newPrice,
            description: newDesc,
            updatedAt: new Date().toISOString(),
            createdBy: this.currentUser,
          };

          this.http.put(`${environment.apiUrl}/api/Items/Update?ItemId=${itemId}`, dto, { headers })
            .subscribe({
              next: () => {
                prod.itemName = newName;
                prod.sellingPrice = newPrice;
                this.emitCostDataChange();
              },
              error: (err) => console.error('Error updating product from mobile:', err),
            });
        },
        error: (err) => console.error('Error fetching product for mobile update:', err),
      });
  }

  private upsertMobileRawMaterial(productId: number, data: CostItemEditData): void {
    const list = this.materialItemsMap[productId] || this.materialItems || [];
    const inventoryId = Number(data.inventoryId ?? 0);

    const purchasePrice = Number(data.purchasePrice ?? 0);
    const purchaseQuantity = Number(data.purchaseQuantity ?? 0);
    const usageQuantity = Number(data.usageQuantity ?? 0);
    const batchSize = Math.max(1, Number(data.batchSize ?? 1));

    if (inventoryId > 0) {
      const existing = list.find((m) => m.inventoryId === inventoryId);
      if (existing) {
        existing.purchasePrice = purchasePrice;
        existing.purchaseQuantity = purchaseQuantity;
        existing.usageQuantity = usageQuantity;
        existing.batchSize = batchSize;
        if (data.purchaseUnit != null) existing.purchaseUnit = data.purchaseUnit;
        if (data.usageUnit != null) existing.usageUnit = data.usageUnit;
        this.calculateRawMaterialUnitCost(existing, this.conversionFactors);
      } else {
        // New item with inventory linkage (Full Plan add — not yet in the list)
        const newLinkedItem: RawMaterial = {
          inventoryId,
          itemName: (data.itemName || '').trim(),
          purchaseQuantity,
          purchasePrice,
          purchasePricePerUnit: purchaseQuantity > 0 ? +(purchasePrice / purchaseQuantity).toFixed(4) : 0,
          usageQuantity,
          unitCost: Number(data.unitCost ?? 0),
          isNew: false,
          batchSize,
          isEditing: false,
          purchaseUnit: data.purchaseUnit ?? '',
          usageUnit: data.usageUnit ?? '',
          itemCode: '',
          description: '',
          itemTypeID: 1,
          baseUOMID: 0,
          lowStockThreshold: 0,
          isActive: true,
          createdAt: '',
          updatedAt: '',
          sellingPrice: 0,
          profitMarginPercent: 0,
          targetCost: 0,
          valuationMethodCode: '',
          categoryID: 0,
          sku: '',
          bomComponentID: 0,
          bomHeaderID: 0,
          componentUOMID: 0,
          scrapFactor: null,
          notes: '',
        } as RawMaterial;
        this.calculateRawMaterialUnitCost(newLinkedItem, this.conversionFactors);
        list.unshift(newLinkedItem);
      }
    } else {
      const newItem: RawMaterial = {
        inventoryId: 0,
        itemName: (data.itemName || '').trim(),
        purchaseQuantity,
        purchasePrice,
        purchasePricePerUnit: purchaseQuantity > 0 ? +(purchasePrice / purchaseQuantity).toFixed(4) : 0,
        usageQuantity,
        unitCost: Number(data.unitCost ?? 0),
        isNew: true,
        batchSize,
        isEditing: false,
        purchaseUnit: data.purchaseUnit ?? '',
        usageUnit: data.usageUnit ?? '',
        itemCode: '',
        description: '',
        itemTypeID: 0,
        baseUOMID: 0,
        lowStockThreshold: 0,
        isActive: false,
        createdAt: '',
        updatedAt: '',
        sellingPrice: 0,
        profitMarginPercent: 0,
        targetCost: 0,
        valuationMethodCode: '',
        categoryID: 0,
        sku: '',
        bomComponentID: 0,
        bomHeaderID: 0,
        componentUOMID: 0,
        scrapFactor: null,
        notes: '',
      } as RawMaterial;

      list.unshift(newItem);
    }

    this.materialItems = list;
    this.materialItemsMap[productId] = list;
    this.emitCostDataChange();
  }

  private upsertMobileDirectLabour(productId: number, data: CostItemEditData): void {
    const list = this.directLaborItemsMap[productId] || this.labourItems || [];
    const itemOperationID = Number(data.itemOperationID ?? 0);

    const employeeID = data.employeeID ?? null;
    const emp =
      employeeID != null
        ? this.employees.find((e) => e.employeeID === employeeID)
        : undefined;
    const employeeName = emp ? `${emp.firstName} ${emp.lastName}` : '';
    const monthlySalary = Number(data.monthlySalary ?? 0);
    const hoursWorkedPm = Number(data.hoursWorkedPm ?? 0);
    const minutesPerJob = Number(data.minutesPerJob ?? 0);
    const batchSize = Math.max(1, Number(data.batchSize ?? 1));

    if (itemOperationID !== 0) {
      const existing = list.find((l) => l.itemOperationID === itemOperationID);
      if (existing) {
        existing.employeeID = employeeID;
        existing.employeeName = employeeName;
        existing.monthlySalary = monthlySalary;
        existing.hoursWorkedPm = hoursWorkedPm;
        existing.minutesPerJob = minutesPerJob;
        existing.batchSize = batchSize;
        this.onEmployeeSelect(existing);
        this.calculateDirectLabourItemUnitCost(existing);
      }
    } else {
      const newItem: LabourItem = {
        inventoryId: 0,
        monthlySalary,
        hoursWorkedPm,
        ratePerMinute: 0,
        minutesPerJob,
        isEditing: false,
        unitCost: Number(data.unitCost ?? 0),
        isNew: true,
        employeeName,
        roleDescription: '',
        activity: (data.itemName || '').trim(),
        labourRateID: 0,
        itemOperationID: -Date.now(),
        employeeID,
        roleID: null,
        rateType: '',
        currency: '',
        effectiveFromDate: '',
        effectiveToDate: null,
        createdAt: '',
        updatedAt: '',
        operationTypeID: 0,
        operationID: 0,
        batchSize,
        operationname: undefined,
      } as LabourItem;

      list.unshift(newItem);
      this.onEmployeeSelect(newItem);
      this.calculateDirectLabourItemUnitCost(newItem);
    }

    this.labourItems = list;
    this.directLaborItemsMap[productId] = list;
    this.emitCostDataChange();
  }

  private upsertMobileIndirectLabour(productId: number, data: CostItemEditData): void {
    const list = this.indirectLaborItemsMap[productId] || this.indirectLabourItems || [];
    const itemOperationID = Number(data.itemOperationID ?? 0);

    const employeeID = data.employeeID ?? null;
    const emp =
      employeeID != null
        ? this.employees.find((e) => e.employeeID === employeeID)
        : undefined;
    const employeeName = emp ? `${emp.firstName} ${emp.lastName}` : '';
    const monthlySalary = Number(data.monthlySalary ?? 0);
    const hoursWorkedPm = Number(data.hoursWorkedPm ?? 0);
    const minutesPerActivity = Number(data.minutesPerActivity ?? 0);
    const batchSize = Math.max(1, Number(data.batchSize ?? 1));

    if (itemOperationID !== 0) {
      const existing = list.find((l) => l.itemOperationID === itemOperationID);
      if (existing) {
        existing.employeeID = employeeID;
        existing.employeeName = employeeName;
        existing.monthlySalary = monthlySalary;
        existing.hoursWorkedPm = hoursWorkedPm;
        existing.minutesPerActivity = minutesPerActivity;
        existing.batchSize = batchSize;
        this.onEmployeeSelect(existing);
        this.calculateIndirectLabourItemUnitCost(existing);
      }
    } else {
      const newItem: IndirectLabourItem = {
        inventoryId: 0,
        batchSize,
        monthlySalary,
        hoursWorkedPm,
        ratePerMinute: 0,
        minutesPerActivity,
        activity: (data.itemName || '').trim(),
        operationname: undefined,
        employeeName,
        roleDescription: '',
        unitCost: Number(data.unitCost ?? 0),
        labourRateID: 0,
        itemOperationID: -Date.now(),
        employeeID,
        roleID: null,
        rateType: '',
        currency: '',
        effectiveFromDate: '',
        effectiveToDate: null,
        createdAt: '',
        updatedAt: '',
        operationTypeID: 0,
        operationID: 0,
        isNew: true,
        isEditing: false,
      } as IndirectLabourItem;

      list.unshift(newItem);
      this.onEmployeeSelect(newItem);
      this.calculateIndirectLabourItemUnitCost(newItem);
    }

    this.indirectLabourItems = list;
    this.indirectLaborItemsMap[productId] = list;
    this.emitCostDataChange();
  }

  private upsertMobileFixedOverhead(productId: number, data: CostItemEditData): void {
    const list = this.fixedOverheadsMap[productId] || this.fixedItems || [];
    // Unify the array reference BEFORE onFOHChange propagates, otherwise the
    // shared batch/minutes propagation lands on a stale array and is discarded.
    this.fixedItems = list;
    this.fixedOverheadsMap[productId] = list;
    const itemId = data.itemId ?? null;

    const itemName = (data.itemName || '').trim();
    const costPerMonth = Number(data.costPerMonth ?? 0);
    const hoursOpenPerMonth = Number(data.hoursOpenPerMonth ?? this._hoursOpenPerMonth ?? 0);
    const minutesPerOrder = Number(data.minutesPerOrder ?? 0);
    const batchSize = Number(data.batchSize ?? 0);

    if (itemId != null) {
      const existing = list.find((f) => f.itemId === itemId);
      if (existing) {
        existing.itemName = itemName;
        existing.costPerMonth = costPerMonth;
        existing.hoursOpenPerMonth = hoursOpenPerMonth;
        existing.minutesPerOrder = minutesPerOrder;
        existing.batchSize = batchSize;
        this.onFOHChange(existing);
      }
    } else {
      const newItem: FixedItem = {
        itemId: null,
        itemName,
        finishedGoodID: productId,
        isNew: true,
        isEditing: false,
        hoursOpenPerMonth,
        ratePerMinute: 0,
        minutesPerOrder,
        unitCost: Number(data.unitCost ?? 0),
        costPerMonth,
        overheadCostPoolID: 0,
        description: null,
        overheadAllocationRateID: 0,
        currencyCode: '',
        rateType: '',
        itemOrderDurationID: 0,
        batchSize,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        overheadCostItemID: data.overheadCostItemID ?? null,
      } as FixedItem;

      list.unshift(newItem);
      this.onFOHChange(newItem);
    }

    this.fohBatchChangePending = true; // show the mobile FOH save popup
    this.emitCostDataChange();
  }

  private upsertMobileVariableOverhead(productId: number, data: CostItemEditData): void {
    const list = this.variableOverheadsMap[productId] || this.variableData || [];
    const inventoryId = Number(data.inventoryId ?? 0);

    const itemName = (data.itemName || '').trim();
    const purchasePrice = Number(data.purchasePrice ?? 0);
    const purchaseQuantity = Number(data.purchaseQuantity ?? 0);
    const usageQuantity = Number(data.usageQuantity ?? 0);
    const batchSize = Number(data.batchSize ?? 0);

    if (inventoryId > 0) {
      const existing = list.find((v) => v.inventoryId === inventoryId);
      if (existing) {
        existing.itemName = itemName || existing.itemName;
        existing.purchasePrice = purchasePrice;
        existing.purchaseQuantity = purchaseQuantity;
        existing.usageQuantity = usageQuantity;
        existing.batchSize = batchSize;
        this.onVariableOHChange(existing);
      } else {
        // Item selected from dropdown but not yet on this product — add it as new
        const newItem: VariableOverhead = {
          inventoryId,
          itemName: itemName || (this.variableOverheadItems.find(v => v.itemID === inventoryId) as any)?.itemName || '',
          purchasePrice,
          purchaseQuantity,
          purchasePricePerUnit: purchaseQuantity > 0 ? +(purchasePrice / purchaseQuantity).toFixed(4) : 0,
          unitCost: Number(data.unitCost ?? 0),
          usageQuantity,
          usageUnit: '',
          batchSize,
          isNew: true,
          isEditing: false,
          isModified: false,
          isLocallyEditing: false,
          variableOverheadItemID: inventoryId,
          uomID: null as any,
          purchaseUOMID: null as any,
          uomid: null as any,
          bovoComponentID: null as any,
          bovoHeaderID: null as any,
          notes: '',
          estimatedSales: 0 as any,
          monthlyCost: 0 as any,
          purchaseUnit: '',
        } as any;
        list.unshift(newItem);
        this.onVariableOHChange(newItem);
      }
    } else {
      const newItem: VariableOverhead = {
        inventoryId: 0,
        itemName,
        purchasePrice,
        purchaseQuantity,
        purchasePricePerUnit: purchaseQuantity > 0 ? +(purchasePrice / purchaseQuantity).toFixed(4) : 0,
        unitCost: Number(data.unitCost ?? 0),
        usageQuantity,
        usageUnit: '',
        batchSize,
        isNew: true,
        isEditing: false,
        isModified: false,
        isLocallyEditing: false,
        variableOverheadItemID: null as any,
        uomID: null as any,
        purchaseUOMID: null as any,
        uomid: null as any,
        bovoComponentID: null as any,
        bovoHeaderID: null as any,
        notes: '',
        estimatedSales: 0 as any,
        monthlyCost: 0 as any,
        purchaseUnit: '',
      } as any;

      list.unshift(newItem);
      this.onVariableOHChange(newItem);
    }

    this.variableFull = list;
    this.variableOverheadsMap[productId] = list;
    this.emitCostDataChange();
  }

  private upsertMobileCompetitor(productId: number, data: CostItemEditData): void {
    if (!this.competitorPricesMap[productId]) this.competitorPricesMap[productId] = [];
    if (!this.competitorPrices[productId]) this.competitorPrices[productId] = [];

    const now = new Date().toISOString();

    if (data.competitorID) {
      const mapIdx = this.competitorPricesMap[productId].findIndex(c => c.competitorID === data.competitorID);
      if (mapIdx >= 0) {
        this.competitorPricesMap[productId][mapIdx] = {
          ...this.competitorPricesMap[productId][mapIdx],
          name: data.itemName || '',
          price: data.price ?? 0,
          website: data.website || '',
          isActive: data.isActive ?? true,
          targetMarketSegment: data.targetMarketSegment || '',
          primarySalesChannel: data.primarySalesChannel || '',
          geographicFocus: data.geographicFocus || '',
          updatedAt: now,
        };
        const priceIdx = this.competitorPrices[productId].findIndex(c => c.competitorID === data.competitorID);
        if (priceIdx >= 0) {
          this.competitorPrices[productId][priceIdx] = { ...this.competitorPricesMap[productId][mapIdx] };
        }
      }
    } else {
      const newComp: Competitor = {
        competitorID: 0,
        itemCompetitorID: 0,
        itemID: productId,
        name: data.itemName || '',
        price: data.price ?? 0,
        website: data.website || '',
        isActive: data.isActive ?? true,
        createdAt: now,
        updatedAt: now,
        targetMarketSegment: data.targetMarketSegment || '',
        primarySalesChannel: data.primarySalesChannel || '',
        geographicFocus: data.geographicFocus || '',
        isNew: true,
      };
      this.competitorPricesMap[productId].push(newComp);
      this.competitorPrices[productId].push({ ...newComp });
    }

    this.competitors = [...this.competitorPricesMap[productId]];
    this.emitCostDataChange();
  }

  private _hasUnsavedChanges = false;

  // --- Unsaved-changes confirm dialog state (rendered by the parent
  //     CostCalculatorComponent so it is visible on both desktop and mobile) ---
  showUnsavedDialog = false;
  unsavedCategoriesList: string[] = [];
  isSavingBeforeLeave = false;
  private leaveResolver: ((leave: boolean) => void) | null = null;

  async canDeactivate(): Promise<boolean> {
    const productId = this.selectedProductId;
    if (productId == null) return true;

    const cats = this.getUnsavedCategories(productId);

    // Nothing changed → leave instantly, no save, no error.
    if (cats.length === 0) {
      this.hasUnsavedChanges = false;
      return true;
    }

    // There are unsaved changes — ask the user, but never trap them.
    this.unsavedCategoriesList = cats;
    this.showUnsavedDialog = true;

    return new Promise<boolean>((resolve) => {
      this.leaveResolver = resolve;
    });
  }

  /** Dialog action: persist changes, then leave regardless of save outcome. */
  async onUnsavedSaveAndLeave(): Promise<void> {
    const productId = this.selectedProductId;
    this.isSavingBeforeLeave = true;
    try {
      if (productId != null) {
        const success = await this.saveChanges(productId, 'auto');
        if (success) {
          this.hasUnsavedChanges = false;
          localStorage.setItem('lastSelectedProduct', productId.toString());
        }
      }
    } catch (error) {
      // Best-effort: a failed save must never trap the user on the page.
      console.error('Auto-save before leaving failed:', error);
    } finally {
      this.isSavingBeforeLeave = false;
      this.closeUnsavedDialog(true);
    }
  }

  /** Dialog action: discard unsaved changes and leave. */
  onUnsavedLeaveAnyway(): void {
    this.hasUnsavedChanges = false;
    this.closeUnsavedDialog(true);
  }

  /** Dialog action: stay on the page. */
  onUnsavedStay(): void {
    this.closeUnsavedDialog(false);
  }

  private closeUnsavedDialog(leave: boolean): void {
    this.showUnsavedDialog = false;
    this.unsavedCategoriesList = [];
    const resolver = this.leaveResolver;
    this.leaveResolver = null;
    if (resolver) resolver(leave);
  }

  variableOverheadItems: VariableOverheadItem[] = [];
  private uomMap = new Map<number, UnitOfMeasure>();
  showMessageModal: boolean = false;
  items: ItemDTO[] = []; // All items fetched from the API
  suppliers: SupplierDTO[] = [];
  supplierLocations: SupplierLocationsDTO[] = [];
  unitsOfMeasure: UnitOfMeasureDTO[] = [];
  categories: CategoryDTO[] = [];
  itemsForVariableOverheadDropdown: ItemDTO[] = [];
  /** Inventory raw materials (itemTypeID === 1) for the full-plan name dropdown. */
  materialDropdownOptions: ItemDTO[] = [];
  isLoadingData: boolean = false;
  variableOverheadCostPoolID: number = 1; // Example, adjust as needed
  unitOfMeasures: UnitOfMeasure[] = [];
  private originalProductValues: Map<
    number,
    { targetCost?: number | null; profitMarginPercent?: number | null }
  > = new Map();
  //get schedule
  private _hoursOpenPerMonth: number | null = null;
  // Cache for variable overheads linked to products (BOVO)
  private variableOverheadsCache = new Map<
    number,
    Observable<VariableOverhead[]>
  >();

  // Map to store latest price history for each itemID
  private latestItemPriceHistoryMap = new Map<
    number,
    ItemSupplierPriceHistoryDTO
  >();
  private allItemPriceHistory: ItemSupplierPriceHistoryDTO[] = []; // To hold all history data

  // Conversion factors for units (example, you'll need a comprehensive list)

  private allProductCostsCompleted = false;

  fixedOverheadNames: OverheadCostItemDTO[] = [];

  //  CACHE MAPS
  private rawMaterialsCache = new Map<number, RawMaterial[]>(); // Cache Map: productId -> RawMaterial[]
  private fixedOverheadsCache = new Map<number, FixedItem[]>();
  private competitorsCache = new Map<number, Competitor[]>();

  private selectedProductIdForFirstLoad: number | null = null;
  private enableAutoSelectOnInit = false;

  editForm!: FormGroup;
  selectedItem: RawMaterial | null = null;
  originalItem: RawMaterial | null = null;
  currentCategory: Category = 'materials';

  showEditModal = false;
  isNewRecord = false;
  competitorPrices: Record<number, Competitor[]> = {};
  selectedProductId: number | null = null;
  selectedBomId: number = 0;
  selectedRawMaterialId: number = 0;
  updatedQuantity: number = 0;
  editingCompetitorIndex: number | null = null;
  isEditingCompetitor = false;
  fixedItems: FixedItem[] = [];
  fohBatchChangePending = false;
  selectedFixed: FixedItem | null = null; // ← add this line
  avgSalesPM: number = 0;
  products: Product[] = [];
  employees: Employee[] = [];
  fixedOverheadItems: FixedOverheadItem[] = []; //for fix oh names and cost
  materialItemsMap: Record<number, RawMaterial[]> = {};
  originalMaterialItemsMap: Record<number, RawMaterial[]> = {};
  currentUser: string = localStorage.getItem('rememberedEmail') || 'system';
  variableOverheadsMap: { [productId: number]: VariableOverhead[] } = {};
  originalVariableOverheadsMap: { [productId: number]: VariableOverhead[] } =
    {};
  public hoursOpenPerMonthForNewEntry: number = 0;
  competitorPricesMap: Record<number, Competitor[]> = {};
  /** Non-null while a competitor save failure banner should be shown. */
  competitorSaveError: string | null = null;
  originalCompetitorPricesMap: Record<number, Competitor[]> = {};
  // keyed by productId
  private directLaborItemsMap: Record<number, LabourItem[]> = {};
  private originalDirectLaborItemsMap: Record<number, LabourItem[]> = {};
  private indirectLaborItemsMap: Record<number, IndirectLabourItem[]> = {};
  private originalinDirectLaborItemsMap: Record<number, IndirectLabourItem[]> =
    {};
  fixedOverheadsMap: { [productId: number]: FixedItem[] } = {};
  originalFixedOverheadsMap: { [productId: number]: FixedItem[] } = {};

  targetTotalCost: number | null = null;
  targetCostHistory: Record<number, number | null> = {};
  public isSaving: boolean = false;
  public modalMessage: string | SafeHtml | null = '';
  public isSuccess: boolean | null = null;
  showDeleteConfirm: boolean = false;
  isDeleteSaveSuccess: boolean = false;
  deleteModalMessage: string | SafeHtml | null = '';
  isSavingDelete = false;
  selectedProduct: string = '';
  materialItems: RawMaterial[] = [];
  private hasUserEdited = false;
  markupPercent = 0;
  searchTerm: string = '';
  labourItems: LabourItem[] = [];
  competitors: Competitor[] = [];
  alertInfo: string | SafeHtml | null = '';
  indirectLabourItems: IndirectLabourItem[] = [];
  totalTargetCostAlert: string | SafeHtml | null = '';
  private conversionFactors: { [key: string]: number } = {
    kg: 1000,
    g: 1,
    L: 1000,
    mL: 1,
    m: 1000,
    cm: 10,
    mm: 1,
    each: 1,
    bun: 1,
    unit: 1,
  };

  /**
   * Returns a normalized conversion factor for a unit code, handling common synonyms
   * and letter casing (e.g., L/l/litre -> 1000, ml -> 1).
   */
  private getConversionFactor(unitCode?: string | null): number {
    if (!unitCode) return 1;
    const normalized = unitCode.toLowerCase();

    const lookup: Record<string, number> = {
      kg: 1000,
      g: 1,
      l: 1000,
      lt: 1000,
      liter: 1000,
      litre: 1000,
      ml: 1,
      m: 1000,
      cm: 10,
      mm: 1,
      each: 1,
      ea: 1,
      bun: 1,
      unit: 1,
    };

    return (
      lookup[normalized] ??
      this.conversionFactors[unitCode] ??
      this.conversionFactors[normalized] ??
      1
    );
  }

  // private variableFull: RawMaterial[] = [];
  variableFull: VariableOverhead[] = [];

  // Account type for showing/hiding create product
  accountType: string = 'full';
  showCreateProductInput: boolean = false;
  newProductName: string = '';
  newProductSellingPrice: number | null = null;
  isCreatingProduct: boolean = false;

  showEditProductModal = false;
  editProductName = '';
  editProductSellingPrice: number | null = null;
  isSavingEditProduct = false;

  // State and helpers for custom product dropdown (modeled after BOM product dropdown)
  productDropdownOpen: boolean = false;
  productDropdownStyles: { top: string; left: string; width: string } | null =
    null;
  productSearchText: string = '';
  productPageSize: number = 25;
  productCurrentPage: number = 1;

  get filteredProducts(): Product[] {
    if (!this.products) {
      return [];
    }
    const search = (this.productSearchText || '').toLowerCase().trim();
    if (!search) {
      return this.products;
    }
    return this.products.filter((p) =>
      (p.itemName || '').toLowerCase().includes(search)
    );
  }

  get productTotalPages(): number {
    const length = this.filteredProducts.length;
    const pages = Math.ceil(length / this.productPageSize) || 1;
    return Math.max(1, pages);
  }

  get productPages(): number[] {
    const total = this.productTotalPages;
    const maxVisible = 7;

    if (total <= maxVisible) {
      return Array.from({ length: total }, (_, index) => index + 1);
    }

    const half = Math.floor(maxVisible / 2);
    let start = this.productCurrentPage - half;
    let end = this.productCurrentPage + half;

    if (start < 1) {
      start = 1;
      end = maxVisible;
    } else if (end > total) {
      end = total;
      start = total - maxVisible + 1;
    }

    const pages: number[] = [];
    for (let p = start; p <= end; p++) {
      pages.push(p);
    }
    return pages;
  }

  get productPaged(): Product[] {
    const start = (this.productCurrentPage - 1) * this.productPageSize;
    const end = start + this.productPageSize;
    return this.filteredProducts.slice(start, end);
  }

  changeProductPage(page: number): void {
    const total = this.productTotalPages;
    if (page < 1 || page > total) {
      return;
    }
    this.productCurrentPage = page;
  }

  onProductSearchChange(): void {
    this.productCurrentPage = 1;
  }

  getSelectedProductDisplay(): string {
    if (!this.selectedProductId) {
      return 'Select Product';
    }
    const prod = this.products.find((p) => p.itemID === this.selectedProductId);
    return prod ? prod.itemName : 'Select Product';
  }

  toggleProductDropdown(triggerEl?: HTMLElement): void {
    const isOpening = !this.productDropdownOpen;
    this.productDropdownOpen = isOpening;
    if (isOpening && triggerEl) {
      const rect = triggerEl.getBoundingClientRect();
      this.productDropdownStyles = {
        top: `${Math.round(rect.bottom + 4)}px`,
        left: `${Math.round(rect.left)}px`,
        width: `${Math.round(rect.width)}px`,
      };
    }
  }

  onProductToggleMouseDown(triggerEl: HTMLElement, event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.toggleProductDropdown(triggerEl);
  }

  selectProduct(product: Product | null): void {
    const newId = product ? product.itemID : null;
    this.selectedProductId = newId;
    if (newId !== null && newId !== undefined) {
      // Clear competitors cache to ensure fresh data with correct selling price
      this.competitorsCache.delete(newId);
      // Must be armed before onProductChange() kicks off the async labour
      // fetch, since it's that fetch's callback which knows whether a rate
      // is actually still needed.
      this.armServiceNeedsRatePrompt(product);
      this.onProductChange(newId);
      this.loadAiSuggestionForProduct(newId);
      this.checkZeroSellingPrice(product);
    }
    this.productDropdownOpen = false;
    this.productSearchText = '';
    this.productCurrentPage = 1;
  }

  // "Selling Price is R0" advisory (spec item 3). Only ever triggered from
  // selectProduct() — a user-initiated pick — so the ngOnInit auto-restore
  // of the last-viewed product (which calls onProductChange directly) never
  // shows this on page load.
  showZeroPricePopup = false;
  zeroPricePopupProductName = '';

  private checkZeroSellingPrice(product: Product | null): void {
    if (!product) return;
    if (product.sellingPrice === null || product.sellingPrice === undefined || product.sellingPrice === 0) {
      this.zeroPricePopupProductName = product.itemName || '';
      this.showZeroPricePopup = true;
    }
  }

  // "This service needs a rate" advisory — the popup counterpart to the
  // banner already shown in the Direct Labour tab. Only ever armed from
  // selectProduct() (a user-initiated pick), same rule as the zero-price
  // popup above, so it never fires on the ngOnInit auto-restore of the
  // last-viewed product.
  showServiceNeedsRatePopup = false;
  serviceNeedsRatePopupProductName = '';
  private pendingServiceRatePromptFor: number | null = null;

  private armServiceNeedsRatePrompt(product: Product | null): void {
    this.pendingServiceRatePromptFor =
      product?.itemTypeID === 4 ? product.itemID : null;
  }

  closeServiceNeedsRatePopup(): void {
    this.showServiceNeedsRatePopup = false;
  }

  goToLabourFromServicePopup(): void {
    this.showServiceNeedsRatePopup = false;
    const labourTab = this.tabs.find((t) => t.id === 'labour');
    if (labourTab) this.setActiveTab(labourTab);
  }

  closeZeroPricePopup(): void {
    this.showZeroPricePopup = false;
  }

  setPriceNowFromZeroPopup(): void {
    this.showZeroPricePopup = false;
    this.openEditProduct();
  }

  showCreateProduct(): void {
    this.showCreateProductInput = true;
    this.newProductName = this.productSearchText || '';
  }

  openEditProduct(): void {
    const prod = this.products.find((p: any) => p.itemID === this.selectedProductId);
    if (!prod) return;
    this.editProductName = prod.itemName ?? '';
    this.editProductSellingPrice = prod.sellingPrice ?? null;
    this.showEditProductModal = true;
  }

  cancelEditProduct(): void {
    this.showEditProductModal = false;
  }

  saveEditProduct(): void {
    if (!this.selectedProductId || !this.editProductName?.trim()) return;
    this.isSavingEditProduct = true;
    this.upsertMobileProduct({
      inventoryId: this.selectedProductId,
      itemName: this.editProductName.trim(),
      sellingPrice: this.editProductSellingPrice ?? undefined,
      category: 'Product',
      unitCost: 0,
    } as CostItemEditData);
    this.showEditProductModal = false;
    this.isSavingEditProduct = false;
  }

  cancelCreateProduct(): void {
    this.showCreateProductInput = false;
    this.newProductName = '';
    this.newProductSellingPrice = null;
  }

  async createProduct(): Promise<void> {
    const name = this.newProductName.trim();
    if (!name || (this.newProductSellingPrice !== null && this.newProductSellingPrice < 0)) return;

    this.isCreatingProduct = true;
    const token = localStorage.getItem('authToken') || '';
    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    });

    const defaultUomId = this.getDefaultUomId();
    const uncategorized = this.categories.find(
      (c) => c.categoryName?.toLowerCase() === 'uncategorized'
    );
    const categoryId = uncategorized?.categoryID || this.categories[0]?.categoryID || 1;

    const itemDto: any = {
      ItemID: 0,
      ItemName: name,
      ItemCode: this.generateSKU(name),
      itemTypeID: 2, // Finished good (product)
      PurchaseUOMID: defaultUomId,
      lowStockThreshold: 0,
      isActive: true,
      valuationMethodCode: 'FIFO',
      categoryID: categoryId,
      profitMarginPercent: 0,
      sellingPrice: this.newProductSellingPrice || 0,
      purchasePrice: 0,
      purchaseQuantity: 1,
    };

    try {
      const created = await firstValueFrom(
        this.http.post<any>(`${environment.apiUrl}/api/Items/Add`, itemDto, { headers })
      );

      const newProduct: Product = {
        itemID: created.itemID || created.ItemID,
        itemName: name,
        profitMarginPercent: 0,
        sellingPrice: this.newProductSellingPrice || 0,
      };

      this.products.unshift(newProduct);
      this.selectProduct(newProduct);
      this.showCreateProductInput = false;
      this.newProductName = '';
      this.newProductSellingPrice = null;
    } catch (err) {
      console.error('Error creating product:', err);
    } finally {
      this.isCreatingProduct = false;
    }
  }

  async createProductFromMobile(name: string, sellingPrice: number): Promise<void> {
    const token = localStorage.getItem('authToken') || '';
    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    });

    const defaultUomId = this.getDefaultUomId();
    const uncategorized = this.categories.find(
      (c) => c.categoryName?.toLowerCase() === 'uncategorized'
    );
    const categoryId = uncategorized?.categoryID || this.categories[0]?.categoryID || 1;

    const itemDto: any = {
      ItemID: 0,
      ItemName: name,
      ItemCode: this.generateSKU(name),
      itemTypeID: 2,
      PurchaseUOMID: defaultUomId,
      lowStockThreshold: 0,
      isActive: true,
      valuationMethodCode: 'FIFO',
      categoryID: categoryId,
      profitMarginPercent: 0,
      sellingPrice: sellingPrice,
      purchasePrice: 0,
      purchaseQuantity: 1,
    };

    const created = await firstValueFrom(
      this.http.post<any>(`${environment.apiUrl}/api/Items/Add`, itemDto, { headers })
    );

    const newProduct: Product = {
      itemID: created.itemID || created.ItemID,
      itemName: name,
      profitMarginPercent: 0,
      sellingPrice: sellingPrice,
    };

    this.products.unshift(newProduct);
    this.selectProduct(newProduct);
  }

  private loadAiSuggestionForProduct(itemId: number): void {
    const token =
      localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
    if (!token) return;

    const headers = new HttpHeaders({ Authorization: `Bearer ${token}` });

    this.http
      .get<any>(`${environment.apiUrl}/api/Items/GetById`, {
        headers,
        params: new HttpParams().set('ItemsId', itemId),
      })
      .subscribe({
        next: (item) => {
          const loadedAiPrice =
            item?.aiSuggestedSellingPrice ?? item?.AISuggestedSellingPrice ?? 0;
          const loadedAiMarkup =
            item?.aiSuggestedMarkup ?? item?.AISuggestedMarkup ?? 0;

          this.aiSuggestedSellingPrice = loadedAiPrice;
          this.aiSuggestedMarkup = loadedAiMarkup;
          this.aiSuggestionLoaded.emit({
            aiSuggestedSellingPrice: this.aiSuggestedSellingPrice,
            aiSuggestedMarkup: this.aiSuggestedMarkup,
          });
        },
        error: (err) => {
          console.error('Error loading AI suggestion for item', itemId, err);
        },
      });
  }

  private async saveAiSuggestionForProduct(
    itemId: number,
    aiSuggestedSellingPrice: number,
    aiSuggestedMarkup: number,
    headers: HttpHeaders
  ): Promise<boolean> {
    try {
      await firstValueFrom(
        this.http.post(
          `${environment.apiUrl}/api/items/${itemId}/ai-suggestion`,
          {
            aiSuggestedSellingPrice,
            aiSuggestedMarkup,
          },
          { headers }
        )
      );
      return true;
    } catch (err) {
      console.error('Error saving AI suggestion for item', itemId, err);
      return false;
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    const target = event.target as HTMLElement | null;
    if (!target) {
      this.productDropdownOpen = false;
      return;
    }
    const btn = document.getElementById('productSelectBtn');
    const menu = document.getElementById('productSelectMenu');
    if (btn?.contains(target) || menu?.contains(target)) {
      return;
    }
    this.productDropdownOpen = false;
  }

  @HostListener('window:resize')
  @HostListener('window:scroll')
  onViewportChangeForProductDropdown() {
    this.productDropdownOpen = false;
  }

  /**
   * @param item   the row being edited
   * @param mode   'alloc' = allocation changed,
   *               'cost'  = cost or toggle changed
   */
  tabs: Tab[] = [
    {
      id: 'materials',
      label: 'Direct Materials',
      abbr: 'DM',
      icon: 'bi-box',
      active: true,
    },
    {
      id: 'labour',
      label: 'Direct Labour',
      abbr: 'DL',
      icon: 'bi-person-badge',
      active: false,
    },
    {
      id: 'indirect-labour',
      label: 'Indirect Labour',
      abbr: 'IL',
      icon: 'bi-person-gear',
      active: false,
    },
    {
      id: 'fixed',
      label: 'Fixed Overheads',
      abbr: 'FO',
      icon: 'bi-building',
      active: false,
    },
    {
      id: 'variable',
      label: 'Variable Overheads',
      abbr: 'VO',
      icon: 'bi-graph-up',
      active: false,
    },
    {
      id: 'competitor',
      label: 'Competitors',
      abbr: 'C',
      icon: 'bi-bar-chart',
      active: false,
    },
  ];
  isExpanded: boolean = false;
  private jwtHelper: JwtHelperService = new JwtHelperService();
  // Dummy data for labour, fixed and variable.
  isTokenExpired(): Observable<boolean> {
    const token = localStorage.getItem('authToken');
    if (!token) return of(true); // No token, consider expired
    try {
      return of(this.jwtHelper.isTokenExpired(token)); // Check expiration
    } catch (e) {
      // $&
      return of(true); // Invalid token, consider expired
    }
  }
  closeShowMessageModal() {
    this.showMessageModal = false;
    this.alertInfo = '';
  }
  initializeHoursOpenPerMonth(): Observable<number> {
    const headers = new HttpHeaders({
      Authorization: `Bearer ${localStorage.getItem('authToken') || ''}`,
      'Content-Type': 'application/json',
    });

    return this.getLatestTenantProfileId().pipe(
      tap((tenantProfileId) => {
        if (tenantProfileId === null) {
          console.warn(
            'initializeHoursOpenPerMonth: No latest tenant profile ID found. Returning 0 hours.'
          );
        }
      }),
      switchMap((tenantProfileId) => {
        if (tenantProfileId === null) {
          return of(0); // Ensure an observable is returned even if tenantProfileId is null
        }

        const scheduleUrl = `${environment.apiUrl}/api/OperatingSchedule/${tenantProfileId}/schedule`;

        return this.http
          .get<OperatingSchedule[]>(scheduleUrl, { headers })
          .pipe(
            tap((schedule) => {
              if (!schedule || schedule.length === 0) {
                console.warn(
                  'initializeHoursOpenPerMonth: Fetched schedule is empty or null.'
                );
              }
            }),
            map((schedule) => {
              // Log the raw schedule received

              const weeklyMinutes = schedule
                .filter((day) => {
                  const isValid =
                    !day.isClosed && day.openTime && day.closeTime;
                  if (!isValid) {
                  }
                  return isValid;
                })
                .reduce((sum, day) => {
                  const [h1, m1] = day.openTime!.split(':').map(Number);
                  const [h2, m2] = day.closeTime!.split(':').map(Number);
                  const openMins = h1 * 60 + m1;
                  const closeMins = h2 * 60 + m2;
                  const duration =
                    closeMins >= openMins
                      ? closeMins - openMins
                      : 1440 - openMins + closeMins;

                  return sum + duration;
                }, 0);

              const hours = parseFloat(((weeklyMinutes / 60) * 4).toFixed(2)); // Assuming 4 weeks per month
              this._hoursOpenPerMonth = hours; // Store the calculated value

              return hours; // Return the calculated hours
            }),
            catchError((err) => {
              console.error(
                'initializeHoursOpenPerMonth: Error fetching or processing operating schedule:',
                err
              );
              this._hoursOpenPerMonth = 0; // Set to 0 on error
              return of(0); // Return 0 to gracefully handle the error in the stream
            })
          );
      })
    );
  }
  getLatestTenantProfileId(): Observable<number | null> {
    const token = localStorage.getItem('authToken') || '';

    if (!token) {
      // $&
      // In a real Angular app, you'd redirect to login or show a user-friendly message
      return throwError(
        () => new Error('Authentication error: No auth token found.')
      );
    }

    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    });

    const tenantProfilesUrl = `${environment.apiUrl}/api/TenantProfile/GetAll`;

    return this.http.get<any[]>(tenantProfilesUrl, { headers }).pipe(
      map((data) => {
        if (data && Array.isArray(data) && data.length > 0) {
          // Find the tenantProfileID with the highest value
          const latestProfile = data.reduce((prev, current) =>
            prev.tenantProfileID > current.tenantProfileID ? prev : current
          );
          return latestProfile.tenantProfileID;
        } else {
          // $&
          return null;
        }
      }),
      catchError((error) => {
        // $&
        return throwError(() => new Error('Failed to fetch tenant profiles.'));
      })
    );
  }

  originalMaterialItems: any;
  //get filtered items
  get filteredMaterialItems(): RawMaterial[] {
    return this.sortRawMaterials(
      this.materialItems.filter((item) =>
        item.itemName.toLowerCase().includes(this.searchTerm)
      )
    );
  }

  get filteredIndirectLabourItems(): IndirectLabourItem[] {
    return this.indirectLabourItems.filter((item) =>
      item.roleDescription.toLowerCase().includes(this.searchTerm)
    );
  }
  get mergedDesktopFixedItems(): FixedItem[] {
    if (!this.fixedOverheadNames || this.fixedOverheadNames.length === 0) {
      return this.fixedItems;
    }
    // Min/Batch + Batch Size are shared per product across ALL fixed overheads
    // (one ItemOrderDuration per finished good). Seed placeholder rows with the
    // product's current shared values so pre-loaded rows display the real values
    // and entering edit mode does not propagate 0 onto the already-saved rows.
    const shared = this.fixedItems.find((f) => !f.isPlaceholder);
    const sharedMinutes = shared?.minutesPerOrder ?? 0;
    const sharedBatch = shared?.batchSize ?? 0;
    // Manually-added custom rows (e.g. from "Add New Entry") have no matching
    // overhead name yet, so the name-mapped list below would drop them. Surface
    // them at the top so they render and can be filled in / saved.
    const extraRows = this.fixedItems.filter(
      (f) =>
        !f.isPlaceholder &&
        !this.fixedOverheadNames.some(
          (n: any) => n.overheadCostItemID === f.overheadCostItemID
        )
    );
    const namedRows = this.fixedOverheadNames.map((name: any) => {
      const existing = this.fixedItems.find(
        (f) => f.overheadCostItemID === name.overheadCostItemID
      );
      if (existing) return existing;
      const ratePerMinute = name.ratePerMinute ?? 0;
      const unitCost =
        sharedMinutes && ratePerMinute && sharedBatch > 0
          ? +((sharedMinutes * ratePerMinute) / sharedBatch).toFixed(4)
          : 0;
      return {
        itemId: null,
        itemName: name.overheadName,
        overheadCostItemID: name.overheadCostItemID,
        finishedGoodID: this.selectedProductId ?? null,
        costPerMonth: name.costPerMonth ?? 0,
        hoursOpenPerMonth: name.hoursOpenPerMonth ?? 0,
        ratePerMinute,
        minutesPerOrder: sharedMinutes,
        batchSize: sharedBatch,
        unitCost,
        overheadAllocationRateID: 0,
        currencyCode: '',
        itemOrderDurationID: 0,
        overheadCostPoolID: 0,
        createdAt: '',
        updatedAt: '',
        rateType: '',
        isNew: false,
        isEditing: false,
        isPlaceholder: true,
        description: name.description ?? null,
      } as FixedItem;
    });
    return [...extraRows, ...namedRows];
  }

  get filteredFixedItems(): FixedItem[] {
    return this.mergedDesktopFixedItems.filter((item) =>
      item.itemName.toLowerCase().includes(this.searchTerm)
    );
  }
  get filteredVariableItems(): VariableOverhead[] {
    return this.variableData.filter((item) =>
      item.itemName.toLowerCase().includes(this.searchTerm)
    );
  }
  get filteredCompetitors(): Competitor[] {
    const competitors = this.competitorPrices[this.selectedRawMaterialId] || [];
    return competitors.filter((c) =>
      c.name.toLowerCase().includes(this.searchTerm)
    );
  }

  //getter for total cost
  get totalCost(): number {
    return [
      ...this.materialItems.map((m) => m.unitCost ?? 0), // Use nullish coalescing operator
      ...this.labourItems.map((l) => l.unitCost ?? 0),
      ...this.indirectLabourItems.map((l) => l.unitCost ?? 0),
      ...this.fixedItems.map((f) => f.unitCost ?? 0),
      ...this.variableData.map((v) => v.unitCost ?? 0),
    ].reduce((a, b) => a + b, 0);
  }
  /** sum of all direct‐labour unit costs */
  get totalDirectLabourCost(): number {
    return this.labourItems.reduce((sum, l) => sum + l.unitCost, 0);
  }
  // Add these to your component class
  showSaveModal = false;

  openSaveConfirmation() {
    this.showSaveModal = true;
    this.modalMessage = '';
    this.isSuccess = true;
  }

  closeSaveModal() {
    this.showSaveModal = false;
  }
  loadFixedOverheadNames(): void {
    const token = localStorage.getItem('authToken');
    if (!token) {
      // $&
      return;
    }

    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    });

    this.http
      .get<OverheadCostItemDTO[]>(
        `${environment.apiUrl}/api/OverheadCostItem/GetAll`,
        { headers }
      )
      .subscribe({
        next: (overheadItemsFromApi) => {
          this.fixedOverheadNames = overheadItemsFromApi;
          // Enrich names with business-level cost info so placeholder cards/rows
          // show Cost PM / Hrs Open / Rate/min without needing an edit.
          this.enrichFixedOverheadNames(overheadItemsFromApi, headers);
        },
        error: (err) => {
          // $&
          this.fixedOverheadNames = [];
        },
      });
  }

  /**
   * Fetches the allocation rate per FOH name and combines it with the business
   * operating hours to pre-compute costPerMonth, hoursOpenPerMonth and
   * ratePerMinute on each `fixedOverheadNames` entry. These are business-level
   * (product-independent) values shared by both the mobile cards and the
   * desktop placeholder rows.
   */
  private enrichFixedOverheadNames(
    names: OverheadCostItemDTO[],
    headers: HttpHeaders
  ): void {
    if (!names || names.length === 0) return;

    const hours$ =
      this._hoursOpenPerMonth != null
        ? of(this._hoursOpenPerMonth)
        : this.initializeHoursOpenPerMonth();

    const rateRequests = names.map((n) =>
      n.overheadCostItemID != null
        ? this.http
            .get<OverheadAllocationRateDTO[]>(
              `${environment.apiUrl}/api/OverheadAllocationRate/GetByCostItemId/${n.overheadCostItemID}`,
              { headers }
            )
            .pipe(catchError(() => of([] as OverheadAllocationRateDTO[])))
        : of([] as OverheadAllocationRateDTO[])
    );

    forkJoin({ hours: hours$, rates: forkJoin(rateRequests) }).subscribe({
      next: ({ hours, rates }) => {
        const hoursOpenPerMonth = hours ?? 0;
        const enriched = names.map((n, i) => {
          const rate = rates[i]?.[0];
          const costPerMonth = rate
            ? this.convertToMonthlyCost(rate.rateValue || 0, rate.periodTypeID || 3)
            : 0;
          const ratePerMinute =
            hoursOpenPerMonth > 0
              ? +(costPerMonth / (hoursOpenPerMonth * 60)).toFixed(4)
              : 0;
          return { ...n, costPerMonth, hoursOpenPerMonth, ratePerMinute };
        });
        this.fixedOverheadNames = enriched;
      },
      error: () => {
        // Leave names un-enriched on failure; placeholders simply show zeros.
      },
    });
  }
  onFOHNameChange(item: FixedItem | any, selectedId: number | null): void {
    // If no item is selected (e.g., dropdown cleared), reset related fields.
    if (selectedId == null) {
      item.overheadCostItemID = null; // Unlink the cost item
      item.itemName = ''; // Clear the display name
      // IMPORTANT: Do NOT set item.overheadCostPoolID = null here.
      // item.overheadCostPoolID represents the ID of the OverheadCostPool for *this row*.
      // Setting it to null here would incorrectly de-associate the UI row from its backend pool.
      // The linkage update (setting OverheadCostItem.overheadCostPoolID to null)
      // will be handled in saveFixedOverheadsChanges if the cost item actually changes.
      item.costPerMonth = 0; // Reset cost
      // Trigger any dependent calculations or UI updates
      this.onFOHChange(item);
      return;
    }

    // Find the selected OverheadCostItem from the list of available fixed overhead names.
    const selectedOverhead = this.fixedOverheadNames.find(
      (oh) => oh.overheadCostItemID === selectedId
    );
    if (!selectedOverhead) {
      // $&
      return;
    }

    // Update the FixedItem properties with the details of the newly selected OverheadCostItem.
    item.overheadCostItemID = selectedOverhead.overheadCostItemID;
    item.itemName = selectedOverhead.overheadName;
    item.description = selectedOverhead.description || null; // Update description if available

    // Note: item.overheadCostPoolID remains unchanged by this function.
    // It represents the OverheadCostPool that this UI row corresponds to (item.itemId).
    // The fact that selectedOverhead (an OverheadCostItem) might have its own overheadCostPoolID
    // is for its *past* or *other* associations, not relevant for this FixedItem's pool association *now*.

    // Mark the item as editing if it's not a new item.
    if (!item.isNew) {
      item.isEditing = true;
    }

    // Trigger calculation of cost per month based on the newly selected OverheadCostItem.
    this.getOverheadCostAndConvert(
      item,
      selectedOverhead.overheadCostItemID ?? 0
    );
  }

  getOverheadCostAndConvert(item: any, overheadCostItemID: number): void {
    const token = localStorage.getItem('authToken');
    if (!token) return;

    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    });

    this.http
      .get<OverheadAllocationRateDTO[]>(
        `${environment.apiUrl}/api/OverheadAllocationRate/GetByCostItemId/${overheadCostItemID}`,
        { headers }
      )
      .subscribe({
        next: (allocRates: OverheadAllocationRateDTO[]) => {
          if (allocRates && allocRates.length > 0) {
            const allocRate = allocRates[0];
            item.costPerMonth = this.convertToMonthlyCost(
              allocRate.rateValue || 0,
              allocRate.periodTypeID || 3
            );
          } else {
            item.costPerMonth = 0;
          }
          // Now recalc derived fields
          this.onFOHChange(item);
        },
        error: (err) => {
          // $&
          item.costPerMonth = 0;
          this.onFOHChange(item);
        },
      });
  }

  convertToMonthlyCost(cost: number, periodTypeID: number): number {
    if (!cost || cost === 0) return 0;

    // Adjust these mappings based on your actual periodTypeID values
    // You may need to modify these IDs based on your database setup
    switch (periodTypeID) {
      case  1: //Hourly
        return cost ;
      case 2: // Daily
        return cost * 30;
      case 4: // Bi-Weekly
        return cost * 2;
      case 3: // Weekly
        return cost * 4;
      case 5: // Monthly
        return cost;
      case 6: // Yearly
        return cost / 12;
      default:
        return cost; // Default to no conversion
    }
  }

  // Alternative method if you have periodTypeID instead of string
  convertToMonthlyCostById(cost: number, periodTypeID: number): number {
    if (!cost || cost === 0) return 0;

    // Adjust these mappings based on your actual periodTypeID values
    switch (periodTypeID) {
      case 2: // Daily
        return cost * 30;
      case 3: // Weekly
        return cost * 4;
      case 5: // Monthly
        return cost;
      case 6: // Yearly
        return cost / 12;
      default:
        return cost;
    }
  }

  /** 1) Compare original vs current direct labour items for this product */
  getChangedLabourMap(
    originalItems: (LabourItem | IndirectLabourItem)[], // Accepts either type
    currentItems: (LabourItem | IndirectLabourItem)[], // Accepts either type
    laborType: 'direct' | 'indirect'
  ): Record<number, string[]> {
    const changes: Record<number, string[]> = {};
    const currentIds = new Set(currentItems.map((l) => l.itemOperationID));

    // 1. Identify Modified or Newly Added Items
    for (const cur of currentItems) {
      const orig = originalItems.find(
        (o) => o.itemOperationID === cur.itemOperationID
      );
      const fields: string[] = [];

      if (!orig || cur.isNew) {
        fields.push('NEW_LABOUR_ADDED');
        changes[cur.itemOperationID] = fields;
        continue;
      }

      // Compare common fields
      if (cur.monthlySalary !== orig.monthlySalary)
        fields.push('monthlySalary');
      if (cur.activity !== orig.activity) fields.push('activity');
      if (cur.hoursWorkedPm !== orig.hoursWorkedPm)
        fields.push('hoursWorkedPm');
      if (cur.roleDescription !== orig.roleDescription)
        fields.push('roleDescription');
      if (cur.batchSize !== orig.batchSize) fields.push('batchSize');

      // Conditionally compare the 'minutes' field based on laborType
      if (laborType === 'direct') {
        // Type assertion: Treat 'cur' and 'orig' as LabourItem for minutesPerJob
        if (
          (cur as LabourItem).minutesPerJob !==
          (orig as LabourItem).minutesPerJob
        ) {
          fields.push('minutesPerJob');
        }
      } else if (laborType === 'indirect') {
        // Type assertion: Treat 'cur' and 'orig' as IndirectLabourItem for minutesPerActivity
        if (
          (cur as IndirectLabourItem).minutesPerActivity !==
          (orig as IndirectLabourItem).minutesPerActivity
        ) {
          fields.push('minutesPerActivity');
        }
      }

      if (fields.length) changes[cur.itemOperationID] = fields;
    }

    // 2. Identify Removed Items
    for (const orig of originalItems) {
      if (!currentIds.has(orig.itemOperationID)) {
        changes[orig.itemOperationID] = ['LABOUR_REMOVED'];
      }
    }
    return changes;
  }
  /**
   * trackBy for the Direct/Indirect Labour *ngFor loops. Keyed on the real
   * backend itemOperationID so identical object churn can't render phantom
   * duplicate rows. New (unsaved) rows carry placeholder ids (0 / negative)
   * that may collide, so they fall back to a per-index key.
   */
  trackLabourItem(
    index: number,
    item: LabourItem | IndirectLabourItem
  ): string | number {
    const id = item?.itemOperationID ?? 0;
    return id > 0 ? `op-${id}` : `new-${index}`;
  }

  getAddedLabourItems(
    originalItems: (LabourItem | IndirectLabourItem)[],
    currentItems: (LabourItem | IndirectLabourItem)[]
  ): (LabourItem | IndirectLabourItem)[] {
    const originalIds = new Set(originalItems.map((o) => o.itemOperationID));
    const added: (LabourItem | IndirectLabourItem)[] = [];

    for (const cur of currentItems) {
      const isNew = cur.isNew || !originalIds.has(cur.itemOperationID);
      if (isNew) {
        added.push(cur);
      }
    }

    return added;
  }

  prepareAddLabourApiCalls(
    // No 'async' keyword here
    productId: number,
    labour: LabourItem | IndirectLabourItem,
    isDirect: boolean,
    headers: HttpHeaders
  ): Observable<any> {
    // We use 'from' to convert the Promise returned by the async IIFE into an Observable.
    return from(
      (async () => {
        const operationDto = {
          operationID: 0,
          operationName: labour.activity,
          operationTypeID: isDirect ? 1 : 2,
          operationCode: this.generateOperationCode(labour.activity),
          description: labour.activity,
          workCenterID: 0,
          isActive: true,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };

        // Use firstValueFrom instead of .toPromise()
        const operationRes = await firstValueFrom(
          this.http.post<OperationResponse>(
            `${environment.apiUrl}/api/Operation`,
            operationDto,
            { headers }
          )
        );

        // Add nullish coalescing or a check if operationID/operationTypeID could truly be undefined
        // If the backend guarantees a response, you can assert non-null like: operationRes!.operationID
        const operationID = operationRes?.operationID;
        const operationTypeID = operationRes?.operationTypeID;

        const labourRateDto = {
          labourRateID: 0,
          employeeID: labour.employeeID,
          rateType: labour.rateType,
          rateValue: labour.monthlySalary,
          hoursWorked: labour.hoursWorkedPm,
          currency: 'ZAR',
          effectiveFromDate:
            labour.effectiveFromDate || new Date().toISOString(),
          effectiveToDate: labour.effectiveToDate ?? null,
          operationTypeID: operationTypeID,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };

        // Use firstValueFrom instead of .toPromise()
        const labourRateRes = await firstValueFrom(
          this.http.post<LabourRateResponse>(
            `${environment.apiUrl}/api/LabourRates/Add`,
            labourRateDto,
            { headers }
          )
        );
        const labourRateID = labourRateRes?.labourRateID; // Add nullish coalescing

        const itemOperationDto = {
          itemOperationID: 0,
          finishedGoodItemID: productId,
          operationID: operationID,
          batchSize: labour.batchSize,
          standardRunTimeMinutesPerUnit: isDirect
            ? (labour as LabourItem).minutesPerJob
            : (labour as IndirectLabourItem).minutesPerActivity,
          labourRateIDDefault: labourRateID,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };

        // Use firstValueFrom instead of .toPromise()
        const itemOpRes = await firstValueFrom(
          this.http.post(
            `${environment.apiUrl}/api/ItemOperation`,
            itemOperationDto,
            {
              headers,
            }
          )
        );

        // Reconcile the in-memory labour item with its real backend IDs so a
        // later save pass (e.g. the auto-save on navigate) no longer treats it
        // as new and re-creates duplicate Operation/LabourRate/ItemOperation rows.
        labour.operationID = operationID;
        labour.labourRateID = labourRateID;
        labour.itemOperationID = (itemOpRes as any).itemOperationID;
        labour.isNew = false;

        return itemOpRes; // This value will be emitted by the Observable returned by 'from'
      })()
    );
  }

  generateOperationCode(activity: string): string {
    if (!activity) {
      return ''; // Handle empty or null activity
    }

    let code = activity.trim();
    code = code.toUpperCase();
    code = code.replace(/\s+/g, '_'); // Replace one or more spaces with a single underscore
    code = code.replace(/[^A-Z0-9_]/g, ''); // Remove non-alphanumeric (except underscore)
    // Optional: Truncate if there's a max length, e.g., 20 characters
    // code = code.substring(0, 20);

    return code;
  }
  /** 2) Send update calls for changed direct labour fields */
  saveLaborChanges(
    productId: number,
    directChangesMap: { [id: number]: string[] },
    indirectChangesMap: { [id: number]: string[] }
  ): Observable<any> {
    this.clearLabourItemsCache();
    const token = localStorage.getItem('authToken') || '';
    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    });

    const allRequests: Observable<any>[] = [];
    let totalChangesDetected = false;

    // Fetch current and original states
    const originalDirect = this.originalDirectLaborItemsMap[productId] || [];
    const currentDirect = this.directLaborItemsMap[productId] || [];
    const originalIndirect =
      this.originalinDirectLaborItemsMap[productId] || [];
    const currentIndirect = this.indirectLaborItemsMap[productId] || [];

    // --- Process New Indirect Labor Items ---
    const newIndirectItems = this.getAddedLabourItems(
      originalIndirect,
      currentIndirect
    );
    if (newIndirectItems.length > 0) {
      totalChangesDetected = true;
      for (const labour of newIndirectItems) {
        try {
          // Push the Observable returned by prepareAddLabourApiCalls directly
          allRequests.push(
            this.prepareAddLabourApiCalls(productId, labour, false, headers)
          );
        } catch (error) {
          console.error(
            'Error preparing to add new indirect labour item:',
            error
          );
          this.modalMessage = '❌ Error preparing new indirect labour item.';
          this.isSaving = false;
          this.isSuccess = false;
          return of(null);
        }
      }
    }

    // --- Process New Direct Labor Items ---
    const newDirectItems = this.getAddedLabourItems(
      originalDirect,
      currentDirect
    );
    if (newDirectItems.length > 0) {
      totalChangesDetected = true;
      for (const labour of newDirectItems) {
        try {
          // Push the Observable returned by prepareAddLabourApiCalls directly
          allRequests.push(
            this.prepareAddLabourApiCalls(productId, labour, true, headers)
          );
        } catch (error) {
          console.error(
            'Error preparing to add new direct labour item:',
            error
          );
          this.modalMessage = '❌ Error preparing new direct labour item.';
          this.isSaving = false;
          this.isSuccess = false;
          return of(null);
        }
      }
    }

    // --- Process Updates for Direct Labor ---
    const directChangedIds = Object.keys(directChangesMap).map((id) => +id);
    if (directChangedIds.length > 0) {
      totalChangesDetected = true;
      for (const id of directChangedIds) {
        const labour = this.directLaborItemsMap[productId].find(
          (l) => l.itemOperationID === id
        ) as LabourItem;
        if (!labour) continue;

        for (const field of directChangesMap[id]) {
          let dto: any, url: string;
          switch (field) {
            case 'monthlySalary':
            case 'hoursWorkedPm':
              dto = {
                labourRateID: labour.labourRateID,
                employeeID: labour.employeeID,
                rateType: labour.rateType,
                rateValue: labour.monthlySalary,
                hoursWorked: labour.hoursWorkedPm,
                currency: labour.currency,
                effectiveFromDate:
                  labour.effectiveFromDate || new Date().toISOString(),
                effectiveToDate: labour.effectiveToDate ?? null,
                createdAt: labour.createdAt || new Date().toISOString(),
                updatedAt: new Date().toISOString(),
                operationTypeID: labour.operationTypeID,
              };
              url = `${environment.apiUrl}/api/LabourRates/Update/${dto.labourRateID}`;
              allRequests.push(this.http.put(url, dto, { headers }));
              break;
            case 'minutesPerJob':
            case 'batchSize':
              dto = {
                itemOperationID: labour.itemOperationID,
                standardRunTimeMinutesPerUnit: labour.minutesPerJob,
                finishedGoodItemID: productId,
                batchSize: labour.batchSize,
                updatedAt: new Date().toISOString(),
              };
              url = `${environment.apiUrl}/api/ItemOperation/${dto.itemOperationID}`;
              allRequests.push(this.http.put(url, dto, { headers }));
              break;
            case 'activity':
              dto = {
                operationID: labour.operationID,
                operationName: labour.activity,
                operationTypeID: labour.operationTypeID ?? 1,
                createdAt: labour.createdAt ?? null,
                updatedAt: new Date().toISOString(),
              };
              url = `${environment.apiUrl}/api/Operation/${dto.operationID}`;
              allRequests.push(this.http.put(url, dto, { headers }));
              break;
            case 'roleDescription':
              dto = {
                roleID: labour.roleID,
                roleName: labour.roleDescription,
                updatedAt: new Date().toISOString(),
              };
              url = `${environment.apiUrl}/api/Role/Update?RoleId=${dto.roleID}`;
              allRequests.push(this.http.put(url, dto, { headers }));
              break;
          }
        }
      }
    }

    // --- Process Updates for Indirect Labor ---
    const indirectChangedIds = Object.keys(indirectChangesMap).map((id) => +id);
    if (indirectChangedIds.length > 0) {
      totalChangesDetected = true;
      for (const id of indirectChangedIds) {
        const labour = this.indirectLaborItemsMap[productId].find(
          (l) => l.itemOperationID === id
        ) as IndirectLabourItem;
        if (!labour) continue;

        for (const field of indirectChangesMap[id]) {
          let dto: any, url: string;
          switch (field) {
            case 'monthlySalary':
            case 'hoursWorkedPm':
              dto = {
                labourRateID: labour.labourRateID,
                employeeID: labour.employeeID,
                rateType: labour.rateType,
                rateValue: labour.monthlySalary,
                hoursWorked: labour.hoursWorkedPm,
                currency: labour.currency,
                effectiveFromDate:
                  labour.effectiveFromDate || new Date().toISOString(),
                effectiveToDate: labour.effectiveToDate ?? null,
                createdAt: labour.createdAt || new Date().toISOString(),
                updatedAt: new Date().toISOString(),
                operationTypeID: labour.operationTypeID,
              };
              url = `${environment.apiUrl}/api/LabourRates/Update/${dto.labourRateID}`;
              allRequests.push(this.http.put(url, dto, { headers }));
              break;
            case 'minutesPerActivity': // Specific to indirect
            case 'batchSize':
              dto = {
                itemOperationID: labour.itemOperationID,
                standardRunTimeMinutesPerUnit: labour.minutesPerActivity,
                finishedGoodItemID: productId,
                batchSize: labour.batchSize,
                updatedAt: new Date().toISOString(),
              };
              url = `${environment.apiUrl}/api/ItemOperation/${dto.itemOperationID}`;
              allRequests.push(this.http.put(url, dto, { headers }));
              break;
            case 'activity':
              dto = {
                operationID: labour.operationID,
                operationName: labour.activity,
                operationTypeID: labour.operationTypeID ?? 2,
                createdAt: labour.createdAt ?? null,
                updatedAt: new Date().toISOString(),
              };
              url = `${environment.apiUrl}/api/Operation/${dto.operationID}`;
              allRequests.push(this.http.put(url, dto, { headers }));
              break;
            case 'roleDescription':
              dto = {
                roleID: labour.roleID,
                roleName: labour.roleDescription,
                updatedAt: new Date().toISOString(),
              };
              url = `${environment.apiUrl}/api/Role/Update?RoleId=${dto.roleID}`;
              allRequests.push(this.http.put(url, dto, { headers }));
              break;
          }
        }
      }
    }

    // --- Execute All Changes ---
    if (!totalChangesDetected) {
      this.modalMessage = '⚠️ No labour changes detected.';
      this.isSuccess = false;
      return of(null);
    }

    // Return the Observable so callers can await it (firstValueFrom). The
    // snapshot reconciliation now runs in tap(), which fires only AFTER every
    // create/update request — and therefore after prepareAddLabourApiCalls has
    // written the real backend IDs back onto each new item. This closes the
    // race where saveChanges returned before persistence completed and the
    // auto-save-on-navigate re-created the labour rows.
    return forkJoin(allRequests).pipe(
      tap(() => {
        this.isSaving = false;
        this.isSuccess = true;

        // IMPORTANT: Refresh the original snapshots after successful save
        this.originalDirectLaborItemsMap[productId] = JSON.parse(
          JSON.stringify(this.directLaborItemsMap[productId])
        );
        this.originalinDirectLaborItemsMap[productId] = JSON.parse(
          JSON.stringify(this.indirectLaborItemsMap[productId])
        );

        setTimeout(() => {
          this.closeSaveModal();
          this.modalMessage = '';
          this.isSuccess = null;
        });
      }),
      catchError((err) => {
        this.isSaving = false;
        this.isSuccess = false;
        this.modalMessage = '❌ Error Saving Changes.';
        return throwError(() => err);
      })
    );
  }
  // Fixed TypeScript methods:

  // Fixed slider input handler
  displayValue: string = '0.0';

  // Format number method for display
  formatNumber(value: number): string {
    if (value === null || value === undefined || isNaN(value)) {
      return '0.0';
    }
    // Always use period as decimal separator
    return value.toFixed(1);
  }

  // Update display value when markupPercent changes
  private updateDisplayValue(): void {
    this.displayValue = this.formatNumber(this.markupPercent);
  }

  // Handle input focus
  onInputFocus(): void {
    // Sync display value with current markup percent when focused
    this.updateDisplayValue();
  }

  private smartSnap(value: number, threshold: number = 0.8): number {
    const roundNumbers = [
      0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55, 60, 65, 70, 75, 80, 85, 90,
      95, 100,
    ];

    for (let roundNum of roundNumbers) {
      if (Math.abs(value - roundNum) <= threshold) {
        return roundNum;
      }
    }
    return Math.round(value * 10) / 10;
  }

  // Fixed slider input handler
  onSliderInput(event: any): void {
    const rawValue = parseFloat(event.target.value);

    // Don't snap during dragging, just update the value
    if (rawValue >= 0 && rawValue <= 100) {
      this.markupPercent = Math.round(rawValue * 10) / 10;
      this.updateDisplayValue();
      // Emit the change during sliding
      this.onMarkupPercentChange();
    }
  }

  // Updated direct input handler that ensures period decimal separator
  onDirectInput(event: any): void {
    const inputElement = event.target;
    let inputValue = inputElement.value;

    // Replace any commas with periods
    inputValue = inputValue.replace(/,/g, '.');

    // Update the input field to show periods
    if (inputElement.value !== inputValue) {
      inputElement.value = inputValue;
    }

    this.displayValue = inputValue;

    // Handle empty input - set to 0 so slider shows correct position
    if (inputValue === '') {
      this.markupPercent = 0;
      return;
    }

    // Allow incomplete decimals like "23." or just "."
    if (inputValue === '.' || inputValue.endsWith('.')) {
      return;
    }

    // Try to parse the value
    const value = parseFloat(inputValue);

    // If parsing fails, don't update anything - let the user continue editing
    if (isNaN(value)) {
      return;
    }

    // Validate and constrain
    let constrainedValue = value;
    if (constrainedValue < 0) constrainedValue = 0;
    if (constrainedValue > 300) constrainedValue = 300;

    const roundedValue = Math.round(constrainedValue * 10) / 10;

    // Only update if the value actually changed
    if (this.markupPercent !== roundedValue) {
      this.markupPercent = roundedValue;
    }
  }

  // Handle when user finishes editing (blur event)
  onMarkupPercentChange(initialLoad: boolean = false): void {
    // Ensure we have a valid number

    if (
      this.markupPercent === undefined ||
      this.markupPercent === null ||
      isNaN(this.markupPercent)
    ) {
      this.markupPercent = 0;
    }

    // Round the markupPercent to one decimal place to match the slider's step
    let roundedValue = parseFloat(this.markupPercent.toFixed(1));

    // Check if the rounded value is very close to zero, and set it to zero
    if (Math.abs(roundedValue) < 0.01) {
      roundedValue = 0;
    }

    this.markupPercent = roundedValue;

    // Ensure it's within bounds
    if (this.markupPercent < 0) this.markupPercent = 0;
    if (this.markupPercent > 300) this.markupPercent = 300;

    // Update display value to ensure it shows periods
    this.updateDisplayValue();

    this.markupPercentChange.emit({
      markupPercent: this.markupPercent,
    });
  }

  // Add this method for when slider is released (snap on release)
  onSliderChange(event: any): void {
    const rawValue = parseFloat(event.target.value);
    const snappedValue = this.smartSnap(rawValue);

    if (snappedValue !== this.markupPercent) {
      this.markupPercent = snappedValue;
      this.updateDisplayValue();
      // Update slider to show snapped position
      event.target.value = snappedValue;
    }
    // Always emit the final change when slider is released
    this.onMarkupPercentChange();
  }
  getAddedFixedOverheads(
    originalItems: FixedItem[],
    currentItems: FixedItem[]
  ): FixedItem[] {
    const originalIds = new Set(originalItems.map((o) => o.itemId ?? o.itemId));
    const added: FixedItem[] = [];

    for (const cur of currentItems) {
      // Auto-generated placeholder rows are not real new items.
      if (cur.isPlaceholder) continue;

      // Criteria for new:
      // - UI sets cur.isNew = true when user adds a new row, OR
      // - itemId or inventoryId is missing or zero or not in originalIds
      const curId = cur.itemId ?? cur.itemId;
      const isNewFlag = !!cur.isNew;
      const notInOriginal =
        curId == null || curId === 0 || !originalIds.has(curId);
      if (isNewFlag || notInOriginal) {
        added.push(cur);
      }
    }
    return added;
  }
  saveFixedOverheadsChanges(
    productId: number,
    changesMap: { [id: number]: string[] }
  ): Observable<any> {
    // $&
    this.clearFixedOverheadsCache();
    const currentList: FixedItem[] = this.fixedOverheadsMap[productId] || [];
    const token = localStorage.getItem('authToken') || '';
    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    });
    const allRequests: Observable<any>[] = [];
    // --- Section 2: Handle Existing Item Changes (Update Logic) ---
    const changedIds = Object.keys(changesMap).map((id) => +id);

    for (const id of changedIds) {
      const fixedOverhead = currentList.find((item) => item.itemId === id);
      if (!fixedOverhead) continue;

      const fieldsChanged = changesMap[id];

      for (const field of fieldsChanged) {
        switch (field) {
          case 'itemName':
            // Update the linked OverheadCostItem's name.
            if (fixedOverhead.overheadCostItemID) {
              const safeItemName = (fixedOverhead.itemName ?? '').trim();
              const updateItemDto = {
                overheadName: safeItemName,
                description:
                  (fixedOverhead.description ?? '').trim() || safeItemName,
                updatedAt: new Date().toISOString(),
              };

              allRequests.push(
                this.http
                  .put(
                    `${environment.apiUrl}/api/OverheadCostItem/Update?id=${fixedOverhead.overheadCostItemID}`,
                    updateItemDto,
                    { headers }
                  )
                  .pipe(
                    catchError((err) => {
                      // $&
                      return of(null);
                    })
                  )
              );
            }
            break;

          case 'costPerMonth':
            // The cost is a property of OverheadAllocationRate.
            if (fixedOverhead.overheadAllocationRateID) {
              const updateRateDto = {
                rateValue: fixedOverhead.costPerMonth,
                updatedAt: new Date().toISOString(),
              };

              allRequests.push(
                this.http
                  .put(
                    `${environment.apiUrl}/api/OverheadAllocationRate/Update?id=${fixedOverhead.overheadAllocationRateID}`,
                    updateRateDto,
                    { headers }
                  )
                  .pipe(
                    catchError((err) => {
                      // $&
                      return of(null);
                    })
                  )
              );
            } else {
              console.warn(
                `Attempted to update costPerMonth for item ID ${id} but overheadAllocationRateID is missing.`
              );
            }
            break;

          case 'overheadCostItemID':
            // Update the OverheadCostPool with the new OverheadCostItemID.
            if (
              fixedOverhead.overheadCostPoolID &&
              fixedOverhead.overheadCostItemID
            ) {
              const updatePoolDto = {
                overheadCostItemID: fixedOverhead.overheadCostItemID,
              };

              allRequests.push(
                this.http
                  .put(
                    `${environment.apiUrl}/api/OverheadCostPool/Update?id=${fixedOverhead.overheadCostPoolID}`,
                    updatePoolDto,
                    { headers }
                  )
                  .pipe(
                    catchError((err) => {
                      // $&
                      return of(null);
                    })
                  )
              );
            }
            break;

          case 'minutesPerOrder':
          case 'batchSize':
            const orderDurationDto = {
              itemOrderDurationID: fixedOverhead.itemOrderDurationID,
              itemID: productId,
              batchSize: fixedOverhead.batchSize,
              totalTimeMinutes: fixedOverhead.minutesPerOrder,
              recordedAt: fixedOverhead.createdAt || new Date().toISOString(),
              recordedBy: this.currentUser,
            };

            if (orderDurationDto.itemOrderDurationID) {
              allRequests.push(
                this.http
                  .put(
                    `${environment.apiUrl}/api/ItemOrderDuration/Update?id=${orderDurationDto.itemOrderDurationID}`,
                    orderDurationDto,
                    { headers }
                  )
                  .pipe(
                    catchError((err) => {
                      // $&
                      return of(null);
                    })
                  )
              );
            }
            break;
        }
      }
    }

    // Execute all requests

    if (allRequests.length === 0) {
      // $&
      this.isSaving = false;
      return of([]);
    }

    return forkJoin(allRequests).pipe(
      tap((results) => {
        const hasErrors = results.some((result) => result === null);

        if (hasErrors) {
          this.isSaving = false;
          this.isSuccess = false;
          this.modalMessage = '❌ Some changes failed to save.';
          // $&
        }

        this.originalFixedOverheadsMap[productId] = JSON.parse(
          JSON.stringify(this.fixedOverheadsMap[productId])
        );
      }),
      catchError((err) => {
        // $&
        this.isSaving = false;
        this.isSuccess = false;
        this.modalMessage = '❌ Error saving some or all changes.';
        return throwError(() => err);
      })
    );

    // Alternative approach using subscribe (like your working example)
    /*
  forkJoin(allRequests).subscribe({
    next: (results) => {
      this.isSaving = false;
      this.isSuccess = true;
      this.modalMessage = this.sanitizer.bypassSecurityTrustHtml(`
        <span style="display: flex; align-items: center; gap: 8px;">
          Changes saved successfully
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="color: #28a745;">
            <path d="M22 11.08V12a10 10 0 1 1-5.93-8.15"></path>
            <polyline points="22 4 12 14.01 9 11.01"></polyline>
          </svg>
        </span>
      `);

      this.originalFixedOverheadsMap[productId] = JSON.parse(
        JSON.stringify(this.fixedOverheadsMap[productId])
      );

      setTimeout(() => {
        this.closeSaveModal();
        this.modalMessage = '';
        this.isSuccess = null;
      });
    },
    error: (err) => {
      this.isSaving = false;
      this.isSuccess = false;
      this.modalMessage = '❌ Error saving some or all changes.';
// $&
    },
  });
  */
  }

  getChangedCompetitorsMap(productId: number): Record<number, string[]> {
    const original = this.originalCompetitorPricesMap[productId] || [];
    const current = this.competitorPricesMap[productId] || [];

    const changes: Record<number, string[]> = {};

    current.forEach((c, i) => {
      // Match the original by identity, not array position. Inserting or
      // removing a row shifts every later index, which previously made the
      // diff compare unrelated competitors and attribute edits to the wrong one.
      const o =
        (c.itemCompetitorID
          ? original.find((x: any) => x.itemCompetitorID === c.itemCompetitorID)
          : undefined) ||
        (c.competitorID
          ? original.find((x: any) => x.competitorID === c.competitorID)
          : undefined) ||
        original[i];
      const changed: string[] = [];

      if (!c.competitorID) {
        // No backend ID yet — needs creation. A price of 0 is legitimate
        // (a competitor can genuinely be priced at 0 or not yet known), so
        // only require something identifying the row.
        if ((c.name || '').trim() !== '') {
          changed.push('NEW_COMPETITOR');
        }
      } else if (o) {
        // Existing competitor with a matching original entry — diff fields
        if (c.name !== o.name) changed.push('name');
        if (c.website !== o.website) changed.push('website');
        if (c.isActive !== o.isActive) changed.push('isActive');
        if (c.targetMarketSegment !== o.targetMarketSegment)
          changed.push('targetMarketSegment');
        if (c.primarySalesChannel !== o.primarySalesChannel)
          changed.push('primarySalesChannel');
        if (c.geographicFocus !== o.geographicFocus)
          changed.push('geographicFocus');
        if (c.price !== o.price) changed.push('price');
      }
      // If c.competitorID is set but no 'o', the competitor was saved in the
      // current session but originalCompetitorPricesMap wasn't yet refreshed —
      // skip to avoid re-creating it.

      if (changed.length) {
        changes[i] = changed;
      }
    });

    return changes;
  }
  saveCompetitorChanges(
    productId: number,
    changesMap: { [id: number]: string[] }
  ): Promise<void> {
    const changedIndices = Object.keys(changesMap).map((k) => +k);

    const token = localStorage.getItem('authToken') || '';
    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    });

    const requests: Observable<any>[] = [];

    for (const idx of changedIndices) {
      const compList = this.competitorPricesMap[productId] || [];
      const comp = compList[idx];
      if (!comp) continue;

      const fieldsChanged = changesMap[idx];

      // --- Company target entry is always at index 0 ---
      if (idx === 0 && fieldsChanged.includes('price')) {
        const full = comp.fullTarget;

        // Condition for UPDATE: If we have all the IDs for existing records.
        // This path is for when everything (competitor, itemCompetitor, targetHistory) already exists and needs an update.
        if (
          full &&
          full.itemTargetHistoryID != null &&
          full.itemTargetHistoryID !== 0 &&
          comp.competitorID != null &&
          comp.competitorID !== 0 &&
          comp.itemCompetitorID != null &&
          comp.itemCompetitorID !== 0
        ) {
          // --- UPDATE Existing Company Target Entry ---
          const updateRequests: Observable<any>[] = [];

          // 1. Update Competitor (if its data changed or just to ensure 'updatedAt' is current)
          const companyCompetitorUpdateDto = {
            competitorID: comp.competitorID,
            competitorName: comp.name || 'Company Target Selling Price',
            website: comp.website || '',
            isActive: comp.isActive ?? true,
            primarySalesChannel: comp.primarySalesChannel || '',
            geographicFocus: comp.geographicFocus || '',
            targetMarketSegment: comp.targetMarketSegment || '',
            createdAt: comp.createdAt || new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          };
          const competitorUpdateUrl = `${environment.apiUrl}/api/Competitors/Update?CompetitorsId=${comp.competitorID}`;
          updateRequests.push(
            this.http.put(competitorUpdateUrl, companyCompetitorUpdateDto, {
              headers,
            })
          );

          // 2. Update ItemCompetitor (for price change)
          const itemCompetitorUpdateDto = {
            itemCompetitorID: comp.itemCompetitorID,
            itemID: productId,
            competitorID: comp.competitorID,
            competitorPrice: comp.price,
          };
          const itemCompetitorUpdateUrl = `${environment.apiUrl}/api/ItemCompetitor/${comp.itemCompetitorID}`;
          updateRequests.push(
            this.http.put(itemCompetitorUpdateUrl, itemCompetitorUpdateDto, {
              headers,
            })
          );

          // 3. Update ItemTargetHistory
          const updateTargetHistoryDto = {
            itemTargetHistoryID: full.itemTargetHistoryID,
            itemID: productId,
            targetSellingPrice: comp.price,
            targetProfitMarginPercent: full.targetProfitMarginPercent,
            targetCost: full.targetCost,
            validFrom: full.validFrom,
            validTo: full.validTo ?? full.validFrom,
            reasonForChange: full.reasonForChange || '',
            userIDEntered: full.userIDEntered ?? null,
            createdAt: full.createdAt,
            updatedAt: new Date().toISOString(),
          };
          const updateTargetHistoryUrl = `${environment.apiUrl}/api/ItemTargetHistory/Update?ItemTargetHistoryId=${full.itemTargetHistoryID}`;
          updateRequests.push(
            this.http.put(updateTargetHistoryUrl, updateTargetHistoryDto, {
              headers,
            })
          );

          requests.push(forkJoin(updateRequests));
        } else {
          // --- CREATE / INITIALIZE Company Target Entry ---
          // This block handles the scenario where the company target entry (or parts of it)
          // does not fully exist with all its IDs in the local `comp` object.

          // Step 1: Find or Create the 'Company Target Selling Price' Competitor
          // Use an endpoint that returns all competitors and filter client-side.
          const getAllCompetitorsUrl = `${environment.apiUrl}/api/Competitors/GetAll`;

          const companyTargetCreationFlow = this.http
            .get<any[]>(getAllCompetitorsUrl, { headers })
            .pipe(
              switchMap((allCompetitors: any[]) => {
                let companyCompetitor = allCompetitors.find(
                  (c) => c.competitorName === 'Company Target Selling Price'
                );
                let competitorLookupOrCreation: Observable<any>;
                let companyCompetitorId: number;

                if (companyCompetitor) {
                  // Competitor already exists, use its ID.
                  companyCompetitorId = companyCompetitor.competitorID;
                  competitorLookupOrCreation = of({
                    competitorID: companyCompetitorId,
                  });
                } else {
                  // Company Competitor does NOT exist, create it.
                  const companyCompetitorDto = {
                    competitorID: 0,
                    competitorName: comp.name || 'Company Target Selling Price',
                    website: comp.website || '',
                    isActive: comp.isActive ?? true,
                    primarySalesChannel: comp.primarySalesChannel || '',
                    geographicFocus: comp.geographicFocus || '',
                    targetMarketSegment: comp.targetMarketSegment || '',
                    createdAt: new Date().toISOString(),
                    updatedAt: new Date().toISOString(),
                  };
                  const competitorAddUrl = `${environment.apiUrl}/api/Competitors/Add`;
                  competitorLookupOrCreation = this.http
                    .post(competitorAddUrl, companyCompetitorDto, { headers })
                    .pipe(
                      tap((res: any) => {
                        companyCompetitorId = res.competitorID || res.id; // Capture ID after creation
                      })
                    );
                }

                return competitorLookupOrCreation.pipe(
                  switchMap((compRes: any) => {
                    // Ensure companyCompetitorId is set from either lookup or creation
                    const actualCompetitorId =
                      compRes.competitorID || compRes.id || companyCompetitorId;

                    // Step 2: Find or Create Item-Competitor Price Entry
                    // We need to check if an ItemCompetitor entry for this specific product
                    // and the company competitor already exists.
                    const getAllItemCompetitorsUrl = `${environment.apiUrl}/api/ItemCompetitor`; // Assuming this endpoint exists

                    return this.http
                      .get<any[]>(getAllItemCompetitorsUrl, { headers })
                      .pipe(
                        switchMap((allItemsCompetitors: any[]) => {
                          let itemCompetitorEntry = allItemsCompetitors.find(
                            (ic) =>
                              ic.itemID === productId &&
                              ic.competitorID === actualCompetitorId
                          );
                          let itemCompetitorLookupOrCreation: Observable<any>;
                          let itemCompetitorId: number;

                          if (itemCompetitorEntry) {
                            // ItemCompetitor already exists, update its price.
                            itemCompetitorId =
                              itemCompetitorEntry.itemCompetitorID;
                            const updateItemCompetitorDto = {
                              itemCompetitorID: itemCompetitorId,
                              itemID: productId,
                              competitorID: actualCompetitorId,
                              competitorPrice: comp.price || 0,
                            };
                            const itemCompetitorUpdateUrl = `${environment.apiUrl}/api/ItemCompetitor/${itemCompetitorId}`;
                            itemCompetitorLookupOrCreation = this.http.put(
                              itemCompetitorUpdateUrl,
                              updateItemCompetitorDto,
                              { headers }
                            );
                          } else {
                            // ItemCompetitor does NOT exist, create it.
                            const itemCompetitorDto = {
                              itemCompetitorID: 0,
                              itemID: productId,
                              competitorID: actualCompetitorId,
                              competitorPrice: comp.price || 0,
                            };
                            const itemCompetitorAddUrl = `${environment.apiUrl}/api/ItemCompetitor`; // Assuming POST to /api/ItemCompetitor adds
                            itemCompetitorLookupOrCreation = this.http
                              .post(itemCompetitorAddUrl, itemCompetitorDto, {
                                headers,
                              })
                              .pipe(
                                tap((res: any) => {
                                  itemCompetitorId =
                                    res.itemCompetitorID || res.id; // Capture ID after creation
                                })
                              );
                          }

                          return itemCompetitorLookupOrCreation.pipe(
                            switchMap((itemCompRes: any) => {
                              // Ensure itemCompetitorId is set from either lookup or creation/update
                              const actualItemCompetitorId =
                                itemCompRes.itemCompetitorID ||
                                itemCompRes.id ||
                                itemCompetitorId;

                              // Step 3: Find or Create Target History Entry
                              // Check if ItemTargetHistory for this product already exists based on full.itemTargetHistoryID
                              let targetHistoryLookupOrCreation: Observable<any>;
                              let targetHistoryId: number;

                              if (
                                full &&
                                full.itemTargetHistoryID != null &&
                                full.itemTargetHistoryID !== 0
                              ) {
                                // ItemTargetHistory exists, update it.
                                targetHistoryId = full.itemTargetHistoryID;
                                const updateDto = {
                                  itemTargetHistoryID: targetHistoryId,
                                  itemID: productId,
                                  targetSellingPrice: comp.price,
                                  targetProfitMarginPercent:
                                    full.targetProfitMarginPercent,
                                  targetCost: full.targetCost,
                                  validFrom: full.validFrom,
                                  validTo: full.validTo ?? full.validFrom,
                                  reasonForChange: full.reasonForChange || '',
                                  userIDEntered: full.userIDEntered ?? null,
                                  createdAt: full.createdAt,
                                  updatedAt: new Date().toISOString(),
                                };
                                const updateUrl = `${environment.apiUrl}/api/ItemTargetHistory/Update?ItemTargetHistoryId=${targetHistoryId}`;
                                targetHistoryLookupOrCreation = this.http.put(
                                  updateUrl,
                                  updateDto,
                                  { headers }
                                );
                              } else {
                                // ItemTargetHistory does NOT exist, create it.
                                const addDto = {
                                  itemTargetHistoryID: 0,
                                  itemID: productId,
                                  targetSellingPrice: comp.price,
                                  targetProfitMarginPercent:
                                    full?.targetProfitMarginPercent || 0,
                                  targetCost: full?.targetCost || 0,
                                  validFrom:
                                    full?.validFrom || new Date().toISOString(),
                                  validTo:
                                    full?.validTo || new Date().toISOString(),
                                  reasonForChange:
                                    full?.reasonForChange ||
                                    'Initial target price setup',
                                  userIDEntered: null,
                                  createdAt: new Date().toISOString(),
                                };
                                const addUrl = `${environment.apiUrl}/api/ItemTargetHistory/Add`;
                                targetHistoryLookupOrCreation = this.http
                                  .post(addUrl, addDto, { headers })
                                  .pipe(
                                    tap((res: any) => {
                                      targetHistoryId =
                                        res.itemTargetHistoryID || res.id; // Capture ID
                                    })
                                  );
                              }

                              return targetHistoryLookupOrCreation.pipe(
                                map((targetHistoryRes: any) => {
                                  // Update local 'comp' object with newly obtained/confirmed IDs
                                  comp.competitorID = actualCompetitorId;
                                  comp.itemCompetitorID =
                                    actualItemCompetitorId;
                                  // comp.fullTarget = {
                                  //     ...(full || {}), // Preserve existing fullTarget properties if any
                                  //     itemTargetHistoryID: targetHistoryRes.itemTargetHistoryID || targetHistoryRes.id || targetHistoryId,
                                  //     targetSellingPrice: comp.price, // Ensure price is updated in local fullTarget
                                  //     updatedAt: new Date().toISOString(),
                                  // };
                                  return {
                                    message:
                                      'Company target fully processed (found/created/updated).',
                                  };
                                })
                              );
                            })
                          );
                        })
                      );
                  })
                );
              })
            );
          requests.push(companyTargetCreationFlow);
        }
        continue;
      }

      if (idx === 0 && fieldsChanged.includes('name')) {
        console.warn(
          'Company target name change is not applicable or allowed for the default entry. Skipping.'
        );
        continue;
      }

      // --- For other competitor entries (idx > 0) ---
      if (idx > 0) {
        // Handle NEW_COMPETITOR first
        if (fieldsChanged.includes('NEW_COMPETITOR')) {
          const competitorDto = {
            competitorName: comp.name || '',
            website: comp.website || '',
            notes: comp.notes || '',
            isActive: comp.isActive ?? true,
            competitorTier: comp.competitorTier || '',
            primarySalesChannel: comp.primarySalesChannel || '',
            geographicFocus: comp.geographicFocus || '',
            targetMarketSegment: comp.targetMarketSegment || '',
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          };
          const competitorUrl = `${environment.apiUrl}/api/Competitors/Add`;

          const createCompetitorRequest = this.http
            .post(competitorUrl, competitorDto, { headers })
            .pipe(
              switchMap((competitorResponse: any) => {
                const newCompetitorId =
                  competitorResponse.competitorID || competitorResponse.id;

                const itemCompetitorDto = {
                  itemID: productId,
                  competitorID: newCompetitorId,
                  competitorPrice: comp.price || 0,
                };
                const itemCompetitorUrl = `${environment.apiUrl}/api/ItemCompetitor`;

                return this.http
                  .post(itemCompetitorUrl, itemCompetitorDto, { headers })
                  .pipe(
                    map((itemCompetitorResponse: any) => {
                      comp.competitorID = newCompetitorId;
                      comp.itemCompetitorID =
                        (itemCompetitorResponse?.itemCompetitorID) ||
                        (itemCompetitorResponse?.id) || 0;

                      // A missing ID here is why later edits silently vanished:
                      // they PUT to /api/ItemCompetitor/0, which matched no row.
                      if (!comp.itemCompetitorID) {
                        console.error(
                          'ItemCompetitor was created but no ID was returned; ' +
                          'subsequent edits to this competitor cannot be saved.',
                          itemCompetitorResponse
                        );
                      }
                      comp.isNew = false;
                      // Also update the entry by index in case `comp` reference
                      // was replaced in the map (defensive in-place sync).
                      const liveList = this.competitorPricesMap[productId];
                      if (liveList) {
                        const liveEntry = liveList.find(
                          (c: any) => c === comp || (c.itemID === comp.itemID && c.name === comp.name && !c.competitorID)
                        );
                        if (liveEntry && liveEntry !== comp) {
                          liveEntry.competitorID = newCompetitorId;
                          liveEntry.itemCompetitorID = comp.itemCompetitorID;
                          liveEntry.isNew = false;
                        }
                      }
                      return { competitorResponse, itemCompetitorResponse };
                    })
                  );
              })
            );

          requests.push(createCompetitorRequest);
          continue;
        }

        // Handle competitor master data changes (for existing competitors)
        const competitorFields = [
          'name',
          'website',
          'isActive',
          'targetMarketSegment',
          'primarySalesChannel',
          'geographicFocus',
        ];
        const hasCompetitorChanges = competitorFields.some((field) =>
          fieldsChanged.includes(field)
        );

        if (hasCompetitorChanges) {
          if (comp.competitorID == null) {
            console.warn(
              'No competitorID for competitor at index',
              idx,
              '. Cannot update master data.'
            );
            continue;
          }
          const dto = {
            competitorID: comp.competitorID,
            competitorName: comp.name,
            website: comp.website || '',
            notes: comp.notes || '',
            isActive: comp.isActive ?? true,
            createdAt: comp.createdAt || new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            competitorTier: comp.competitorTier || '',
            primarySalesChannel: comp.primarySalesChannel || '',
            geographicFocus: comp.geographicFocus || '',
            targetMarketSegment: comp.targetMarketSegment || '',
          };
          const url = `${environment.apiUrl}/api/Competitors/Update?CompetitorsId=${comp.competitorID}`;
          requests.push(this.http.put(url, dto, { headers }));
        }

        // Handle price change separately (for existing competitors)
        if (fieldsChanged.includes('price')) {
          // 0 is not a usable ID: PUT /api/ItemCompetitor/0 used to return 200
          // while writing nothing, so the price change was silently lost.
          if (!comp.itemCompetitorID) {
            console.error(
              'No itemCompetitorID for competitor price at index',
              idx,
              '. Cannot update price — the competitor link was never created properly.'
            );
            this.showCompetitorSaveError(
              `Could not save the price for "${comp.name || 'this competitor'}". Please reload the page and try again.`
            );
            continue;
          }
          const dto = {
            itemCompetitorID: comp.itemCompetitorID,
            itemID: comp.itemID || productId,
            competitorID: comp.competitorID,
            competitorPrice: comp.price,
          };
          const url = `${environment.apiUrl}/api/ItemCompetitor/${comp.itemCompetitorID}`;
          requests.push(this.http.put(url, dto, { headers }));
        }
      }
    }

    if (requests.length === 0) {
      return Promise.resolve();
    }

    // Resolve only when the writes actually complete, so callers cannot report
    // success while requests are still in flight (or have failed).
    return new Promise<void>((resolve, reject) => {
      forkJoin(requests).subscribe({
        next: (_responses) => {
          // Sync competitorPrices from the map (source of truth after IDs are assigned)
          // and update originalCompetitorPricesMap so subsequent saves don't re-create
          // already-persisted entries.
          this.competitorPrices[productId] = (this.competitorPricesMap[productId] || []).map(c => ({ ...c }));
          this.originalCompetitorPricesMap[productId] = JSON.parse(JSON.stringify(this.competitorPricesMap[productId] || []));
          this.competitors = [...(this.competitorPricesMap[productId] || [])];
          // The cached read would otherwise serve pre-save data on the next load.
          this.competitorsCache.delete(productId);
          this.emitCostDataChange();
          resolve();
        },
        error: (err) => {
          // Previously empty: a failed save left the UI looking clean while
          // nothing had been written, which is how competitors "disappeared"
          // on the next reload.
          console.error('Failed to save competitor changes', err);
          this.showCompetitorSaveError(
            'Your competitor changes could not be saved. Please try again.'
          );
          reject(err);
        },
      });
    });
  }

  /** Surface a competitor save failure to the user rather than failing silently. */
  private showCompetitorSaveError(message: string): void {
    this.competitorSaveError = message;
    setTimeout(() => {
      if (this.competitorSaveError === message) {
        this.competitorSaveError = null;
      }
    }, 8000);
  }
  private getAddedVariableOverheads(
    originalItems: VariableOverhead[],
    currentItems: VariableOverhead[]
  ): VariableOverhead[] {
    const originalIds = new Set<number>();
    for (const item of originalItems) {
      if (item.inventoryId !== null && item.inventoryId !== undefined) {
        originalIds.add(item.inventoryId);
      }
    }

    const added: VariableOverhead[] = [];

    for (const cur of currentItems) {
      // If an item is marked as new, and its inventoryId is not found in the original list, it's truly added.
      // This is the most reliable check.
      if (cur.isNew) {
        if (cur.inventoryId === null || cur.inventoryId === undefined) {
          // If it's new and has no ID yet (e.g., still 0 or null before a temporary ID is assigned)

          added.push(cur);
        } else if (!originalIds.has(cur.inventoryId)) {
          // It's new and has an ID that wasn't in the original list.

          added.push(cur);
        }
      }
    }

    return added;
  }
  getChangedVariableOverheadsMap(productId: number): Record<number, string[]> {
    const originalItems = this.originalVariableOverheadsMap[productId] || [];
    const currentItems = this.variableOverheadsMap[productId] || []; // This now represents the 'this.variableFull' after local removal

    const changesMap: Record<number, string[]> = {};

    const currentItemIds = new Set<number>(
      currentItems
        .map((item) => item.inventoryId)
        .filter((id): id is number => id !== null)
    );

    // 1. Check for Modified and Newly Added Items (Your existing logic, keep it as is, or with the bovoComponentID fix)
    for (const current of currentItems) {
      const original = originalItems.find(
        (o) => o.inventoryId === current.inventoryId
      );
      const changedFields: string[] = [];

      if (!original || (current.isNew && current.inventoryId === 0)) {
        changedFields.push('NEW_ITEM_ADDED');
        changesMap[current.inventoryId ?? 0] = changedFields;
        continue;
      }

      if (original) {
        // B. Handle MODIFIED items
        if (current.itemName !== original.itemName)
          changedFields.push('itemName');
        if (current.purchasePrice !== original.purchasePrice)
          changedFields.push('purchasePrice');
        if (current.purchaseQuantity !== original.purchaseQuantity)
          changedFields.push('purchaseQuantity');
        if (current.purchaseUnit !== original.purchaseUnit)
          changedFields.push('purchaseUnit');
        if (current.usageQuantity !== original.usageQuantity)
          changedFields.push('usageQuantity');
        if (current.usageUnit !== original.usageUnit)
          changedFields.push('usageUnit');
        if (current.batchSize !== original.batchSize)
          changedFields.push('batchSize');
        if (current.purchasePricePerUnit !== original.purchasePricePerUnit)
          changedFields.push('purchasePricePerUnit');
        // ... (add all other fields to compare for modifications)

        if (changedFields.length > 0) {
          if (
            current.inventoryId !== null &&
            current.inventoryId !== undefined
          ) {
            changesMap[current.inventoryId] = changedFields;
          } else {
            // $&
          }
        }
      }
    }

    // 2. Check for Removed Items (This is good, ensure it's re-enabled)
    // This loop identifies items that were in the original list but are no longer in the current list.
    for (const original of originalItems) {
      // If the original item's inventoryId is NOT found in the current list of item IDs
      if (!currentItemIds.has(original.inventoryId ?? 0)) {
        if (
          original.inventoryId !== null &&
          original.inventoryId !== undefined
        ) {
          // Mark it as removed. We'll use this inventoryId to find its bovoComponentID later.
          changesMap[original.inventoryId] = ['ITEM_REMOVED'];
        }
      }
    }

    return changesMap;
  }
  private getDefaultUomId(): number {
    // Replace with a default UOM ID that actually exists in your UnitsOfMeasure table.
    // For example, if UOM 'Units' has ID 1, use 1.
    // You might fetch this from your UOM list, or have a constant.
    if (this.unitOfMeasures && this.unitOfMeasures.length > 0) {
      // Attempt to find a common default, e.g., 'EA' for 'Each' or the first one
      const defaultUom =
        this.unitOfMeasures.find((uom) => uom.uomCode === 'EA') ||
        this.unitOfMeasures[0];
      return defaultUom.uomid;
    }
    // Fallback if no UOMs are loaded or a suitable default isn't found
    // This '0' will still cause the FK error if UOMID 0 does not exist in your DB.
    // It's vital to ensure this scenario doesn't happen by properly initializing/selecting UOMs.
    console.warn(
      'No default UOM found or unitsOfMeasures is empty. Returning 0 as fallback, which may cause a foreign key error.'
    );
    return 0; // This `0` will still fail if your DB doesn't have UOMID 0.
    // The ultimate solution is to ensure `item.uomID` is set to a valid ID on the frontend.
  }

  saveVariableOverheadsChanges(
    productId: number,
    changesMap: { [id: number]: string[] } // `id` here is VariableOverhead.inventoryId
  ): void {
    this.clearVariableOverheadsCache();

    const originalList: VariableOverhead[] =
      this.originalVariableOverheadsMap[productId] || [];
    const currentList: VariableOverhead[] =
      this.variableOverheadsMap[productId] || [];
    const allRequests: Observable<any>[] = []; // Collects all HTTP requests
    const token = localStorage.getItem('authToken') || '';
    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    });

    // delete if eny
    if (this.deletedBovoComponentIDs.length > 0) {
      // Create an array of deletion requests
      const deleteRequests = this.deletedBovoComponentIDs.map(
        (bovoComponentId) => {
          const url = `${environment.apiUrl}/api/BOVOComponent/${bovoComponentId}`;

          return this.http.delete(url, { headers }).pipe(
            catchError((error) => {
              console.error(
                `Error DELETING BOVOComponentID ${bovoComponentId}:`,
                error
              );
              // Decide how to handle this:
              // 1. Re-throw the error if you want the entire transaction to fail.
              // 2. Return 'of(null)' to allow other requests to proceed (current behavior).
              // 3. Store failed deletions to retry or notify the user.
              return of(null); // Continue stream for other operations
            })
          );
        }
      );
      allRequests.push(...deleteRequests); // Add all deletion observables to the main request list
    }

    const newlyAddedItems = this.getAddedVariableOverheads(
      originalList,
      currentList
    );
    const needsNewBOVOHeader = newlyAddedItems.length > 0;

    if (needsNewBOVOHeader) {
      const bovoHeaderDto: BOVOHeaderDto = {
        bovoHeaderID: 0,
        finishedGoodItemID: productId,
        bovoVersion: '1',
        description: `Variable Overhead Version for Product ID: ${productId} - ${new Date().toLocaleDateString()}`,
        isActive: true,
        validFrom: new Date().toISOString(),
        validTo: null,
        createdAt: new Date().toISOString(),
        userIDCreated: null,
      };
      const createHeaderAndComponentsObservable = this.http
        .post<{ bovoHeaderID: number }>(
          `${environment.apiUrl}/api/BOVOHeader/AddBOVOHeader`,
          bovoHeaderDto,
          { headers }
        )
        .pipe(
          switchMap((headerResp) => {
            const newBovoHeaderID = headerResp.bovoHeaderID;

            // Prepare the DTO for updating the BOVOHeader with the new bovoVersion
            const bovoHeaderDtoUpdate: BOVOHeaderDto = {
              bovoHeaderID: newBovoHeaderID,
              finishedGoodItemID: productId,
              bovoVersion: `${newBovoHeaderID}`, // Set bovoVersion to the new ID
              description: `Variable Overhead Version for Product ID: ${productId} - ${new Date().toLocaleDateString()}`,
              isActive: true,
              validFrom: new Date().toISOString(),
              validTo: null,
              createdAt: new Date().toISOString(),
              userIDCreated: null,
            };

            // Chain the PUT request to update the BOVOHeader
            return this.http
              .put<BOVOHeaderDto>( // It's often good to specify the expected return type, e.g., <any> or <BOVOHeaderDto>
                `${environment.apiUrl}/api/BOVOHeader/UpdateBOVOHeader/${newBovoHeaderID}`,
                bovoHeaderDtoUpdate,
                { headers }
              )
              .pipe(
                switchMap(() => {
                  const componentCreationRequests: Observable<any>[] = [];
                  currentList.forEach((item) => {
                    // ... (existing logic for UOM validation and BOVOComponentDto creation)
                    const usageUomIdToSend =
                      item.uomID !== null && item.uomID !== undefined
                        ? item.uomID
                        : this.getDefaultUomId();
                    const purchaseUomIdToSend =
                      item.purchaseUOMID !== null &&
                      item.purchaseUOMID !== undefined
                        ? item.purchaseUOMID
                        : this.getDefaultUomId();

                    if (usageUomIdToSend === 0 || purchaseUomIdToSend === 0) {
                      console.error(`Attempting to send BOVOComponent with an invalid UOM ID (0) for item: ${item.itemName}.
                                    Please ensure a valid UOM is selected or set a proper default.`);
                    }

                    const bovoComponentDto: BOVOComponentDto = {
                      bovoComponentID: 0,
                      bovoHeaderID: newBovoHeaderID,
                      variableOverheadItemID: item.inventoryId ?? 0,
                      usageQuantity: item.usageQuantity ?? 0,
                      batchSize: item.batchSize ?? 0,
                      uomid: usageUomIdToSend,
                      notes: item.notes || '',
                    };

                    componentCreationRequests.push(
                      this.http
                        .post<any>(
                          `${environment.apiUrl}/api/BOVOComponent/Add`,
                          bovoComponentDto,
                          { headers }
                        )
                        .pipe(
                          tap((componentResp) => {
                            item.bovoComponentID =
                              componentResp.bovoComponentID;
                            item.bovoHeaderID = newBovoHeaderID;
                            item.isNew = false;
                            item.isEditing = false;
                            item.isModified = false;
                          }),
                          catchError((err) => {
                            console.error(
                              `Error creating BOVOComponent for item ${item.itemName}:`,
                              err
                            );
                            return of(null);
                          })
                        )
                    );
                  });
                  return forkJoin(componentCreationRequests);
                }),
                catchError((err) => {
                  console.error(
                    `Error updating BOVOHeader ID ${newBovoHeaderID}:`,
                    err
                  );
                  return throwError(() => err); // Propagate the error
                })
              );
          }),
          catchError((err) => {
            console.error(
              'Error in creating new BOVO Header or its components:',
              err
            );
            return of(null);
          })
        );
      allRequests.push(createHeaderAndComponentsObservable);
    }
    // --- Section 2: Handle MODIFIED Existing Items (Update Logic) ---
    for (const id of Object.keys(changesMap).map((id) => +id)) {
      const changeTypes = changesMap[id];
      if (
        !changeTypes.includes('NEW_ITEM_ADDED') &&
        !changeTypes.includes('ITEM_REMOVED')
      ) {
        // Only process if not new or removed
        const voh = currentList.find((item) => item.inventoryId === id);
        const originalVoh = originalList.find(
          (item) => item.inventoryId === id
        );

        if (!voh || !originalVoh) continue; // Should always find an item if it's not new/remove

        for (const field of changeTypes) {
          // Iterate through specific changed fields
          let dto: any;
          let url: string;
          switch (field) {
            case 'usageQuantity':
            case 'batchSize':
              // $&
              if (voh.bovoComponentID) {
                dto = {
                  bovoComponentID: voh.bovoComponentID,
                  bovoHeaderID: voh.bovoHeaderID,
                  usageQuantity: voh.usageQuantity ?? 0,
                  batchSize: voh.batchSize ?? 0,
                  uomid: voh.uomID ?? 0,
                  variableOverheadItemID: voh.variableOverheadItemID ?? 0,
                  notes: `updated on ${new Date().toISOString()}`,
                };

                url = `${environment.apiUrl}/api/BOVOComponent/Update/${voh.bovoComponentID}`;
                allRequests.push(this.http.put(url, dto, { headers }));
              } else {
                console.warn(
                  `Attempted to update BOVOComponent for item ID ${id} but bovoComponentID is missing. This item might have been newly added but creation failed or wasn't handled correctly.`,
                  voh
                );
              }
              break;

            default:
              console.warn(
                `⚠️ Unhandled field change for existing item: ${field}`
              );
              break;
          }
        }
      }
    }

    this.isSaving = true;
    this.modalMessage = 'Saving changes…';
    this.isSuccess = null;

    forkJoin(allRequests).subscribe({
      next: (results) => {
        const successfulResults = results.filter((result) => result !== null);
        const hasErrors = successfulResults.length !== allRequests.length;

        if (hasErrors) {
          this.isSaving = false;
          // this.isSuccess = false;
          // this.modalMessage = '❌ Some changes failed to save.';
          //console.error('Some requests within the batch failed.');
        } else {
          this.isSaving = false;
          this.isSuccess = true;
          this.modalMessage = this.sanitizer.bypassSecurityTrustHtml(`
          <span style="display: flex; align-items: center; gap: 8px;">
           Changes saved successfully
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="color: #28a745;">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-8.15"></path>
                <polyline points="22 4 12 14.01 9 11.01"></polyline>
            </svg>
          </span>
        `);
        }

        // Deep clone to snapshot the current state into originalVariableOverheadsMap
        this.originalVariableOverheadsMap[productId] = JSON.parse(
          JSON.stringify(this.variableOverheadsMap[productId])
        );

        setTimeout(() => {
          this.closeSaveModal();
          this.modalMessage = '';
          this.isSuccess = null;
        });
      },
      error: (err) => {
        // $&
        this.isSaving = false;
        this.isSuccess = false;
        this.modalMessage = '❌ Error saving some or all changes.';
      },
    });
  }

  getChangedFixedOverheadsMap(productId: number): Record<number, string[]> {
    const originalItems = this.originalFixedOverheadsMap[productId] || [];

    const currentItems = this.fixedOverheadsMap[productId] || [];

    const changesMap: Record<number, string[]> = {};

    // For quick removal check
    const currentItemIds = new Set<number>(
      currentItems.map((item) => item.itemId ?? 0)
    );

    // 1. Check for Modified and Newly Added Items
    for (const current of currentItems) {
      // Auto-generated placeholder rows (no real data entered yet) are not changes.
      if (current.isPlaceholder) continue;

      const original = originalItems.find((o) => o.itemId === current.itemId);
      const changedFields: string[] = [];

      if (!original || current.isNew) {
        changedFields.push('NEW_ITEM_ADDED');
        changesMap[current.itemId ?? 0] = changedFields;
        continue;
      }
      if (current.itemName !== original.itemName)
        changedFields.push('itemName');
      if (current.costPerMonth !== original.costPerMonth)
        changedFields.push('costPerMonth');
      if (current.minutesPerOrder !== original.minutesPerOrder)
        changedFields.push('minutesPerOrder');
      if (current.batchSize !== original.batchSize)
        changedFields.push('batchSize');
      if (changedFields.length > 0) {
        changesMap[current.itemId ?? 0] = changedFields;
      }
    }

    // 2. Check for Removed Items
    for (const original of originalItems) {
      if (!currentItemIds.has(original.itemId ?? 0)) {
        changesMap[original.itemId ?? 0] = ['ITEM_REMOVED'];
      }
    }

    return changesMap;
  }

  /**
   * Returns the list of human-readable category names that have unsaved
   * changes for the given product. Purely in-memory (no HTTP, never throws),
   * so it is safe to call from canDeactivate() to drive the confirm dialog.
   */
  getUnsavedCategories(productId: number): string[] {
    const cats: string[] = [];
    if (productId == null) return cats;

    // Raw Materials
    if (Object.keys(this.getChangedMaterialsMap(productId)).length > 0) {
      cats.push('Raw Materials');
    }

    // Labor (direct + indirect)
    const directChanges = this.getChangedLabourMap(
      this.originalDirectLaborItemsMap[productId] || [],
      this.directLaborItemsMap[productId] || [],
      'direct'
    );
    const indirectChanges = this.getChangedLabourMap(
      this.originalinDirectLaborItemsMap[productId] || [],
      this.indirectLaborItemsMap[productId] || [],
      'indirect'
    );
    if (
      Object.keys(directChanges).length > 0 ||
      Object.keys(indirectChanges).length > 0
    ) {
      cats.push('Labor');
    }

    // Fixed Overheads (placeholders already excluded in the helpers)
    const fixedChanged =
      Object.keys(this.getChangedFixedOverheadsMap(productId)).length > 0 ||
      this.getAddedFixedOverheads(
        this.originalFixedOverheadsMap[productId] || [],
        this.fixedOverheadsMap[productId] || []
      ).length > 0;
    if (fixedChanged) {
      cats.push('Fixed Overheads');
    }

    // Variable Overheads
    if (
      Object.keys(this.getChangedVariableOverheadsMap(productId)).length > 0 ||
      this.deletedBovoComponentIDs.length > 0
    ) {
      cats.push('Variable Overheads');
    }

    // Competitors
    if (Object.keys(this.getChangedCompetitorsMap(productId)).length > 0) {
      cats.push('Competitors');
    }

    // Markup / Target Cost — null-safe, rounded comparison so precision/format
    // drift (e.g. 1dp slider rounding, currency rounding) doesn't false-flag.
    const originalValues = this.originalProductValues.get(productId) || {
      targetCost: null,
      profitMarginPercent: null,
    };
    const norm = (v: number | null | undefined, dp: number): number | null => {
      if (v == null || isNaN(Number(v))) return null;
      const f = Math.pow(10, dp);
      return Math.round(Number(v) * f) / f;
    };
    if (norm(this.markupPercent, 1) !== norm(originalValues.profitMarginPercent, 1)) {
      cats.push('Markup');
    }
    if (norm(this.targetTotalCost, 2) !== norm(originalValues.targetCost, 2)) {
      cats.push('Target Cost');
    }

    return cats;
  }

  getChangedMaterialsMap(productId: number): Record<number, string[]> {
    const originalItems = this.originalMaterialItemsMap[productId] || [];

    const currentItems = this.materialItemsMap[productId] || [];

    const changesMap: Record<number, string[]> = {};

    // Create a set of current item IDs for efficient lookup to detect removed items
    const currentItemIds = new Set<number>(
      currentItems.map((item) => item.inventoryId)
    );

    // --- 1. Check for Modified and Newly Added Items ---
    for (const current of currentItems) {
      const original = originalItems.find(
        (o) => o.inventoryId === current.inventoryId
      );
      const changedFields: string[] = [];

      // If there's no original counterpart OR if it's explicitly marked as a new item
      if (!original || current.isNew) {
        // For new items, all non-internal fields are considered "changed" (added)
        // We can use a special flag for easier identification on the backend
        changedFields.push('NEW_ITEM_ADDED');
        // Optionally, you could list all properties of the new item if needed for backend handling
        // for (const key of Object.keys(current)) {
        //   if (key !== 'isNew' && key !== 'isEditing') {
        //     changedFields.push(key);
        //   }
        // }
        changesMap[current.inventoryId] = changedFields;
        continue; // Move to the next current item
      }

      // Compare properties for existing items
      if (current.purchaseQuantity !== original.purchaseQuantity)
        changedFields.push('purchaseQuantity');
      if (current.profitMarginPercent !== original.profitMarginPercent)
        changedFields.push('profitMarginPercent');
      if (current.targetCost !== original.targetCost)
        changedFields.push('targetCost');
      if (current.purchaseQuantity !== original.purchaseQuantity)
        changedFields.push('purchaseQuantity');
      if (current.itemName !== original.itemName)
        changedFields.push('itemName');
      if (current.purchasePrice !== original.purchasePrice)
        changedFields.push('purchasePrice');
      if (current.usageQuantity !== original.usageQuantity)
        changedFields.push('usageQuantity');
      if (current.batchSize !== original.batchSize)
        changedFields.push('batchSize');
      if (current.description !== original.description)
        changedFields.push('description');
      if (current.itemTypeID !== original.itemTypeID)
        changedFields.push('itemTypeID');
      if (current.baseUOMID !== original.baseUOMID)
        changedFields.push('baseUOMID');
      if (current.lowStockThreshold !== original.lowStockThreshold)
        changedFields.push('lowStockThreshold');
      if (current.sellingPrice !== original.sellingPrice)
        changedFields.push('targetSellingPrice');
      if (current.profitMarginPercent !== original.profitMarginPercent)
        changedFields.push('targetProfitMarginPercent');
      if (current.valuationMethodCode !== original.valuationMethodCode)
        changedFields.push('valuationMethodCode');
      if (current.categoryID !== original.categoryID)
        changedFields.push('categoryID');
      if (current.sku !== original.sku) changedFields.push('sku');
      if (current.targetCost !== original.targetCost)
        changedFields.push('targetCost');
      // Note: purchasePricePerUnit and unitCost are usually calculated,
      // so including them here would only flag a change if a source property changed.
      // It's generally better to track the direct input changes.

      if (changedFields.length > 0) {
        changesMap[current.inventoryId] = changedFields;
      }
    }

    // --- 2. Check for Removed Items ---
    // Iterate through the original items to find any that are no longer in the current list
    for (const original of originalItems) {
      if (!currentItemIds.has(original.inventoryId)) {
        // This item was in the original list but is not in the current list
        changesMap[original.inventoryId] = ['ITEM_REMOVED']; // Special flag for removed items
      }
    }
    // $&
    return changesMap;
  }

  private async saveRawmaterialChangesAsync(
    productId: number,
    changesMap: { [id: number]: string[] }
  ): Promise<boolean> {
    this.clearMaterialItemsCache();
    const changedIds = Object.keys(changesMap).map((id) => +id);

    // Separate new items from existing-item updates
    const newItems: number[] = [];
    for (const inventoryIdString in changesMap) {
      const inventoryId = +inventoryIdString;
      if (changesMap[inventoryId].includes('NEW_ITEM_ADDED')) {
        newItems.push(inventoryId);
      }
    }

    if (newItems.length > 0) {
      // Auto-create Items + BOM entries for new materials (throws on failure)
      await this.autoCreateNewMaterials(productId, newItems, changesMap);

      // Remove NEW_ITEM_ADDED entries — they've been handled
      for (const id of newItems) {
        delete changesMap[id];
      }

      // Recursively save any remaining field-update changes
      const remainingChanges = Object.keys(changesMap).length;
      if (remainingChanges > 0) {
        await this.saveRawmaterialChangesAsync(productId, changesMap);
      }
      return true;
    }

    const token = localStorage.getItem('authToken') || '';
    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    });

    // Collect an array of Observables to forkJoin at the end.
    const requests: Observable<any>[] = [];

    for (const id of changedIds) {
      const material = this.materialItemsMap[productId].find(
        (m) => m.inventoryId === id
      );
      if (!material) continue;

      const fieldsChanged = changesMap[id];

      for (const field of fieldsChanged) {
        let dto: any;
        let url: string;

        switch (field) {
          case 'itemName': {
            // Rebuilt from the stored item: this row's purchase fields come from price
            // history, not the item, and a partial body would overwrite the rest.
            const updateUrl = `${environment.apiUrl}/api/Items/Update?ItemId=${material.inventoryId}`;
            requests.push(
              this.http
                .get<ItemDTO>(`${environment.apiUrl}/api/Items/GetById?ItemsId=${material.inventoryId}`, { headers })
                .pipe(switchMap((item) => this.http.put(updateUrl, { ...item, itemName: material.itemName }, { headers })))
            );
            break;
          }

          case 'purchasePrice':
          case 'purchaseQuantity':
            // Purchase price/quantity are managed from the Inventory dashboard — skip silently.
            break;

          case 'usageQuantity':
          case 'batchSize':
            // Build the full BOMComponentsDTO *once* per component:
            {
              const dto = {
                // NOTE: use exactly the property names in BOMComponentsDTO:
                bomComponentID: material.bomComponentID,
                bomHeaderID: material.bomHeaderID,
                rawMaterialItemID: material.inventoryId,
                quantityRequired: material.usageQuantity,
                batchSize: material.batchSize,
                componentUOMID: material.componentUOMID,
                scrapFactor: material.scrapFactor ?? 0,
                notes: material.notes || '',
              };

              const url = `${environment.apiUrl}/api/BOMComponents/Update?BOMComponentsId=${material.bomComponentID}`;
              requests.push(this.http.put(url, dto, { headers }));
            }
            break;
          case 'targetCost':
            dto = {
              itemId: material.inventoryId,
              targetCost: material.targetCost,
              unitCost: material.unitCost,
              updatedAt: new Date().toISOString(),
            };
            url = `${environment.apiUrl}/api/TargetCost/Update`;
            requests.push(this.http.put(url, dto, { headers }));
            break;

          default:
            // $&
            break;
        }
      }
    }

    // No requests to send — nothing to save (all fields were unsupported or skipped)
    if (requests.length === 0) {
      return false;
    }

    // Execute all HTTP requests and propagate errors to the caller
    await firstValueFrom(forkJoin(requests));

    this.originalMaterialItemsMap[productId] = JSON.parse(
      JSON.stringify(this.materialItemsMap[productId])
    );

    return true;
  }

  /**
   * Auto-creates Items and BOM entries for new materials added inline.
   * For each new material:
   * 1. Creates the Item (raw material, itemTypeID=1) via POST
   * 2. Ensures a BOM Header exists for the selected product
   * 3. Creates a BOM Component linking the item to the BOM header
   */
  private async autoCreateNewMaterials(
    productId: number,
    newItemIds: number[],
    changesMap: { [id: number]: string[] }
  ): Promise<void> {
    const token = localStorage.getItem('authToken') || '';
    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    });

    const materials = this.materialItemsMap[productId] || [];

    // Step 1: Ensure BOM Header exists for this product
    let bomHeaderId = await this.ensureBomHeaderExists(productId, headers);
    if (!bomHeaderId) {
      throw new Error('Failed to create or find BOM Header for product');
    }

    // Step 2: Process each new material sequentially to avoid race conditions
    for (const inventoryId of newItemIds) {
      const material = materials.find((m) => m.inventoryId === inventoryId);
      if (!material) continue;

      // Step 2a: Create the raw material Item (matching inventory dashboard DTO format)
      const defaultUomId = this.getDefaultUomId();
      const uncategorized = this.categories.find(
        (c) => c.categoryName?.toLowerCase() === 'uncategorized'
      );
      const fallbackCategory = uncategorized || this.categories[0];
      const categoryId = material.categoryID || fallbackCategory?.categoryID || 1;

      console.log('Auto-create material values:', {
        itemName: material.itemName,
        purchasePrice: material.purchasePrice,
        purchaseQuantity: material.purchaseQuantity,
        purchasePricePerUnit: material.purchasePricePerUnit,
        usageQuantity: material.usageQuantity,
        batchSize: material.batchSize,
        componentUOMID: material.componentUOMID,
        purchaseUOMID: (material as any).purchaseUOMID,
        baseUOMID: (material as any).baseUOMID,
      });

      const itemDto: any = {
        ItemID: 0,
        ItemName: material.itemName,
        ItemCode: material.itemCode || this.generateSKU(material.itemName),
        itemTypeID: 1, // Raw material
        PurchaseUOMID: (material as any).purchaseUOMID || material.componentUOMID || (material as any).baseUOMID || defaultUomId,
        lowStockThreshold: 0,
        isActive: true,
        valuationMethodCode: 'FIFO',
        categoryID: categoryId,
        profitMarginPercent: 0,
        sellingPrice: 0,
        purchasePrice: material.purchasePrice || 0,
        purchaseQuantity: material.purchaseQuantity || 0,
      };

      try {
        const createdItem = await firstValueFrom(
          this.http.post<any>(
            `${environment.apiUrl}/api/Items/Add`,
            itemDto,
            { headers }
          )
        );

        const newItemId = createdItem.itemID || createdItem.itemId;

        // Step 2b: Create inventory transaction (Receipt) for the purchase quantity
        if (material.purchaseQuantity > 0) {
          const transactionDto = {
            ItemID: newItemId,
            WarehouseID: 1,
            TransactionType: 'Receipt',
            QuantityChange: material.purchaseQuantity,
            CostPerUnit: material.purchasePricePerUnit || (material.purchaseQuantity > 0 ? material.purchasePrice / material.purchaseQuantity : 0),
            LowStockThreshold: 0,
            TransactionDate: new Date().toISOString(),
            ReferenceType: 'Manual Entry',
            ReferenceID: `CALC-${Date.now()}`,
            UserID: 1,
            Notes: 'Added via Cost Calculator',
            BatchNumber: null,
            ExpiryDate: null,
          };

          try {
            await firstValueFrom(
              this.http.post(`${environment.apiUrl}/api/InventoryTransactions/Add`, transactionDto, { headers })
            );
          } catch (txErr) {
            console.warn('Failed to create inventory transaction for item', newItemId, txErr);
          }
        }

        // Step 2c: Check if BOM Component already exists for this item + header
        const existingComponent = await this.findExistingBomComponent(
          bomHeaderId,
          newItemId,
          headers
        );

        if (existingComponent) {
          // Update existing BOM Component
          const updateDto = {
            bomComponentID: existingComponent.bomComponentID,
            bomHeaderID: bomHeaderId,
            rawMaterialItemID: newItemId,
            quantityRequired: material.usageQuantity || 0,
            batchSize: material.batchSize || 1,
            componentUOMID: material.componentUOMID || defaultUomId,
            scrapFactor: material.scrapFactor ?? 0,
            notes: material.notes || '',
          };

          await firstValueFrom(
            this.http.put(
              `${environment.apiUrl}/api/BOMComponents/Update?BOMComponentsId=${existingComponent.bomComponentID}`,
              updateDto,
              { headers }
            )
          );

          material.inventoryId = newItemId;
          material.bomHeaderID = bomHeaderId;
          material.bomComponentID = existingComponent.bomComponentID;
        } else {
          // Create new BOM Component
          const bomComponentDto = {
            bomComponentID: 0,
            bomHeaderID: bomHeaderId,
            rawMaterialItemID: newItemId,
            quantityRequired: material.usageQuantity || 0,
            batchSize: material.batchSize || 1,
            componentUOMID: material.componentUOMID || defaultUomId,
            scrapFactor: material.scrapFactor ?? 0,
            notes: material.notes || '',
          };

          const createdComponent = await firstValueFrom(
            this.http.post<any>(
              `${environment.apiUrl}/api/BOMComponents/Add`,
              bomComponentDto,
              { headers }
            )
          );

          material.inventoryId = newItemId;
          material.bomHeaderID = bomHeaderId;
          material.bomComponentID =
            createdComponent.bomComponentID || createdComponent.bomComponentId;
        }

        material.isNew = false;
        material.isEditing = false;
      } catch (err) {
        console.error(
          `Error auto-creating material "${material.itemName}":`,
          err
        );
        throw err;
      }
    }

    // Update original map so change detection is in sync
    this.originalMaterialItemsMap[productId] = JSON.parse(
      JSON.stringify(this.materialItemsMap[productId])
    );

  }

  /**
   * Ensures a BOM Header exists for the given productId.
   * Returns the bomHeaderID (existing or newly created).
   */
  private async ensureBomHeaderExists(
    productId: number,
    headers: HttpHeaders
  ): Promise<number> {
    try {
      const bomHeaders = await firstValueFrom(
        this.http.get<BOMHeader[]>(
          `${environment.apiUrl}/api/BOMHeaders/GetAll`,
          { headers }
        )
      );

      const existing = bomHeaders.find(
        (h) => h.finishedGoodItemID === productId
      );

      if (existing) {
        return existing.bomHeaderID;
      }

      // Create a new BOM Header
      const bomHeaderDto = {
        bomHeaderID: 0,
        finishedGoodItemID: productId,
        bomVersion: '1.0',
        description: 'Auto-created from Cost Calculator',
        isActive: true,
        validFrom: new Date().toISOString(),
        validTo: null,
        createdAt: new Date().toISOString(),
        userIDCreated: null,
      };

      const created = await firstValueFrom(
        this.http.post<any>(
          `${environment.apiUrl}/api/BOMHeaders/Add`,
          bomHeaderDto,
          { headers }
        )
      );

      return created.bomHeaderID || created.bomHeaderId;
    } catch (err) {
      console.error('Error ensuring BOM Header exists:', err);
      throw err;
    }
  }

  /**
   * Checks if a BOM Component already exists for the given header + raw material item.
   * Returns the component if found, null otherwise.
   */
  private async findExistingBomComponent(
    bomHeaderId: number,
    rawMaterialItemId: number,
    headers: HttpHeaders
  ): Promise<BOMComponent | null> {
    try {
      const allComponents = await firstValueFrom(
        this.http.get<BOMComponent[]>(
          `${environment.apiUrl}/api/BOMComponents/GetAll`,
          { headers }
        )
      );

      return (
        allComponents.find(
          (c) =>
            c.bomHeaderID === bomHeaderId &&
            c.rawMaterialItemID === rawMaterialItemId
        ) || null
      );
    } catch (err) {
      console.error('Error finding existing BOM component:', err);
      return null;
    }
  }

  private generateSKU(productName: string): string {
    if (!productName) return '';

    const cleanName = productName
      .trim()
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-zA-Z0-9\s]/g, '')
      .toUpperCase();

    const words = cleanName.split(/\s+/).filter(Boolean);

    let letters: string;
    if (words.length >= 2) {
      letters = (words[0][0] || 'X') + (words[1].substring(0, 2) || 'XX');
      letters = letters.padEnd(3, 'X');
    } else if (words.length === 1) {
      letters = words[0].substring(0, 3).padEnd(3, 'X');
    } else {
      letters = 'XXX';
    }

    const hash = (str: string): number => {
      let h = 5381;
      for (let i = 0; i < str.length; i++) {
        h = ((h << 5) + h) ^ str.charCodeAt(i);
      }
      return Math.abs(h);
    };

    const alphaOnly = cleanName.replace(/[^A-Z]/g, '');
    const mid = hash(alphaOnly || cleanName) % 1000;
    const last = hash(cleanName + '|SKU') % 1000;

    return `${letters}-${mid.toString().padStart(3, '0')}-${last.toString().padStart(3, '0')}`;
  }

  restoreZeroIfEmpty(event: FocusEvent): void {
    const input = event.target as HTMLInputElement;
    if (input.value === '' || isNaN(parseFloat(input.value))) {
      input.value = '0';
    }
  }

  addNewOverheadCostPools(productId: number, newItems: FixedItem[]): void {
    if (newItems.length === 0) {
      // $&
      return;
    }

    const token = localStorage.getItem('authToken') || '';
    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    });

    // $&
    // $&

    // Process each new item individually
    newItems.forEach((newItem, index) => {
      if (!newItem.overheadCostItemID) {
        // $&
        return;
      }

      const poolDto = {
        poolName: newItem.itemName.trim(),
        description: newItem.itemName.trim(),
        isActive: true,
        finishedGoodID: productId,
        overheadCostItemID: newItem.overheadCostItemID,
      };

      // $&

      this.http
        .post<{ overheadCostPoolID: number }>(
          `${environment.apiUrl}/api/OverheadCostPool/Add`,
          poolDto,
          { headers }
        )
        .subscribe({
          next: (response) => {
            // Update the item with the new pool ID
            newItem.overheadCostPoolID = response.overheadCostPoolID;
            newItem.itemId = response.overheadCostPoolID;

            // Update your maps/arrays as needed
            // Example: this.fixedOverheadsMap[productId] = updatedArray;

            // $&
          },
          error: (error) => {
            // $&
            // $&
            // $&
            // $&
            // $&
          },
        });
    });
  }
  private async saveCombinedCost(
    itemID: number,
    headers: HttpHeaders
  ): Promise<boolean> {
    if (itemID == null) {
      console.warn('saveCombinedCost called with null itemID');
      return false;
    }

    const total = Number(Number(this.totalCost || 0).toFixed(2));
    const nowIso = new Date().toISOString();

    try {
      // --- Fetch existing histories ---
      const getUrl = `${environment.apiUrl}/api/ItemStandardCostHistory/GetByItemId?itemId=${itemID}`;
      let histories: any[] = [];

      try {
        histories = await firstValueFrom(
          this.http.get<any[]>(getUrl, { headers })
        );
      } catch (err: any) {
        if (err && (err.status === 404 || err.status === 204)) {
          histories = [];
        } else {
          console.error('Error fetching cost history:', err);
          throw err;
        }
      }

      // Latest history
      const existingRecord =
        histories.length > 0
          ? histories.reduce((latest, h) =>
              new Date(h.createdAt) > new Date(latest.createdAt) ? h : latest
            )
          : null;
      if (existingRecord) {
        // --- UPDATE - Preserve original createdAt and userIDEntered ---
        const updateDto = {
          costHistoryID: existingRecord.costHistoryID,
          itemID: itemID,
          standardCost: total,
          validFrom: nowIso,
          validTo: nowIso,
          createdAt: existingRecord.createdAt, // Keep original creation time
          userIDEntered: existingRecord.userIDEntered, // Keep original user
        };
        const updateUrl = `${environment.apiUrl}/api/ItemStandardCostHistory/Update/${existingRecord.costHistoryID}`;
        await firstValueFrom(this.http.put(updateUrl, updateDto, { headers }));
      } else {
        // --- ADD - New record gets current timestamp ---
        const addDto = {
          costHistoryID: 0,
          itemID: itemID,
          standardCost: total,
          validFrom: nowIso,
          validTo: nowIso,
          createdAt: nowIso,
          userIDEntered: null,
        };
        const addUrl = `${environment.apiUrl}/api/ItemStandardCostHistory/Add`;
        await firstValueFrom(this.http.post(addUrl, addDto, { headers }));
      }

      return true;
    } catch (err: any) {
      console.error('saveCombinedCost failed:', err);

      // Log the full error response for debugging
      if (err.error) {
        console.error('API Error Details:', err.error);
      }
      if (err.status) {
        console.error('HTTP Status:', err.status);
      }

      return false;
    }
  }

  async saveChanges(
    productId: number,
    initiator: string = 'manual'
  ): Promise<boolean> {
    this.closeSaveModal();
    this.isLoadingData = true;
    const token = localStorage.getItem('authToken') || '';
    const itemID = this.selectedProductId;
    // $&
    // $&

    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    });

    if (productId == null || itemID === null) {
      console.warn(
        'saveChanges called with null productId or selectedProductId'
      );
      this.modalMessage =
        '⚠️ Cannot save: Product not selected or ID is missing.';
      this.isSuccess = false;
      this.isSaving = false;
      return true;
    }

    this.isSaving = true;
    this.modalMessage = 'Saving changes...';
    this.isSuccess = null;

    try {
      let changesSaved = false;
      const combinedCostSaved = await this.saveCombinedCost(itemID, headers);
      if (combinedCostSaved) changesSaved = true;
      // --- STEP 1: Handle New Overhead Items ---
      // $&
      const newOverheadSaved = await this.saveNewOverheadItems(
        productId,
        headers
      );
      if (newOverheadSaved) changesSaved = true;

      // --- STEP 2: Handle Item Updates (Markup) ---
      // $&
      const markupSaved = await this.saveMarkupChanges(itemID, headers);
      if (markupSaved) changesSaved = true;

      // --- STEP 3: Handle Target Cost History ---
      // $&
      const historySaved = await this.saveTargetCostHistory(
        productId,
        itemID,
        headers
      );
      if (historySaved) changesSaved = true;

      // --- STEP 4: Handle Materials ---
      // $&
      const materialsSaved = await this.saveMaterialChanges(productId);
      if (materialsSaved) changesSaved = true;

      // --- STEP 5: Handle Labor ---
      // $&
      const laborSaved = await this.saveLaborChangesAsync(productId);
      if (laborSaved) changesSaved = true;

      // --- STEP 6: Handle Existing Overhead Updates ---
      // $&
      const overheadUpdatesSaved = await this.saveExistingOverheadUpdates(
        productId,
        headers
      );
      if (overheadUpdatesSaved) changesSaved = true;

      // --- STEP 7: Handle Variable Overheads ---
      // $&
      const variableOverheadSaved = await this.saveVariableOverheadChanges(
        productId
      );
      if (variableOverheadSaved) changesSaved = true;

      // --- STEP 8: Handle Competitors ---
      // $&
      const competitorsSaved = await this.saveCompetitorChangesAsync(productId);
      if (competitorsSaved) changesSaved = true;

      // --- STEP 9: Save AI suggestion (only on manual save) ---
      if (initiator === 'manual' && itemID != null) {
        const aiSaved = await this.saveAiSuggestionForProduct(
          itemID,
          this.aiSuggestedSellingPrice,
          this.aiSuggestedMarkup,
          headers
        );
        if (aiSaved) changesSaved = true;
      }

      // Inline edit/new flags are UI-only; clear them after a successful save so
      // guards like addNewFixed() don't treat already-saved rows as pending edits.
      this.fixedItems?.forEach((item) => {
        item.isNew = false;
        item.isEditing = false;
      });
      if (itemID != null) this.fixedOverheadsMap[itemID] = [...this.fixedItems];

      // Final success message
      if (changesSaved) {
        this.isSuccess = true;
        this.showMessageModal = true;

        // $&
        if (initiator === 'manual') {
          this.isLoadingData = false;
          this.showMessageModal = true;
          this.alertInfo = this.sanitizer.bypassSecurityTrustHtml(`
        <span class="message-icon-wrapper">
          All Changes Successfully Saved!
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="color: #28a745;">
            <path d="M22 11.08V12a10 10 0 1 1-5.93-8.15"></path>
            <polyline points="22 4 12 14.01 9 11.01"></polyline>
          </svg>
        </span>
      `);
        }
      } else {
        this.isSuccess = true;
        this.isLoadingData = false;
        this.showMessageModal = true;
        this.alertInfo = this.sanitizer.bypassSecurityTrustHtml(`
        <span class="message-icon-wrapper ">
          <strong class="text-primary fw-semibold">No Changes</strong> Were Detected For Any Category!

        </span>
      `);
      }
    } catch (error) {
      // $&
      this.isSuccess = false;
      this.isLoadingData = false;
      this.alertInfo = '❌ Error occurred while saving changes.';
    } finally {
      this.isSaving = false;
    }

    return this.isSuccess === true;
  }

  private async saveCompetitorChangesAsync(
    productId: number
  ): Promise<boolean> {
    const competitorChangesMap = this.getChangedCompetitorsMap(productId);

    if (Object.keys(competitorChangesMap).length === 0) {
      // $&
      return false;
    }

    // $&

    try {
      // Await the real HTTP completion. Previously this resolved immediately
      // and then overwrote the baseline, so a failed save still looked saved.
      await this.saveCompetitorChanges(productId, competitorChangesMap);

      // Snapshot only after the writes succeeded, so canDeactivate() reflects
      // what is actually persisted.
      this.originalCompetitorPricesMap[productId] = JSON.parse(
        JSON.stringify(this.competitorPricesMap[productId] || [])
      );

      return true;
    } catch (error) {
      console.error('Competitor changes failed to save', error);
      return false;
    }
  }
  private async saveVariableOverheadChanges(
    productId: number
  ): Promise<boolean> {
    const variableOverheadChangesMap =
      this.getChangedVariableOverheadsMap(productId);

    if (
      Object.keys(variableOverheadChangesMap).length === 0 &&
      this.deletedBovoComponentIDs.length === 0
    ) {
      // $&
      return false;
    }

    // $&

    try {
      await new Promise<void>((resolve, reject) => {
        this.saveVariableOverheadsChanges(
          productId,
          variableOverheadChangesMap
        );
        resolve();
      });

      // $&
      return true;
    } catch (error) {
      // $&
      throw error;
    }
  }
  private async saveMaterialChanges(productId: number): Promise<boolean> {
    const materialsChangesMap = this.getChangedMaterialsMap(productId);

    if (Object.keys(materialsChangesMap).length === 0) {
      // $&
      return false;
    }

    await this.saveRawmaterialChangesAsync(productId, materialsChangesMap);
    return true;
  }
  private async saveExistingOverheadUpdates(
    productId: number,
    headers: HttpHeaders
  ): Promise<boolean> {
    const fixedOverheadChangesMap = this.getChangedFixedOverheadsMap(productId);

    if (Object.keys(fixedOverheadChangesMap).length === 0) {
      // $&
      return false;
    }

    // $&

    try {
      // Subscribe/await the returned Observable so the FOH update requests
      // actually fire and persist (previously the Observable was discarded).
      await firstValueFrom(
        this.saveFixedOverheadsChanges(productId, fixedOverheadChangesMap)
      );
      return true;
    } catch (error) {
      // $&
      throw error;
    }
  }
  private async saveLaborChangesAsync(productId: number): Promise<boolean> {
    const directLaborChangesMap = this.getChangedLabourMap(
      this.originalDirectLaborItemsMap[productId] || [],
      this.directLaborItemsMap[productId] || [],
      'direct'
    );

    const indirectLaborChangesMap = this.getChangedLabourMap(
      this.originalinDirectLaborItemsMap[productId] || [],
      this.indirectLaborItemsMap[productId] || [],
      'indirect'
    );

    if (
      Object.keys(directLaborChangesMap).length === 0 &&
      Object.keys(indirectLaborChangesMap).length === 0
    ) {
      // $&
      return false;
    }

    // $&

    try {
      // Await the labour save Observable so creation + ID reconciliation +
      // snapshot refresh all complete before saveChanges returns. Mirrors
      // saveExistingOverheadUpdates / saveRawmaterialChangesAsync.
      await firstValueFrom(
        this.saveLaborChanges(
          productId,
          directLaborChangesMap,
          indirectLaborChangesMap
        )
      );

      // $&
      return true;
    } catch (error) {
      // $&
      throw error;
    }
  }
  private createNewFixedOverheadObservables(
    productId: number,
    headers: HttpHeaders,
    newItems: FixedItem[]
  ): Observable<any>[] {
    const allRequests: Observable<any>[] = [];

    // Min/Batch + Batch Size are shared per product (one ItemOrderDuration per
    // finished good). If the product already has one, do NOT create another —
    // changes to the shared value are persisted by saveExistingOverheadUpdates.
    // Only create the single shared ItemOrderDuration when none exists yet, and
    // only once across all newly-activated overheads.
    const productHasOrderDuration =
      (this.fixedOverheadsMap[productId] || this.fixedItems || []).some(
        (f) => !f.isPlaceholder && (f.itemOrderDurationID ?? 0) > 0
      );
    let orderDurationCreated = false;

    for (const newItem of newItems) {
      if (!newItem.overheadCostItemID) {
        // $&
        continue;
      }

      // 1) Create OverheadCostPool entry
      const safeNewItemName = (newItem.itemName ?? '').trim();
      const poolDto = {
        poolName: safeNewItemName,
        description: safeNewItemName,
        isActive: true,
        finishedGoodID: productId,
        overheadCostItemID: newItem.overheadCostItemID,
      };

      const creationObservable = this.http
        .post<{ overheadCostPoolID: number }>(
          `${environment.apiUrl}/api/OverheadCostPool/Add`,
          poolDto,
          { headers }
        )
        .pipe(
          switchMap((poolResp) => {
            // Update the newItem object with the newly generated OverheadCostPoolID
            newItem.overheadCostPoolID = poolResp.overheadCostPoolID;
            newItem.itemId = poolResp.overheadCostPoolID; // Ensure itemId matches the new pool ID
            newItem.isNew = false; // finalize: no longer a pending new row
            newItem.isEditing = false; // finalize: leave edit mode

            // 2) Update OverheadCostItem is not needed as per your initial `saveNewOverheadItems` logic.
            // Your current `saveNewOverheadItems` code doesn't update OverheadCostItem.
            // We'll proceed directly to ItemOrderDuration.

            // 3) Create the product's single shared ItemOrderDuration — only if
            //    the product doesn't already have one, and only once.
            if (
              !productHasOrderDuration &&
              !orderDurationCreated &&
              newItem.batchSize != null &&
              newItem.minutesPerOrder != null
            ) {
              orderDurationCreated = true;
              const orderDurationDto = {
                itemOrderDurationID: 0, // Set to 0 for new creation
                itemID: productId, // shared per finished good
                batchSize: newItem.batchSize,
                totalTimeMinutes: newItem.minutesPerOrder,
                recordedAt: new Date().toISOString(),
                recordedBy: this.currentUser,
              };

              return this.http.post(
                `${environment.apiUrl}/api/ItemOrderDuration/Add`,
                orderDurationDto,
                { headers }
              );
            }
            // Product already has a shared ItemOrderDuration (or one was just
            // created for an earlier new item) — nothing more to create here.
            return of(null);
          }),
          catchError((err) => {
            console.error(
              'Error saving new fixed overhead item:',
              newItem,
              err
            );
            return of(null); // Allow other requests in forkJoin to proceed
          })
        );
      allRequests.push(creationObservable);
    }
    return allRequests;
  }
  private async saveNewOverheadItems(
    productId: number,
    headers: HttpHeaders
  ): Promise<boolean> {
    const originalList: FixedItem[] =
      this.originalFixedOverheadsMap[productId] || [];
    const currentList: FixedItem[] = this.fixedOverheadsMap[productId] || [];
    const newItems = this.getAddedFixedOverheads(originalList, currentList);

    if (newItems.length === 0) {
      // $&
      return false;
    }

    const allRequests = this.createNewFixedOverheadObservables(
      productId,
      headers,
      newItems
    );

    if (allRequests.length === 0) {
      return false;
    }

    // Use forkJoin to execute all creation requests in parallel
    try {
      const results = await forkJoin(allRequests).toPromise();

      const hasErrors = results?.some((result) => result === null);

      if (hasErrors) {
        // $&
        // You might want to throw an error here to signal the failure
        return false;
      }

      // Update original map after successful saves
      this.originalFixedOverheadsMap[productId] = JSON.parse(
        JSON.stringify(this.fixedOverheadsMap[productId])
      );

      return true;
    } catch (err) {
      // $&
      return false;
    }
  }
  private async saveMarkupChanges(
    itemID: number,
    headers: HttpHeaders
  ): Promise<boolean> {
    const originalValues = this.originalProductValues.get(itemID) || {
      targetCost: null,
      profitMarginPercent: null,
    };

    const currentMarkupPercent = this.markupPercent;

    if (currentMarkupPercent === originalValues.profitMarginPercent) {
      // $&
      return false;
    }

    // $&

    try {
      // Fetch full item details
      const material = await this.http
        .get<ItemDTO>(
          `${environment.apiUrl}/api/Items/GetById?ItemsId=${itemID}`,
          { headers }
        )
        .toPromise();

      const dto: ItemDTO = {
        itemID: material!.itemID,
        itemCode: material!.itemCode || '',
        itemName: material!.itemName,
        description: material!.description || '',
        itemTypeID: material!.itemTypeID,
        purchaseUOMID: material!.purchaseUOMID,
        lowStockThreshold: material!.lowStockThreshold,
        isActive: material!.isActive,
        createdAt: material!.createdAt,
        updatedAt: new Date().toISOString(),
        sellingPrice: material!.sellingPrice,
        profitMarginPercent: currentMarkupPercent,
        purchasePrice: material!.purchasePrice,
        valuationMethodCode: material!.valuationMethodCode,
        categoryID: material!.categoryID,
        purchaseQuantity: material!.purchaseQuantity,
        supplierID: material!.supplierID,
        createdBy: this.currentUser,
      };

      const url = `${environment.apiUrl}/api/Items/Update?ItemId=${itemID}`;
      await this.http.put(url, dto, { headers }).toPromise();

      // $&

      // Update stored original value
      this.originalProductValues.set(itemID, {
        ...this.originalProductValues.get(itemID),
        profitMarginPercent: currentMarkupPercent,
      });

      // Update the cached products array with the new profit margin percent
      const productIndex = this.products.findIndex(p => p.itemID === itemID);
      if (productIndex !== -1) {
        this.products[productIndex].profitMarginPercent = currentMarkupPercent;
      }

      return true;
    } catch (error) {
      // $&
      throw error;
    }
  }
  private async saveTargetCostHistory(
    productId: number,
    itemID: number,
    headers: HttpHeaders
  ): Promise<boolean> {
    const originalValues = this.originalProductValues.get(itemID) || {
      targetCost: null,
      profitMarginPercent: null,
    };

    const currentTargetCost = this.targetTotalCost;
    const currentProfitMarginPercent = this.markupPercent;

    const targetCostChanged = currentTargetCost !== originalValues.targetCost;
    const profitMarginChanged =
      currentProfitMarginPercent !== originalValues.profitMarginPercent;

    if (!targetCostChanged && !profitMarginChanged) {
      return false;
    }

    // $&

    try {
      // Fetch latest history
      const allHistories = await this.http
        .get<ItemTargetHistoryDTO[]>(
          `${environment.apiUrl}/api/ItemTargetHistory/GetByItemId?itemId=${itemID}`,
          { headers }
        )
        .toPromise();

      const filtered = allHistories!.filter((h) => h.itemID === itemID);
      const latest =
        filtered.sort(
          (a, b) =>
            new Date(b.validFrom).getTime() - new Date(a.validFrom).getTime()
        )[0] || null;

      const now = new Date().toISOString();
      const dto: ItemTargetHistoryDTO = {
        itemTargetHistoryID: latest ? latest.itemTargetHistoryID : 0,
        itemID: itemID,
        targetSellingPrice: latest ? latest.targetSellingPrice : 0,
        targetProfitMarginPercent: currentProfitMarginPercent ?? null,
        targetCost: currentTargetCost ?? null,
        validFrom: now,
        validTo: null,
        reasonForChange: 'Updated via product selection',
        userIDEntered: null,
        createdAt: now,
      };

      const isUpdate = !!latest;
      const endpoint = isUpdate
        ? `${environment.apiUrl}/api/ItemTargetHistory/Update?ItemTargetHistoryId=${dto.itemTargetHistoryID}`
        : `${environment.apiUrl}/api/ItemTargetHistory/Add`;

      if (isUpdate) {
        await this.http.put(endpoint, dto, { headers }).toPromise();
      } else {
        await this.http.post(endpoint, dto, { headers }).toPromise();
      }

      // Re-fetch history and update stored values
      this.fetchTargetHistory(productId);
      this.originalProductValues.set(itemID, {
        targetCost: currentTargetCost ?? null,
        profitMarginPercent: currentProfitMarginPercent ?? null,
      });

      return true;
    } catch (error) {
      // $&
      throw error;
    }
  }

  resolveDelete: any;
  closeDeleteConfirm() {
    this.showDeleteConfirm = false;
    this.resolveDelete(false);
  }

  async confirmDelete() {
    this.showDeleteConfirm = false;
    this.resolveDelete(true);
  }
  deleteConfirmed = new Promise((resolve) => {
    this.resolveDelete = resolve;
  });
  /** sum of all indirect‐labour unit costs */
  get totalIndirectLabourCost(): number {
    return this.indirectLabourItems.reduce((sum, l) => sum + l.unitCost, 0);
  }
  deleteIndirectRow(index: number): void {
    // Confirm deletion
    const confirmDelete = confirm('Are you sure you want to delete this row?');
    if (!confirmDelete) return;

    // Remove from the filtered list
    this.filteredIndirectLabourItems.splice(index, 1);

    // Optionally also remove from the original data source if separate
    // this.indirectLabourItems.splice(index, 1);
  }
  editIndirectRow(item: any): void {
    item.isEditing = true;
  }

  // fetching costs for all products
  public fetchAllProductCosts(
    itemType: number,
    isIsolated: boolean = false // Add an optional boolean parameter with a default value
  ): Observable<Record<string, CombinedCostItem[]>> {
    const token = localStorage.getItem('authToken') || '';
    const headers = new HttpHeaders({ Authorization: `Bearer ${token}` });

    return this.http
      .get<ApiItemDTO[]>(`${environment.apiUrl}/api/Items/ByType/${itemType}`, {
        headers,
      })
      .pipe(
        switchMap((allDtos) => {
          if (!allDtos || allDtos.length === 0) {
            console.warn(
              '[fetchAllProductCosts] No products returned for itemType',
              itemType
            );
            return of({} as Record<string, CombinedCostItem[]>);
          }

          const calls: Observable<{
            productName: string;
            costs: CombinedCostItem[];
          }>[] = allDtos.map((dto) => {
            return this.fetchAllCostsForProduct(
              dto.itemID,
              dto.itemName,
              dto.sellingPrice ?? 0
            ).pipe(
              map((costsArr) => ({
                productName: dto.itemName,
                costs: costsArr,
              })),
              catchError((err) => {
                console.error(
                  `[fetchAllProductCosts] Error fetching costs for "${dto.itemName}":`,
                  err
                );
                return of({ productName: dto.itemName, costs: [] });
              })
            );
          });

          return forkJoin(calls).pipe(
            map((resultsArray) => {
              const resultMap: Record<string, CombinedCostItem[]> = {};
              resultsArray.forEach((entry) => {
                resultMap[entry.productName] = entry.costs;
              });

              // Conditionally update UI-bound variables based on isIsolated flag
              if (!isIsolated) {
                // Your existing logic to set this.materialItems, this.fixedItems, etc.
                // Example:
                // this.materialItems = this.getMaterialItems(resultMap);
                // this.fixedItems = this.getFixedItems(resultMap);
              }

              return resultMap;
            }),
            catchError((err) => {
              // $&
              return of({} as Record<string, CombinedCostItem[]>);
            })
          );
        }),
        catchError((err) => {
          // $&
          return of({} as Record<string, CombinedCostItem[]>);
        })
      );
  }

  calculateRawMaterialUnitCost(
    item: RawMaterial,
    conversionFactors: Record<string, number>
  ): RawMaterial {
    // 1) price per *purchase-unit* := total price / total qty
    item.purchasePricePerUnit = item.purchaseQuantity
      ? +(item.purchasePrice / item.purchaseQuantity).toFixed(4)
      : 0;

    // 2) convert usageQuantity into *purchase-unit*
    const pu = this.getConversionFactor(item.purchaseUnit);
    const uu = this.getConversionFactor(item.usageUnit);
    const usageInPurchaseUnits = item.usageQuantity * (uu / pu);

    // 3) cost per finished unit
    item.unitCost =
      item.batchSize > 0
        ? +(
            (item.purchasePricePerUnit * usageInPurchaseUnits) /
            item.batchSize
          ).toFixed(2)
        : 0;
    return item;
  }
  calculateLabourItemUnitCost(item: LabourItem): LabourItem {
    const hrs = Math.max(0, item.hoursWorkedPm ?? 0);
    const salary = Math.max(0, item.monthlySalary ?? 0);
    const batchSize = Math.max(1, item.batchSize ?? 1);

    if (hrs > 0) {
      item.ratePerMinute = +(salary / (hrs * 60)).toFixed(4);
    } else {
      item.ratePerMinute = 0;
    }

    const minutes = item.minutesPerJob ?? 0;
    const totalCost = item.ratePerMinute! * minutes;
    item.unitCost = +(totalCost / batchSize).toFixed(2);
    return item;
  }
  calculateFixedOverheadUnitCost(item: FixedItem): FixedItem {
    item.ratePerMinute =
      item.hoursOpenPerMonth > 0
        ? +(item.costPerMonth / (item.hoursOpenPerMonth * 60)).toFixed(4)
        : 0;

    item.unitCost =
      item.minutesPerOrder && item.ratePerMinute && item.batchSize > 0
        ? +(
            (item.minutesPerOrder * item.ratePerMinute) /
            item.batchSize
          ).toFixed(4)
        : 0;
    return item;
  }

  calculateVariableOverheadUnitCost(
    item: VariableOverhead,
    conversionFactors: Record<string, number>
  ): VariableOverhead {
    const purchasePrice = item.purchasePrice ?? 0;
    const purchaseQuantity = item.purchaseQuantity ?? 0;

    item.purchasePricePerUnit =
      purchaseQuantity > 0 ? +(purchasePrice / purchaseQuantity).toFixed(4) : 0;

    const purchaseUnitCode = item.purchaseUnit ?? '';
    const usageUnitCode = item.usageUnit ?? '';

    const pu = this.getConversionFactor(purchaseUnitCode);
    const uu = this.getConversionFactor(usageUnitCode);

    const usageQuantity = item.usageQuantity ?? 0;
    const usageInPurchaseUnits = usageQuantity * (uu / pu);

    const batchSize = item.batchSize ?? 0;

    item.unitCost =
      batchSize > 0
        ? +(
            ((item.purchasePricePerUnit ?? 0) * usageInPurchaseUnits) /
            batchSize
          ).toFixed(2)
        : 0;

    return item;
  }
  calculateIndirectLabourItemUnitCost(
    item: IndirectLabourItem
  ): IndirectLabourItem {
    const hrs = Math.max(0, item.hoursWorkedPm ?? 0);
    const salary = Math.max(0, item.monthlySalary ?? 0);
    const batchSize = Math.max(1, item.batchSize ?? 1); // Avoid divide-by-zero

    // Recalculate ratePerMinute
    if (hrs > 0) {
      item.ratePerMinute = +(salary / (hrs * 60)).toFixed(4);
    } else {
      item.ratePerMinute = 0;
    }

    // Recalculate unitCost using minutesPerActivity
    const minutes = item.minutesPerActivity ?? 0; // Uses minutesPerActivity
    const totalCost = item.ratePerMinute! * minutes;
    item.unitCost = +(totalCost / batchSize).toFixed(2);
    return item;
  }
  calculateDirectLabourItemUnitCost(item: LabourItem): LabourItem {
    const hrs = Math.max(0, item.hoursWorkedPm ?? 0);
    const salary = Math.max(0, item.monthlySalary ?? 0);
    const batchSize = Math.max(1, item.batchSize ?? 1); // Avoid divide-by-zero

    // Recalculate ratePerMinute
    if (hrs > 0) {
      item.ratePerMinute = +(salary / (hrs * 60)).toFixed(4);
    } else {
      item.ratePerMinute = 0;
    }

    // Recalculate unitCost using minutesPerJob
    const minutes = item.minutesPerJob ?? 0; // Uses minutesPerJob
    const totalCost = item.ratePerMinute! * minutes;
    item.unitCost = +(totalCost / batchSize).toFixed(2);
    return item;
  }

  // fetch all costs for a single product
  private fetchAllCostsForProduct(
    productId: number,
    productName: string,
    sellingPrice?: number
  ): Observable<CombinedCostItem[]> {
    const rawMaterials$ = this.fetchRawMaterials(productId, true).pipe(
      catchError((err) => {
        console.warn(
          `[${productName}] fetchRawMaterials error, returning empty array:`,
          err
        );
        return of([] as RawMaterial[]);
      }),
      map((raws) =>
        raws.map<CombinedCostItem>((m) => {
          const calculatedM = this.calculateRawMaterialUnitCost(
            m,
            this.conversionFactors
          );
          return {
            productId,
            productName,
            sellingPrice,
            category: 'Raw Material',
            itemOrRole: calculatedM.itemName,
            unitCost: calculatedM.unitCost ?? 0, // <--- Add ?? 0 here
          };
        })
      )
    );

    const directLabor$ = this.fetchDirectLabourItems(productId, true).pipe(
      catchError((err) => {
        console.warn(
          `[${productName}] fetchDirectLabourItems error, returning empty array:`,
          err
        );
        return of([] as LabourItem[]);
      }),
      map((dl) =>
        dl.map<CombinedCostItem>((lab) => {
          const calculatedLab = this.calculateDirectLabourItemUnitCost(lab);
          return {
            productId,
            productName,
            sellingPrice,
            category: 'Direct Labor',
            itemOrRole:
              calculatedLab.roleDescription ||
              calculatedLab.activity ||
              calculatedLab.employeeName ||
              'Unnamed Labor',
            unitCost: calculatedLab.unitCost ?? 0, // <--- Add ?? 0 here
          };
        })
      )
    );

    const indirectLabor$ = this.fetchIndirectLabourItems(productId, true).pipe(
      catchError((err) => {
        console.warn(
          `[${productName}] fetchIndirectLabourItems error, returning empty array:`,
          err
        );
        return of([] as IndirectLabourItem[]);
      }),
      map((il) =>
        il.map<CombinedCostItem>((lab) => {
          const calculatedLab = this.calculateIndirectLabourItemUnitCost(lab);
          return {
            productId,
            productName,
            sellingPrice,
            category: 'Indirect Labor',
            itemOrRole:
              calculatedLab.roleDescription ||
              calculatedLab.activity ||
              'Unnamed Indirect',
            unitCost: calculatedLab.unitCost ?? 0, // <--- Add ?? 0 here
          };
        })
      )
    );

    const fixedOverheads$ = this.fetchFixedOverheads(productId, true).pipe(
      catchError((err) => {
        console.warn(
          `[${productName}] fetchFixedOverheads error, returning empty array:`,
          err
        );
        return of([] as FixedItem[]);
      }),
      map((fo) =>
        fo.map<CombinedCostItem>((f) => {
          const calculatedF = this.calculateFixedOverheadUnitCost(f);
          return {
            productId,
            productName,
            sellingPrice,
            category: 'Fixed Overhead',
            itemOrRole: calculatedF.itemName,
            unitCost: calculatedF.unitCost ?? 0, // <--- Add ?? 0 here
          };
        })
      )
    );

    const variableOverheads$ = this.fetchVariableOverheads(productId).pipe(
      catchError((err) => {
        console.warn(
          `[${productName}] fetchVariableOverheads error, returning empty array:`,
          err
        );
        return of([] as VariableOverhead[]);
      }),
      map((vo) =>
        vo.map<CombinedCostItem>((v) => {
          const calculatedV = this.calculateVariableOverheadUnitCost(
            v,
            this.conversionFactors
          );

          return {
            productId,
            productName,
            sellingPrice,
            category: 'Variable Overhead',
            itemOrRole: calculatedV.itemName,
            unitCost: calculatedV.unitCost ?? 0, // <--- Add ?? 0 here
          };
        })
      )
    );

    return forkJoin([
      rawMaterials$,
      directLabor$,
      indirectLabor$,
      fixedOverheads$,
      variableOverheads$,
    ]).pipe(
      map(([rawArr, directLabArr, indirectLabArr, fixedArr, varOvhArr]) => {
        const combined = [
          ...rawArr,
          ...directLabArr,
          ...indirectLabArr,
          ...fixedArr,
          ...varOvhArr,
        ];

        // If there are no cost items (e.g., no BOM), still return a placeholder so sellingPrice can surface in UI
        if (combined.length === 0) {
          combined.push({
            productId,
            productName,
            sellingPrice,
            category: 'Selling Price',
            itemOrRole: 'Selling Price',
            unitCost: 0,
          });
        }

        return combined;
      }),
      catchError((err) => {
        console.error(
          `[fetchAllCostsForProduct][${productName}] forkJoin failed:`,
          err
        );
        return of([] as CombinedCostItem[]);
      })
    );
  }
  onAvgSalesBlur() {
    this.avgSalesPM = Math.abs(this.avgSalesPM || 0);
    this.onAvgSalesChange(this.avgSalesPM);
  }
  onTargetCostChange(value: number) {
    this.hasUserEdited = true;
    this.targetTotalCost = value;
  }
  onAvgSalesChange(value: number) {
    this.avgSalesPM = Math.max(0, value);
    this.hasUserEdited = true;
    // Update Labour items
    this.labourItems.forEach((l) => {
      l.monthlySalary = this.avgSalesPM;
      l.unitCost = this.avgSalesPM > 0 ? l.monthlySalary / this.avgSalesPM : 0;
    });

    this.indirectLabourItems.forEach((l) => {
      l.monthlySalary = this.avgSalesPM;
      l.unitCost = this.avgSalesPM > 0 ? l.monthlySalary / this.avgSalesPM : 0;
    });

    // Update Variable Overheads

    this.emitCostDataChange();
  }
  emitCostDataChange(): void {
    this.costDataChange.emit({
      directMaterials: [...this.materialItems],
      directLabor: this.labourItems,
      indirectLabor: this.indirectLabourItems,
      fixedOverheads: this.fixedItems,
      variableOverheads: this.variableData,
      competitors: this.competitors,
    });
  }

  getAverageSalesPM(): number {
    const all = [...this.indirectLabourItems];
    if (!all.length) return 0;
    const sum = all.reduce((s, l) => s + l.monthlySalary, 0);
    return Math.round(sum / all.length);
  }

  variableDummyData: Record<number, RawMaterial[]> = {
    1: [
      {
        inventoryId: 31,
        itemName: 'Utilities',
        usageQuantity: 1,
        unitCost: 300,
        purchaseQuantity: 0,
        purchasePrice: 0,
        purchasePricePerUnit: 0,
        batchSize: 1,
        purchaseUnit: '',
        usageUnit: '',
        isNew: false,
        isEditing: false,
        itemCode: '',
        description: '',
        itemTypeID: 0,
        baseUOMID: 0,
        lowStockThreshold: 0,
        isActive: false,
        createdAt: '',
        updatedAt: '',
        sellingPrice: 0,
        profitMarginPercent: 0,
        targetCost: 0,
        valuationMethodCode: '',
        categoryID: 0,
        sku: '',
        bomComponentID: 0,
        bomHeaderID: 0,
        componentUOMID: 0,
        scrapFactor: null,
        notes: '',
      },
    ],
  };
  // Private full lists for each category (source of truth)
  private materialsFull: RawMaterial[] = [];
  private labourFull: RawMaterial[] = [];

  // The search term (applies to the active tab)

  // The sort option selected.
  // Allowed values: 'cost-asc', 'cost-desc', 'usage-asc', 'usage-desc',
  // 'availability-asc' (alphabetical A-Z), 'availability-desc' (alphabetical Z-A)
  sortBy: string = '';

  @Output() costDataChange = new EventEmitter<{
    directMaterials: RawMaterial[];
    directLabor: LabourItem[];
    indirectLabor: IndirectLabourItem[];
    fixedOverheads: FixedItem[];
    variableOverheads: VariableOverhead[]; // ← here
    competitors: Competitor[];
  }>();
  @Output() markupPercentChange = new EventEmitter<{
    markupPercent: number;
  }>();
  @Output() selectedProductChange = new EventEmitter<{
    selectedProductName: string;
  }>();
  competitorForm!: FormGroup;

  //edit competittor details
  // Helper to sort items based on the sortBy value.

  // new: holds current slider value

  // Assuming VariableOverheadItem is the interface for your table items
  // and VariableOverhead is the interface for your backend model.
  // They should ideally be similar or you'd need a mapping function.

  saveVariableItem(item: VariableOverheadItem): void {
    // Initial validation for itemName remains important
    if (!item.itemName || !item.itemName.trim()) {
      return;
    }
    // $&
    // Set isModified flag if needed
    item.isModified = true;

    // Recalculate any derived values BEFORE creating itemToSave
    this.recalculateVariableOverhead(item);

    const itemToSave: VariableOverhead = {
      itemName: item.itemName ?? '',
      purchasePrice: item.purchasePrice ?? 0,
      purchaseQuantity: item.purchaseQuantity ?? 0,
      uomID: item.uomID ?? null,
      usageQuantity: item.usageQuantity ?? 0,
      batchSize: item.batchSize ?? 1,

      monthlyCost: (item as any).monthlyCost ?? 0,
      estimatedSales: (item as any).estimatedSales ?? 0,
      variableOverheadItemID: item.variableOverheadItemID ?? 0,
      invalidLowStockThreshold: item.invalidLowStockThreshold ?? false,
      overhead: item.overhead ?? null,
      supplierID: item.supplierID ?? null,
      overheadCostPoolID: item.overheadCostPoolID ?? 0,

      inventoryId: item.inventoryId,
      purchasePricePerUnit: item.purchasePricePerUnit,
      unitCost: item.unitCost,
      purchaseUOMID: item.purchaseUOMID,

      bovoComponentID: item.bovoComponentID ?? 0,
      bovoHeaderID: item.bovoHeaderID ?? 0,

      isLocallyEditing: false,
      isNew: item.isNew ?? false,
      isModified: item.isModified ?? false,
    };

    // $&

    // Update variableFull array
    const index = this.variableFull.findIndex(
      (v) =>
        v.inventoryId === itemToSave.inventoryId ||
        (itemToSave.isNew && v === item)
    );

    if (index !== -1) {
      this.variableFull[index] = itemToSave;
    } else {
      const isDuplicate = this.variableFull.some(
        (v) =>
          v.inventoryId === itemToSave.inventoryId &&
          v.overheadCostPoolID === itemToSave.overheadCostPoolID
      );

      if (isDuplicate) {
        alert(
          '❌ Entry cannot be added. This variable overhead already exists in the selected cost pool.'
        );
        return;
      }

      this.variableFull.push(itemToSave);
    }

    // Update variableOverheadsMap
    if (this.selectedProductId != null) {
      const mapItems = this.variableOverheadsMap[this.selectedProductId];
      if (mapItems) {
        const mapIndex = mapItems.findIndex(
          (v) =>
            v.inventoryId === itemToSave.inventoryId ||
            (itemToSave.isNew && v === item)
        );

        if (mapIndex !== -1) {
          mapItems[mapIndex] = itemToSave;
        } else {
          const isDuplicate = mapItems.some(
            (v) =>
              v.inventoryId === itemToSave.inventoryId &&
              v.overheadCostPoolID === itemToSave.overheadCostPoolID
          );

          if (isDuplicate) {
            alert(
              '❌ Entry cannot be added. This variable overhead already exists in the selected cost pool.'
            );
            return;
          }

          mapItems.push(itemToSave);
        }

        this.variableOverheadsMap[this.selectedProductId] = [...mapItems]; // Ensure change detection
      }
    }

    // Final cleanup and event updates
    item.isLocallyEditing = false;

    this.emitCostDataChange();

    const productName = this.selectedProduct;
    const productId = this.selectedProductId;

    if (!productName || productId == null) {
      console.warn(
        '[RawMaterialsComponent] No product selected; cannot update combined costs.'
      );
      return;
    }

    const updatedList: CombinedCostItem[] = this.getCombinedCosts();
    this.allProductCosts[productName] = updatedList;
    this.allProductCosts = { ...this.allProductCosts };

    this.allProductCostsLoaded.emit(this.allProductCosts);

    // Reminder: Save to backend API here if applicable.
  }

  // called on slider move

  getSellingPrice(): number {
    const sellingPrice = (1 + this.markupPercent / 100) * this.totalCost;
    return Math.round(sellingPrice * 100) / 100; // round to 2 decimal places
  }

  private applyMarkupToAllItems(pct: number) {
    this.labourData.forEach((item) => {
      // example: bump each unitCost by pct%
      // item.unitCost = item.baseUnitCost * (1 + pct / 100);
    });
  }
  get indirectLabourData(): IndirectLabourItem[] {
    return this.currentCategory === 'indirect-labour'
      ? this.indirectLabourItems
      : [];
  }
  saveCompetitorRow(comp: Competitor, index: number): void {
    // Validate. Returning silently here means the row never reaches
    // competitorPricesMap and is therefore never saved, with no indication to
    // the user — so say why instead of just dropping it.
    if (comp.name?.trim() === '' && index !== 0) {
      this.showCompetitorSaveError('Enter a competitor name before saving this row.');
      return;
    }
    if (comp.price < 0) {
      this.showCompetitorSaveError('Competitor price cannot be negative.');
      return;
    }

    comp.isEditing = false;
    comp.isNew = false;
    comp.updatedAt = new Date().toISOString(); // Update the updatedAt timestamp

    if (this.selectedProductId != null) {
      const updatedCompetitors = [
        ...(this.competitorPricesMap[this.selectedProductId] || []),
      ];
      updatedCompetitors[index] = { ...comp };

      // Update current map
      this.competitorPricesMap[this.selectedProductId] = updatedCompetitors;

      // Refresh UI binding (optional)
      this.competitors = [...updatedCompetitors];
    }

    this.emitCostDataChange();
  }

  addNewIndirectLabour() {
    const isEditing = this.indirectLabourData.some((item) => item.isEditing);
    if (isEditing) {
      this.showMessageModal = true;
      this.alertInfo =
        'Please save all inline edits before adding a new entry.';
      return;
    }
    const isPositive = this.checkTargetTotalCost(this.targetTotalCost);
    if (!isPositive) {
      this.showMessageModal = true;
      this.alertInfo = this.totalTargetCostAlert;
      return;
    }
    this.indirectLabourData.unshift({
      inventoryId: 0,
      roleDescription: '',
      monthlySalary: 0,

      unitCost: 0,
      isNew: true,

      isEditing: true,
      hoursWorkedPm: 0,
      ratePerMinute: 0,
      minutesPerActivity: 0,
      activity: '',
      operationname: undefined,
      employeeName: '',
      labourRateID: 0,
      itemOperationID: 0,
      employeeID: null,
      roleID: null,
      rateType: '',
      currency: '',
      effectiveFromDate: '',
      effectiveToDate: null,
      createdAt: '',
      updatedAt: '',
      operationTypeID: 0,
      operationID: 0,
      batchSize: 1,
    });
  }
  onVOHNameChange(item: VariableOverheadItem, selectedItemId: number): void {
    const selectedItem = this.items.find((i) => i.itemID === selectedItemId);
    if (selectedItem) {
      item.itemName = selectedItem.itemName;
      item.inventoryId = selectedItem.itemID; // Ensure inventoryId is set

      // Set UOMs and pricing directly from the selectedItem (Item DTO)
      item.uomID = selectedItem.purchaseUOMID || null; // Usage UOMID
      item.purchaseUOMID = selectedItem.purchaseUOMID; // Purchase UOMID
      item.purchasePrice = selectedItem.purchasePrice;
      item.purchaseQuantity = selectedItem.purchaseQuantity;
      item.supplierID = selectedItem.supplierID; // Supplier ID from Item DTO

      // Calculate purchasePricePerUnit
      item.purchasePricePerUnit = item.purchaseQuantity
        ? item.purchasePrice / item.purchaseQuantity
        : 0;

      // Set validFrom and validTo. Using createdAt from Item DTO for validFrom.
      // validTo is null as it's not present in the Item DTO.
      item.validFrom = selectedItem.createdAt;
      item.validTo = null;

      // Find the UOM code based on purchaseUOMID
      const purchaseUOM = this.unitsOfMeasure.find(
        (uom) => uom.uomID === item.purchaseUOMID
      );
      item.purchaseUnit = purchaseUOM ? purchaseUOM.uomCode : '';

      // Find the UOM code based on usage UOMID (uomID)
      const usageUOM = this.unitsOfMeasure.find(
        (uom) => uom.uomID === item.uomID
      );
      item.usageUnit = usageUOM ? usageUOM.uomCode : '';

      // Initialize other fields that were previously from history but not in Item DTO
      item.supplierPriceHistoryId = undefined;
      item.currencyCode = '';
      item.uomConversionFactor = undefined;
      item.supplierItemCode = '';
      item.supplierLocationID = undefined;
      item.leadTimeDays = undefined;
      item.historyCreatedAt = '';
      item.userIDEntered = undefined;
    } else {
      // Handle case where selected item is not found (e.g., reset values)
      item.itemName = '';
      item.purchasePrice = 0;
      item.purchaseQuantity = 0;
      item.purchasePricePerUnit = 0;
      item.purchaseUnit = '';
      item.usageUnit = '';
      item.uomID = null; // Reset usage UOMID
      item.purchaseUOMID = null; // Reset purchase UOMID
      item.validFrom = '';
      item.validTo = null;
      item.supplierID = null;
      item.supplierPriceHistoryId = undefined;
      item.currencyCode = '';
      item.uomConversionFactor = undefined;
      item.supplierItemCode = '';
      item.supplierLocationID = undefined;
      item.leadTimeDays = undefined;
      item.historyCreatedAt = '';
      item.userIDEntered = undefined;
      console.warn(
        `Selected item with ID ${selectedItemId} not found in local items cache.`
      );
    }

    item.isModified = true; // Mark as modified
    this.onVariableOHChange(item); // Recalculate based on new purchase data
  }

  addNewVariableOH(): void {
    const isEditing = this.variableFull.some((item) => item.isEditing);
    if (isEditing) {
      this.showMessageModal = true;
      this.alertInfo =
        'Please save all inline edits before adding a new entry.';
      return;
    }
    const isPositive = this.checkTargetTotalCost(this.targetTotalCost);
    if (!isPositive) {
      this.showMessageModal = true;
      this.alertInfo = this.totalTargetCostAlert;
      return;
    }
    const newItem: VariableOverhead = {
      // Core identification
      inventoryId: null, // This will be assigned a temporary UI ID or left null
      itemName: '',
      variableOverheadItemID: 0, // Ensure this is also initialized if it's the primary ID

      // Supplier and pricing
      supplierLocationID: 0,
      purchasePrice: 0,
      purchaseQuantity: 0,
      purchaseUnit: '', // Initialize as empty string or a default code
      purchasePricePerUnit: 0,

      // Usage and batch
      usageQuantity: 0,
      usageUnit: '', // Initialize as empty string or a default code
      batchSize: 1,
      unitCost: 0,

      // Units and categories
      categoryID: null,
      lowStockThreshold: null,

      // Business logic
      leadTimeDays: null,
      userIDEntered: null,

      validFrom: new Date().toISOString(),
      validTo: null,

      // UI state flags
      isNew: true,
      isEditing: true, // Typically, a new item should be in edit mode
      isModified: false, // Not modified yet, just new
      isLocallyEditing: true,

      // Properties that are required by VariableOverhead (which newItem is typed as)
      invalidLowStockThreshold: false,
      overhead: null,
      supplierID: null,
      uomID: null, // Initialize usage UOM to null
      overheadCostPoolID: 0,

      // Other properties that VariableOverhead expects with sensible defaults
      monthlyCost: 0,
      estimatedSales: 0,
      // Add purchaseUOMID and set it to null initially
      purchaseUOMID: null, // Initialize purchase UOM to null
      bovoComponentID: 0, // Add if required and needed a default
      bovoHeaderID: 0, // Add if required and needed a default
      notes: '', // Add if required and needed a default
      itemCode: '', // Add if required and needed a default
      description: '', // Add if required and needed a default
      itemTypeID: 0, // Add if required and needed a default
      baseUOMID: 0, // Add if required and needed a default
      isActive: true, // Add if required and needed a default
      createdAt: new Date().toISOString(), // Add if required and needed a default
      updatedAt: '', // Add if required and needed a default
      targetSellingPrice: 0, // Add if required and needed a default
      targetProfitMarginPercent: 0, // Add if required and needed a default
      targetCost: 0, // Add if required and needed a default
      valuationMethodCode: '', // Add if required and needed a default
      sku: '', // Add if required and needed a default
      supplierPriceHistoryId: 0, // Add if required and needed a default
      currencyCode: '', // Add if required and needed a default
      uomConversionFactor: 0, // Add if required and needed a default
      supplierItemCode: '', // Add if required and needed a default
      historyCreatedAt: new Date().toISOString(), // Add if required and needed a default
      invalidOverhead: false, // Add if required and needed a default
      invalidSupplier: false, // Add if required and needed a default
      invalidPurchasePrice: false, // Add if required and needed a default
      invalidPurchaseQuantity: false, // Add if required and needed a default
      invalidUOM: false, // Add if required and needed a default
      invalidCategory: false, // Add if required and needed a default
      itemID: null, // Or same as inventoryId, depending on your model
      isAutoPurchasePrice: false, // Add if required and needed a default
      isAutoPurchaseQuantity: false, // Add if required and needed a default
      customOverhead: null, // Add if required and needed a default
      overheadName: '', // Add if required and needed a default
    };

    this.variableFull.unshift(newItem);
    this.filteredVariableItems.unshift(newItem);

    // You might want to pre-select a default UOM if available,
    // e.g., if you have a "Units" UOM with ID 1, you could set:
    // if (this.unitOfMeasures.length > 0) {
    //   newItem.uomID = this.unitOfMeasures[0].uomid; // Or a specific default ID
    //   newItem.purchaseUOMID = this.unitOfMeasures[0].uomid;
    // }
  }

  saveNewVariableItem(item: VariableOverhead): void {
    if (item.itemName.trim()) {
      item.isNew = false;
      this.emitCostDataChange();
    }
  }
  startEditVariableOH(index: number): void {
    // Ensure only one item is in edit mode at a time
    this.filteredVariableItems.forEach((item, i) => {
      if (i !== index) {
        item.isLocallyEditing = false;
      }
    });
    this.filteredVariableItems[index].isLocallyEditing = true;
    // You might also want to store a copy of the original item here
    // for potential cancellation of edits.
  }
  checkTargetTotalCost(targetTotalCost: number | null) {
    // We use the greater than operator (>) to check if the number is positive.
    // The function returns true if the condition is met.
    if (targetTotalCost === null) return;
    if (targetTotalCost > 0) {
      return true;
    } else {
      // If the condition is false, the function returns false.
      return false;
    }
  }

  addNewMaterial(): void {
    // Check if any material item is currently in editing mode
    const isEditing = this.materialItems.some((item) => item.isEditing);
    if (isEditing) {
      this.showMessageModal = true;
      this.alertInfo =
        'Please save all inline edits before adding a new entry.';
      return;
    }

    // Your existing logic for checking target total cost
    const isPositive = this.checkTargetTotalCost(this.targetTotalCost);
    if (!isPositive) {
      this.showMessageModal = true;
      this.alertInfo = this.totalTargetCostAlert;
      return;
    }

    // Your existing logic for adding a new material item
    this.materialItems.unshift({
      inventoryId: -(Date.now() + Math.floor(Math.random() * 1000)),
      itemName: '',
      purchaseQuantity: 0,
      purchasePrice: 0,
      purchasePricePerUnit: 0,
      usageQuantity: 0,
      unitCost: 0,
      isNew: true,
      batchSize: 1,
      isEditing: true,
      purchaseUnit: '',
      usageUnit: '',
      itemCode: '',
      description: '',
      itemTypeID: 0,
      baseUOMID: 0,
      lowStockThreshold: 0,
      isActive: false,
      createdAt: '',
      updatedAt: '',
      sellingPrice: 0,
      profitMarginPercent: 0,
      targetCost: 0,
      valuationMethodCode: '',
      categoryID: 0,
      sku: '',
      bomComponentID: 0,
      bomHeaderID: 0,
      componentUOMID: 0,
      scrapFactor: null,
      notes: '',
    });
  }
  startEditMaterial(index: number): void {
    const m = this.materialItems[index];
    m.isEditing = true;
  }
  public getCombinedCosts(): CombinedCostItem[] {
    const pid = this.selectedProductId ?? 0;
    const pname = this.selectedProduct;

    // Get the selling price from the selected product
    const selectedProd = this.products.find((p) => p.itemID === pid);
    const sellingPrice = selectedProd?.sellingPrice ?? undefined;

    // 1) Raw Materials
    const materials: CombinedCostItem[] = this.materialItems.map((m) => ({
      productId: pid,
      productName: pname,
      sellingPrice,
      category: 'Raw Material',
      itemOrRole: m.itemName,
      unitCost: m.unitCost,
    }));

    // 2) Direct Labor
    const directLabors: CombinedCostItem[] = this.labourItems.map((l) => ({
      productId: pid,
      productName: pname,
      category: 'Direct Labor',
      itemOrRole:
        l.roleDescription || l.activity || l.employeeName || 'Unnamed Direct',
      unitCost: l.unitCost,
    }));

    // 3) Indirect Labor
    const indirectLabors: CombinedCostItem[] = this.indirectLabourItems.map(
      (l) => ({
        productId: pid,
        productName: pname,
        category: 'Indirect Labor',
        itemOrRole: l.roleDescription || l.activity || 'Unnamed Indirect',
        unitCost: l.unitCost,
      })
    );

    // 4) Fixed Overheads
    const fixed: CombinedCostItem[] = this.fixedItems.map((f) => ({
      productId: pid,
      productName: pname,
      category: 'Fixed Overhead',
      itemOrRole: f.itemName,
      unitCost: f.unitCost,
    }));

    // 5) Variable Overheads
    const variable: CombinedCostItem[] = this.variableData.map((v) => ({
      productId: pid,
      productName: pname,
      category: 'Variable Overhead',
      itemOrRole: v.itemName,
      unitCost: v.unitCost ?? 0, // <--- Add the nullish coalescing operator here
    }));

    const allCosts = [
      ...materials,
      ...directLabors,
      ...indirectLabors,
      ...fixed,
      ...variable,
    ];

    // If no cost items but we have a selling price, include a placeholder so
    // the parent component can still read the selling price
    if (allCosts.length === 0 && sellingPrice != null && sellingPrice > 0) {
      allCosts.push({
        productId: pid,
        productName: pname,
        sellingPrice,
        category: 'Product',
        itemOrRole: pname,
        unitCost: 0,
      });
    }

    return allCosts;
  }
  private getAuthHeaders(): HttpHeaders {
    const token = localStorage.getItem('authToken');
    if (!token) {
      throw new Error('Authentication error. Please log in again.');
    }
    return new HttpHeaders({
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    });
  }
  fetchLatestPriceForItem(itemID: number, item: VariableOverhead): void {
    const headers = this.getAuthHeaders();

    this.http
      .get<ItemSupplierPriceHistoryDTO[]>(
        `${environment.apiUrl}/api/ItemSupplierPriceHistory/GetByItemID/${itemID}`,
        { headers }
      )
      .pipe(
        catchError((error) => {
          // $&
          // Don't show alert for this, just log the error
          return of([]);
        })
      )
      .subscribe((priceHistory: ItemSupplierPriceHistoryDTO[]) => {
        if (priceHistory && priceHistory.length > 0) {
          // Sort by validFrom date to get the most recent
          const sortedHistory = priceHistory.sort(
            (a, b) =>
              new Date(b.validFrom).getTime() - new Date(a.validFrom).getTime()
          );

          const latestPrice = sortedHistory[0];

          // Auto-populate price and quantity fields
          item.purchasePrice = latestPrice.purchasePrice;
          item.purchaseQuantity = latestPrice.purchaseQuantity;
          item.supplierLocationID = latestPrice.supplierLocationID;

          // Find the supplier ID from supplier location
          const supplierLocation = this.supplierLocations.find(
            (loc) => loc.locationID === latestPrice.supplierLocationID
          );
          // if (supplierLocation) {
          //   item.supplierID = supplierLocation.supplierID;
          // }

          // Find the UOM and set the purchase unit
          const uom = this.unitsOfMeasure.find(
            (u) => u.uomID === latestPrice.purchaseUOMID
          );
          if (uom) {
            item.purchaseUnit = uom.uomCode;
          }

          // Mark fields as auto-populated (readonly)
          // item.isAutoPurchasePrice = true;
          // item.isAutoPurchaseQuantity = true;

          // Calculate price per unit
          if (item.purchasePrice && item.purchaseQuantity) {
            item.purchasePricePerUnit =
              item.purchasePrice / item.purchaseQuantity;
          }

          // Trigger calculations
          // this.onVariableOHChange(item);
        } else {
          // No price history found, allow manual entry
          // item.purchasePrice = null;
          // item.purchaseQuantity = null;
          // item.purchaseUnit = 'g';
          // item.purchasePricePerUnit = 0;
          // item.isAutoPurchasePrice = false;
          // item.isAutoPurchaseQuantity = false;
          // item.supplierLocationID = null;
          // item.supplierID = null;
          // item.uomID = null;
        }
      });
  }

  // Save new or edited row
  saveMaterialRow(item: RawMaterial): void {
    // 1) Basic validation
    if (!item.itemName.trim()) {
      this.showMessageModal = true;
      this.alertInfo = 'Please enter an item name.';
      return;
    }

    if (!item.purchaseUnit) {
      this.showMessageModal = true;
      this.alertInfo = 'Please select a UOM for purchase quantity.';
      return;
    }

    if (!item.usageUnit) {
      this.showMessageModal = true;
      this.alertInfo = 'Please select a UOM for usage quantity.';
      return;
    }

    item.isNew = false;
    item.isEditing = false; // Mark the specific item as no longer editing

    // Assign a unique negative temp ID so each new item tracks independently in changesMap.
    if (!item.inventoryId) {
      item.inventoryId = -(Date.now() + Math.floor(Math.random() * 1000));
    }

    // 2) Re-run any child-level recalculations (unitCost, etc.)
    // Calling onMaterialChange(item) will perform recalculations on the 'item' object directly.
    this.onMaterialChange(item); // This directly modifies properties on 'item'

    // Find the index of the item in the current materialItems array
    const index = this.materialItems.findIndex(
      (m) => m.inventoryId === item.inventoryId
    );

    if (index !== -1) {
      // If the item exists, update it in place in the array.
      // We create a new object reference to ensure Angular detects the change if using OnPush
      this.materialItems[index] = { ...item };

      // --- CRUCIAL ADDITION ---
      // Explicitly update the materialItemsMap for the currently selected product
      // by assigning the current `this.materialItems` array.
      // This ensures that the map always holds the latest state of the array.
      if (this.selectedProductId != null) {
        this.materialItemsMap[this.selectedProductId] = this.materialItems;
      }
    } else {
      // This case should ideally not happen for saving existing items.
      // If it's a new item being added and then saved, you would push it to the array here:
      // this.materialItems.push({ ...item });
      // And then update the map:
      // if (this.selectedProductId != null) {
      //   this.materialItemsMap[this.selectedProductId] = this.materialItems;
      // }
      console.warn(
        `[RawMaterialsComponent] Saved item with inventoryId ${item.inventoryId} not found in materialItems array.`
      );
    }

    // After the in-place update, we need to trigger change detection for the parent/overall costs.
    this.emitCostDataChange(); // This emits the updated current state

    // ----------------------------------------------------
    // The following section related to allProductCosts and CombinedCostItem
    // This part remains largely the same as it correctly reads from the now-updated `materialItems`.

    const pname = this.selectedProduct;
    const pid = this.selectedProductId;

    if (!pname || pid == null) {
      console.warn(
        '[RawMaterialsComponent] No product selected; cannot update overall costs.'
      );
      return;
    }

    // Recalculate combined costs against the now updated 'materialItems'
    const updatedList: CombinedCostItem[] = this.getCombinedCosts(); // This should read from this.materialItems

    // Update the overall product costs map.
    // Assuming allProductCosts is also a map where values are arrays of CombinedCostItem.
    this.allProductCosts[pname] = updatedList;

    // This spread operator for allProductCosts ensures OnPush components using it detect a change
    this.allProductCosts = { ...this.allProductCosts };

    // Emit the new reference map
    this.allProductCostsLoaded.emit(this.allProductCosts);
  }

  sortColumn: keyof RawMaterial = 'itemName';
  sortDirection: 'asc' | 'desc' = 'asc';

  /** When the user blurs the name input, we save it */
  saveNewMaterial(item: RawMaterial): void {
    if (item.itemName.trim().length) {
      item.isNew = false;
      // If you want to assign a real ID, do it here.
      this.emitCostDataChange();
    }
  }

  onIndirectChange(item: IndirectLabourItem) {
    this.calculateRateAndUnitCost(item);
  }

  private calculateRateAndUnitCost(item: IndirectLabourItem) {
    // Rate / min = monthlySalary ÷ (hoursWorkedPm × 60)
    const hrs = Math.max(0, item.hoursWorkedPm ?? 0);
    const salary = Math.max(0, item.monthlySalary ?? 0);
    const batchSize = Math.max(1, item.batchSize ?? 1);
    if (item.batchSize <= 0) {
      item.unitCost = 0;
      return;
    }
    // ratePerMinute
    if (hrs > 0) {
      item.ratePerMinute = +(salary / (hrs * 60)).toFixed(4);
    } else {
      item.ratePerMinute = 0;
    }
    // unitCost
    const minutes = item.minutesPerActivity ?? 0;
    item.unitCost = +((item.ratePerMinute! * minutes) / batchSize).toFixed(2);

    // Propagate among indirect rows same employeeID
    const empId = item.employeeID;
    if (empId != null) {
      this.filteredIndirectLabourItems.forEach((other) => {
        if (
          other !== item &&
          this.isIndirectLabourItem(other) &&
          other.employeeID === empId
        ) {
          other.monthlySalary = salary;
          other.hoursWorkedPm = hrs;
          other.ratePerMinute = item.ratePerMinute;
          // recalc unitCost
          const m2 = other.minutesPerActivity ?? 0;
          other.unitCost = +(
            (other.ratePerMinute! * m2) /
            Math.max(1, other.batchSize ?? 1)
          ).toFixed(2);
          // propagate roleDescription if desired
          if (item.roleDescription !== undefined) {
            other.roleDescription = item.roleDescription;
          }
          if (item.rateType !== undefined) {
            other.rateType = item.rateType;
          }
        }
      });
    }

    this.emitCostDataChange();
  }
  // Remove your old syncIndirect; replace with this:

  // get fixedData(): FixedItem[] {
  //   return this.currentCategory === 'fixed' ? this.fixedItems : [];
  // }
  // get selectedProductName(): string {
  //   const p = this.products.find(
  //     (x) => x.inventoryId === this.selectedProductId
  //   );
  //   return p ? p.itemName : 'Select product';
  // }
  cancelEditCompetitor(): void {
    this.editingCompetitorIndex = null;
    this.competitorForm.reset();
  }

  saveCompetitorEdit(): void {
    if (!this.competitorForm.valid || this.editingCompetitorIndex === null)
      return;
    if (this.selectedProductId == null || this.editingCompetitorIndex == null)
      return;
    const updated = this.competitorForm.value;
    this.competitorPrices[this.selectedProductId][this.editingCompetitorIndex] =
      updated;
    this.cancelEditCompetitor();
  }

  private sortItems(items: VariableOverhead[]): VariableOverhead[] {
    const sorted = items.slice();
    switch (this.sortBy) {
      case 'cost-asc':
        sorted.sort((a, b) => a.monthlyCost - b.monthlyCost);
        break;
      case 'cost-desc':
        sorted.sort((a, b) => b.monthlyCost - a.monthlyCost);
        break;
      case 'usage-asc':
        sorted.sort((a, b) => a.estimatedSales - b.estimatedSales);
        break;
      case 'usage-desc':
        sorted.sort((a, b) => b.estimatedSales - a.estimatedSales);
        break;
      case 'availability-asc':
        sorted.sort((a, b) => a.itemName.localeCompare(b.itemName));
        break;
      case 'availability-desc':
        sorted.sort((a, b) => b.itemName.localeCompare(a.itemName));
        break;
      default:
        break;
    }
    return sorted;
  }
  // GET FIXED OH TOT
  get totalFixedOverheads(): number {
    return this.fixedItems.reduce((sum, item) => sum + item.unitCost, 0);
  }

  // Getters for filtered (and sorted) data based on searchTerm and sortBy.
  // 1) Add this helper alongside your existing sortItems:
  private sortRawMaterials(items: RawMaterial[]): RawMaterial[] {
    const sorted = items.slice();
    switch (this.sortBy) {
      case 'cost-asc':
        sorted.sort((a, b) => a.unitCost - b.unitCost);
        break;
      case 'cost-desc':
        sorted.sort((a, b) => b.unitCost - a.unitCost);
        break;
      case 'usage-asc':
        sorted.sort((a, b) => a.usageQuantity - b.usageQuantity);
        break;
      case 'usage-desc':
        sorted.sort((a, b) => b.usageQuantity - a.usageQuantity);
        break;
      case 'availability-asc':
        sorted.sort((a, b) => a.itemName.localeCompare(b.itemName));
        break;
      case 'availability-desc':
        sorted.sort((a, b) => b.itemName.localeCompare(a.itemName));
        break;
      default:
        break;
    }
    return sorted;
  } // In your component class, update the method signature so it accepts all three types:
  // after
  startEditLabour(index: number): void {
    const item = this.labourData[index];
    item.isEditing = true;
  }

  /** save both new and edits */
  editItem(
    item: RawMaterial | FixedItem | VariableOverhead,
    category: Category,
    index?: number
  ): void {
    this.isNewRecord = false;
    this.currentCategory = category;

    if (category === 'materials') {
      // operate on materialItems
      this.selectedItem = null; // if you're re-using selectedItem
      this.selectedFixed = null;
      // stash the original values if you want “Cancel” support:
      this.originalItem = { ...item } as any;
      // pre-fill your editForm with the MaterialItem fields:
      this.editForm.patchValue({
        itemName: (item as RawMaterial).itemName,
        purchaseQuantity: (item as RawMaterial).purchaseQuantity,
        purchasePrice: (item as RawMaterial).purchasePrice,
        usageQuantity: (item as RawMaterial).usageQuantity,
      });
    } else if (category === 'fixed') {
      // your existing FixedItem logic…
    }
    // …etc.

    this.showEditModal = true;
  }
  startEditIndirectLabour(index: number): void {
    const item = this.indirectLabourData[index];
    item.isEditing = true;
  }
  get filteredLabourItems(): LabourItem[] {
    return this.labourItems.filter((item) =>
      item.roleDescription.toLowerCase().includes(this.searchTerm)
    );
  }
  saveLabourRow(item: LabourItem): void {
    if (!item.roleDescription.trim()) {
      return;
    }

    this.onLabourChange(item);

    item.isNew = false;
    item.isEditing = false;

    const index = this.labourItems.findIndex(
      (m) => m.itemOperationID === item.itemOperationID
    );
    if (index !== -1) {
      this.labourItems[index] = { ...item }; // This line is now correct and necessary

      if (this.selectedProductId != null) {
        // Update the map with a fresh copy of the *current* labourItems array
        this.directLaborItemsMap[this.selectedProductId] = [
          ...this.labourItems,
        ];
      }
    }

    // Rest of your code for emitting events
    this.emitCostDataChange();

    const pname = this.selectedProduct;
    const pid = this.selectedProductId;

    if (!pname || pid == null) {
      console.warn(
        '[RawMaterialsComponent] No product selected; cannot update.'
      );
      return;
    }

    const updatedList: CombinedCostItem[] = this.getCombinedCosts();
    this.allProductCosts[pname] = updatedList;
    this.allProductCosts = { ...this.allProductCosts };
    this.allProductCostsLoaded.emit(this.allProductCosts);
  }
  /** Save (for both new and edits) */
  saveIndirectRow(item: IndirectLabourItem): void {
    if (!item.roleDescription.trim()) {
      return; // optionally, show validation
    }
    item.isNew = false;
    item.isEditing = false;
    const index = this.indirectLabourItems.findIndex(
      (m) => m.itemOperationID === item.itemOperationID
    );
    if (index !== -1) {
      this.indirectLabourItems[index] = { ...item };

      if (this.selectedProductId != null) {
        this.indirectLaborItemsMap[this.selectedProductId] =
          this.indirectLabourItems;
      }
    }
    // Recalculate ratePerMinute and unitCost, if needed:
    this.onIndirectChange(item);
    this.emitCostDataChange();
    const pname = this.selectedProduct; // e.g. "Classic Beef Burger & Chips"
    const pid = this.selectedProductId; // e.g. 13

    if (!pname || pid == null) {
      console.warn(
        '[RawMaterialsComponent] No product selected; cannot update.'
      );
      return;
    }

    // getCombinedCosts will see the brand-new item in this.rawMaterials
    const updatedList: CombinedCostItem[] = this.getCombinedCosts();

    // 3) Overwrite that product’s entry in the in-memory map
    this.allProductCosts[pname] = updatedList;

    // 4) Reassign a fresh object so OnPush components detect the change
    this.allProductCosts = { ...this.allProductCosts };

    // 5) Emit the new-reference map
    this.allProductCostsLoaded.emit(this.allProductCosts);
  }

  // 2) Update your getter to use it:
  get rawMaterials(): RawMaterial[] {
    const materialItemsArray = this.materialItems ?? [];

    const items = materialItemsArray
      .slice()
      .filter(
        (item) =>
          !this.searchTerm ||
          item.itemName.toLowerCase().includes(this.searchTerm)
      );

    return this.sortRawMaterials(items);
  }

  // 3) Expose labourItems when the Labour tab is active
  // Safely get labour data, defaulting to an empty array.
  get labourData(): LabourItem[] {
    const labourItemsArray = this.labourItems ?? [];

    return this.currentCategory === 'labour' ? labourItemsArray : [];
  }

  // Safely get fixed data, defaulting to an empty array.
  get fixedData(): FixedItem[] {
    const fixedItemsArray = this.fixedItems ?? [];

    return this.currentCategory === 'fixed' ? fixedItemsArray : [];
  }

  // after
  get variableData(): VariableOverhead[] {
    // Use a nullish coalescing operator (??) to provide a default empty array.
    // This ensures that `variableFull` is always an array, even if it's null or undefined.
    const variableFullArray = this.variableFull ?? [];

    let items: VariableOverhead[] =
      this.currentCategory !== 'variable' || !this.searchTerm
        ? variableFullArray.slice()
        : variableFullArray.filter((item) =>
            item.itemName.toLowerCase().includes(this.searchTerm)
          );

    return this.currentCategory === 'variable' ? this.sortItems(items) : items;
  }
  private async deleteRawMaterialFromBackend(
    bomComponentID: number
  ): Promise<void> {
    if (!bomComponentID) return;

    const token = localStorage.getItem('authToken') || '';
    const headers = new HttpHeaders({ Authorization: `Bearer ${token}` });
    const url = `${environment.apiUrl}/api/BOMComponents/Delete?BOMComponentsId=${bomComponentID}`;

    try {
      await this.http.delete<void>(url, { headers }).toPromise();
      // $&
    } catch (error) {
      console.error(
        `Failed to delete RawMaterial ID ${bomComponentID}:`,
        error
      );
      throw error;
    }
  }
  async deleteItem(
    item: RawMaterial | FixedItem | VariableOverhead,
    category: Category,
    index?: number
  ): Promise<void> {
    this.showDeleteConfirm = true;
    const confirmed = await this.deleteConfirmed;
    if (!confirmed) {
      this.deleteConfirmed = new Promise((resolve) => {
        this.resolveDelete = resolve;
      });
      return;
    }
    this.deleteConfirmed = new Promise((resolve) => {
      this.resolveDelete = resolve;
    });

    if (category === 'materials' && index != null) {
      const rawMaterial = item as RawMaterial;

      // 🔁 Reusable backend delete
      if (rawMaterial.bomComponentID) {
        try {
          await this.deleteRawMaterialFromBackend(rawMaterial.bomComponentID);
        } catch (err) {
          // Optional: show toast or message if it fails
        }
      }

      this.materialItems.splice(index, 1);
      this.emitCostDataChange();

      const pname = this.selectedProduct;
      const pid = this.selectedProductId;
      if (!pname || pid == null) {
        console.warn(
          '[RawMaterialsComponent] No product selected; cannot update.'
        );
        return;
      }

      const updatedList: CombinedCostItem[] = this.getCombinedCosts();
      this.allProductCosts[pname] = updatedList;
      this.allProductCostsLoaded.emit(this.allProductCosts);
    }

    // Other category logic unchanged...
    else if (category === 'labour' && index != null) {
      this.labourItems.splice(index, 1);
    } else if (category === 'indirect-labour' && index != null) {
      this.indirectLabourItems.splice(index, 1);
    } else if (category === 'fixed' && index != null) {
      this.fixedItems.splice(index, 1);
      this.emitCostDataChange();

      const pname = this.selectedProduct;
      const pid = this.selectedProductId;
      if (!pname || pid == null) {
        console.warn(
          '[RawMaterialsComponent] No product selected; cannot update.'
        );
        return;
      }

      const updatedList: CombinedCostItem[] = this.getCombinedCosts();
      this.allProductCosts[pname] = updatedList;
      this.allProductCostsLoaded.emit(this.allProductCosts);
    } else if (category === 'variable' && index != null) {
      this.variableFull.splice(index, 1);
    } else if (category === 'competitor' && index != null) {
      if (this.selectedProductId == null) return;
      this.competitorPrices[this.selectedProductId].splice(index, 1);
    }

    this.emitCostDataChange();
  }

  // delete labor item
  private async deleteLabourFromBackend(
    itemOperationID: number
  ): Promise<void> {
    if (!itemOperationID) return;

    const token = localStorage.getItem('authToken') || '';
    const headers = new HttpHeaders({ Authorization: `Bearer ${token}` });
    const url = `${environment.apiUrl}/api/ItemOperation/${itemOperationID}`;

    try {
      await this.http.delete<void>(url, { headers }).toPromise();
    } catch (error) {
      console.error(
        `Failed to delete Labour item with ID ${itemOperationID}:`,
        error
      );
      throw error;
    }
  }
  /** Remove a labour entry by index */
  async removeLabour(index: number): Promise<void> {
    this.showDeleteConfirm = true;
    const confirmed = await this.deleteConfirmed;
    if (!confirmed) {
      this.deleteConfirmed = new Promise((resolve) => {
        this.resolveDelete = resolve;
      });
      return;
    }
    this.deleteConfirmed = new Promise((resolve) => {
      this.resolveDelete = resolve;
    });

    const labourItem = this.labourItems[index];

    // 🔥 Call backend deletion
    if (labourItem?.itemOperationID) {
      try {
        await this.deleteLabourFromBackend(labourItem.itemOperationID);
      } catch (err) {
        // You can optionally show a toast here
      }
    }

    // Remove from UI
    this.labourItems.splice(index, 1);
    this.emitCostDataChange();

    const pname = this.selectedProduct;
    const pid = this.selectedProductId;

    if (!pname || pid == null) {
      console.warn(
        '[RawMaterialsComponent] No product selected; cannot update.'
      );
      return;
    }

    const updatedList: CombinedCostItem[] = this.getCombinedCosts();
    this.allProductCosts[pname] = updatedList;
    this.allProductCosts = { ...this.allProductCosts };
    this.allProductCostsLoaded.emit(this.allProductCosts);
  }

  async removeIndirectLabour(index: number): Promise<void> {
    this.showDeleteConfirm = true;
    const confirmed = await this.deleteConfirmed;
    if (!confirmed) {
      this.deleteConfirmed = new Promise((resolve) => {
        this.resolveDelete = resolve;
      });
      return;
    }
    this.deleteConfirmed = new Promise((resolve) => {
      this.resolveDelete = resolve;
    });

    const labourItem = this.indirectLabourData[index];

    // 🔥 Call backend deletion
    if (labourItem?.itemOperationID) {
      try {
        await this.deleteLabourFromBackend(labourItem.itemOperationID);
      } catch (err) {
        // Optional: show toast or handle fallback
      }
    }

    // Remove from UI
    this.indirectLabourData.splice(index, 1);

    const pname = this.selectedProduct;
    const pid = this.selectedProductId;

    if (!pname || pid == null) {
      console.warn(
        '[RawMaterialsComponent] No product selected; cannot update.'
      );
      return;
    }

    const updatedList: CombinedCostItem[] = this.getCombinedCosts();
    this.allProductCosts[pname] = updatedList;
    this.allProductCosts = { ...this.allProductCosts };
    this.allProductCostsLoaded.emit(this.allProductCosts);
  }
  private async deleteFromBackend(
    resourcePath: string,
    idName: string,
    id: number,
    useQueryParam = true
  ): Promise<void> {
    if (!id) return;

    const token = localStorage.getItem('authToken') || '';
    const headers = new HttpHeaders({ Authorization: `Bearer ${token}` });

    // Choose URL format based on useQueryParam flag:
    const url = useQueryParam
      ? `${environment.apiUrl}/api/${resourcePath}/Delete?${idName}=${id}`
      : `${environment.apiUrl}/api/${resourcePath}/${id}`;

    try {
      await this.http.delete<void>(url, { headers }).toPromise();
      // $&
    } catch (error) {
      console.error(
        `Failed to delete from ${resourcePath} where ${idName}=${id}:`,
        error
      );
      throw error;
    }
  }
  async removeFixedOverhead(index: number): Promise<void> {
    this.showDeleteConfirm = true;
    const confirmed = await this.deleteConfirmed;
    if (!confirmed) {
      this.deleteConfirmed = new Promise((resolve) => {
        this.resolveDelete = resolve;
      });
      return;
    }
    this.deleteConfirmed = new Promise((resolve) => {
      this.resolveDelete = resolve;
    });

    const item = this.fixedItems[index];
    await this.deleteFromBackend(
      'OverheadCostPool',
      'OverheadCostPoolId',
      item.overheadCostPoolID
    );

    this.fixedItems.splice(index, 1);
    this.fixedItems = [...this.fixedItems];

    const pname = this.selectedProduct;
    const pid = this.selectedProductId;

    if (!pname || pid == null) {
      console.warn(
        '[RawMaterialsComponent] No product selected; cannot update.'
      );
      return;
    }

    const updatedList: CombinedCostItem[] = this.getCombinedCosts();
    this.allProductCosts[pname] = updatedList;
    this.allProductCosts = { ...this.allProductCosts };

    this.emitCostDataChange();
    this.allProductCostsLoaded.emit(this.allProductCosts);
  }
  async removeVariableOH(index: number): Promise<void> {
    this.showDeleteConfirm = true;
    const confirmed = await this.deleteConfirmed;
    if (!confirmed) {
      this.deleteConfirmed = new Promise((resolve) => {
        this.resolveDelete = resolve;
      });
      return;
    }
    this.deleteConfirmed = new Promise((resolve) => {
      this.resolveDelete = resolve;
    });

    const itemToRemove = this.variableFull[index];

    if (itemToRemove && itemToRemove.bovoComponentID) {
      // Delete from backend first (useQueryParam = false)
      try {
        await this.deleteFromBackend(
          'BOVOComponent',
          'BOVOComponentId',
          itemToRemove.bovoComponentID,
          false
        );
        this.deletedBovoComponentIDs.push(itemToRemove.bovoComponentID);
      } catch (error) {
        // $&
        return; // stop further processing if delete fails
      }
    } else {
      // $&
    }

    // Remove from local arrays
    this.variableFull.splice(index, 1);

    const filteredIndex = this.filteredVariableItems.findIndex(
      (item) => item.inventoryId === itemToRemove.inventoryId
    );
    if (filteredIndex !== -1) {
      this.filteredVariableItems.splice(filteredIndex, 1);
    }

    this.emitCostDataChange();

    const pname = this.selectedProduct;
    const pid = this.selectedProductId;

    if (!pname || pid == null) {
      console.warn(
        '[RawMaterialsComponent] No product selected; cannot update costs after removal.'
      );
      return;
    }

    const updatedList: CombinedCostItem[] = this.getCombinedCosts();
    this.allProductCosts[pname] = updatedList;
    this.allProductCosts = { ...this.allProductCosts };
    this.allProductCostsLoaded.emit(this.allProductCosts);
  }
  async removeCompetitor(idx: number): Promise<void> {
    this.showDeleteConfirm = true;
    const confirmed = await this.deleteConfirmed;

    if (!confirmed) {
      this.deleteConfirmed = new Promise((resolve) => {
        this.resolveDelete = resolve;
      });
      return;
    }

    // Reset confirmation for next use
    this.deleteConfirmed = new Promise((resolve) => {
      this.resolveDelete = resolve;
    });

    if (this.selectedProductId == null) return;

    const competitor = this.competitorPrices[this.selectedProductId][idx];
    if (!competitor) {
      // $&
      return;
    }

    // CASE 1: If the entry is new (not saved to backend), just remove it locally
    if (!competitor.itemCompetitorID) {
      this.removeCompetitorLocally(this.selectedProductId, idx, competitor);
      return;
    }

    // CASE 2: Entry has been saved; delete from backend
    try {
      await this.deleteFromBackend(
        'ItemCompetitor',
        'ItemCompetitorId',
        competitor.itemCompetitorID,
        false
      );
    } catch (error) {
      // Do not remove it from the UI: the row still exists server-side and
      // would reappear on the next reload, looking like lost data.
      console.error('Failed to delete competitor', error);
      this.showCompetitorSaveError(
        `Could not delete "${competitor.name || 'this competitor'}". Please try again.`
      );
      return;
    }

    // Remove locally after successful backend delete
    this.removeCompetitorLocally(this.selectedProductId, idx, competitor);
    this.competitorsCache.delete(this.selectedProductId);
  }

  /**
   * Drop a competitor from every local collection.
   * Removing it from competitorPrices alone leaves competitorPricesMap holding
   * the row, which the save diff then treats as still present.
   */
  private removeCompetitorLocally(
    productId: number,
    idx: number,
    competitor: Competitor
  ): void {
    this.competitorPrices[productId]?.splice(idx, 1);

    const matches = (c: any) =>
      (competitor.itemCompetitorID && c.itemCompetitorID === competitor.itemCompetitorID) ||
      (competitor.competitorID && c.competitorID === competitor.competitorID) ||
      c === competitor;

    const mapList = this.competitorPricesMap[productId];
    if (mapList) {
      const mapIdx = mapList.findIndex(matches);
      if (mapIdx > -1) mapList.splice(mapIdx, 1);
    }

    const originalList = this.originalCompetitorPricesMap[productId];
    if (originalList) {
      const origIdx = originalList.findIndex(matches);
      if (origIdx > -1) originalList.splice(origIdx, 1);
    }

    this.competitors = [...(this.competitorPricesMap[productId] || [])];
  }

  ngOnInit(): void {
    this.isLoadingData = true;
    this.accountType = this.authService.getAccountType();
    this.autoSaveService.registerComponent(this);

    this.initializeHoursOpenPerMonth()
      .pipe(
        switchMap(() => {
          const itemType = 2; // Replace with the correct itemType value
          const token = localStorage.getItem('authToken') || '';
          const headers = new HttpHeaders({ Authorization: `Bearer ${token}` });

          // Load employees in parallel with product
          const employees$ = this.http
            .get<Employee[]>(`${environment.apiUrl}/api/Employees/GetAll`, {
              headers,
            })
            .pipe(tap((list) => (this.employees = list)));

          // 👉 Check for previously selected product
          const lastSelected = localStorage.getItem('lastSelectedProduct');

          // If found, load that product, else fallback to fetchFirstProduct
          const product$ = this.enableAutoSelectOnInit
            ? lastSelected
              ? this.http
                  .get<ItemDTO>(
                    `${environment.apiUrl}/api/Items/GetById?ItemsId=${lastSelected}`,
                    { headers }
                  )
                  .pipe(
                    tap((product) => {
                      if (product) {
                        this.selectedProductId = product.itemID;
                        this.markupPercent = product.profitMarginPercent;
                        this.displayValue = (
                          product.profitMarginPercent || 0
                        ).toString();
                        this.selectedProduct = product.itemName;
                        this.productNameChange.emit(this.selectedProduct);

                        this.selectedProductIdForFirstLoad = product.itemID;
                        this.allProductCosts ??= {};
                        this.allProductCosts[product.itemID.toString()] = [];
                        this.allProductCostsLoaded.emit(this.allProductCosts);

                        // Load persisted AI suggestion for initial product
                        this.loadAiSuggestionForProduct(product.itemID);
                      }
                    }),
                    switchMap((product) =>
                      this.loadFirstProductDetailsAsync(product.itemID)
                    ),
                    catchError((err) => {
                      console.error(
                        'Failed to load last selected product, falling back:',
                        err
                      );
                      // Fallback: load first product if lastSelected fails
                      return this.fetchFirstProduct(itemType).pipe(
                        tap((product) => {
                          this.selectedProductId = product.itemID;
                          this.markupPercent = product.profitMarginPercent;
                          this.displayValue = (
                            product.profitMarginPercent || 0
                          ).toString();
                          this.selectedProduct = product.itemName;
                          this.productNameChange.emit(this.selectedProduct);

                          this.selectedProductIdForFirstLoad = product.itemID;
                          this.allProductCosts ??= {};
                          this.allProductCosts[product.itemID.toString()] = [];
                          this.allProductCostsLoaded.emit(this.allProductCosts);

                          // Load persisted AI suggestion for initial product
                          this.loadAiSuggestionForProduct(product.itemID);
                        }),
                        switchMap((product) =>
                          this.loadFirstProductDetailsAsync(product.itemID)
                        )
                      );
                    })
                  )
              : this.fetchFirstProduct(itemType).pipe(
                  tap((product) => {
                    this.selectedProductId = product.itemID;
                    this.selectedProductIdForFirstLoad = product.itemID;
                    this.allProductCosts ??= {};
                    this.allProductCosts[product.itemID.toString()] = [];
                    this.allProductCostsLoaded.emit(this.allProductCosts);

                    // Load persisted AI suggestion for initial product
                    this.loadAiSuggestionForProduct(product.itemID);
                  }),
                  switchMap((product) =>
                    this.loadFirstProductDetailsAsync(product.itemID)
                  )
                )
            : of(null);

          // Combine employee load + product load
          return forkJoin({
            employees: employees$,
            product: product$,
          });
        }),
        // Fetch all product costs in background
        switchMap(() => {
          const itemType = 2;
          return this.fetchAllProductCostsIsolated(itemType).pipe(
            tap((allProductCosts: Record<string, CombinedCostItem[]>) => {
              this.allProductCosts = {
                ...this.allProductCosts,
                ...allProductCosts,
              };
              this.allProductCostsLoaded.emit(this.allProductCosts);
              this.allProductCostsCompleted = true;
            }),
            catchError((err) => {
              console.error(
                'Failed to fetch all product costs in background:',
                err
              );
              this.allProductCostsCompleted = true;
              return of({} as Record<string, CombinedCostItem[]>);
            })
          );
        })
      )
      .subscribe({
        next: () => {
          this.isLoadingData = false;
        },
        error: (err) => {
          this.isLoadingData = false;
        },
      });

    // Parallel initializations
    this.loadUnitOfMeasures();
    this.loadFixedOverheadNames();
    this.loadVariableOverheads();
    this.initForms();
    this.initializeForm();
    this.loadProductsOnly();
  }

  ngOnDestroy() {
    // Unregister this component
    this.autoSaveService.unregisterComponent(this);

    // ... your existing ngOnDestroy code
  }
  fetchProductData(productId: number): void {
    const token = localStorage.getItem('authToken') || '';
    const headers = new HttpHeaders({ Authorization: `Bearer ${token}` });

    // Fetch Item details (for profitMarginPercent)
    this.http
      .get<ItemDTO>(
        `${environment.apiUrl}/api/Items/GetById?ItemsId=${productId}`,
        { headers }
      )
      .subscribe({
        next: (item) => {
          // Store the original profit margin percent
          this.originalProductValues.set(productId, {
            ...this.originalProductValues.get(productId), // Preserve existing targetCost if already set
            profitMarginPercent: item.profitMarginPercent,
          });
        },
        error: (err) =>
          console.error('Error fetching item for original profit margin:', err),
      });

    // Fetch ItemTargetHistory (for targetCost and targetProfitMarginPercent)
    this.http
      .get<ItemTargetHistoryDTO[]>(
        `${environment.apiUrl}/api/ItemTargetHistory/GetByItemId?itemId=${productId}`,
        { headers }
      )
      .subscribe({
        next: (allHistories) => {
          const filtered = allHistories.filter((h) => h.itemID === productId);
          const latest =
            filtered.sort(
              (a, b) =>
                new Date(b.validFrom).getTime() -
                new Date(a.validFrom).getTime()
            )[0] || null;

          if (latest) {
            // Assuming this is where you set this.targetTotalCost
            // this.targetTotalCost = latest.targetCost;

            // Store the original target cost and profit margin from history
            this.originalProductValues.set(productId, {
              ...this.originalProductValues.get(productId), // Preserve existing profitMarginPercent if already set
              targetCost: latest.targetCost,
              profitMarginPercent: latest.targetProfitMarginPercent, // Also store this for comparison
            });
          } else {
            // If no history, ensure it's recorded as null/undefined
            this.originalProductValues.set(productId, {
              ...this.originalProductValues.get(productId),
              targetCost: null,
              profitMarginPercent: null,
            });
          }
        },
        error: (err) =>
          console.error(
            'Error fetching target history for original cost:',
            err
          ),
      });
  }
  private fetchFirstProduct(itemType: number): Observable<any> {
    const token = localStorage.getItem('authToken') || '';
    const headers = new HttpHeaders({ Authorization: `Bearer ${token}` });

    return this.http.get<any>(
      `${environment.apiUrl}/api/Items/GetFirstByType/${itemType}`,
      {
        headers: headers,
      }
    );
  }
  private loadFirstProductDetailsAsync(productId: number): Observable<any> {
    this.clearCurrentUIData();

    const rawMaterials$ = this.fetchRawMaterials(productId).pipe(
      tap((materials) => {
        this.materialItems = Array.isArray(materials) ? materials : [];
        // Add a check to ensure `this.rawMaterials` exists and is not empty.
        if (Array.isArray(this.rawMaterials) && this.rawMaterials.length > 0) {
          this.rawMaterials.forEach((mat) => this.recalculateMaterial(mat));
        }
      }),
      catchError((error) => {
        // $&
        this.materialItems = [];
        return of([]);
      })
    );

    const variableOverheads$ = this.fetchVariableOverheads(productId).pipe(
      tap((variableOverheads: VariableOverhead[]) => {
        // Ensure we have an array
        const overheadsArray = Array.isArray(variableOverheads)
          ? variableOverheads
          : [];
        this.variableFull = overheadsArray;

        if (this.selectedProductId == null) return;

        this.variableOverheadsMap[this.selectedProductId] = JSON.parse(
          JSON.stringify(overheadsArray)
        );

        if (
          !this.originalVariableOverheadsMap[this.selectedProductId] ||
          this.originalVariableOverheadsMap[this.selectedProductId].length === 0
        ) {
          this.originalVariableOverheadsMap[this.selectedProductId] =
            JSON.parse(JSON.stringify(overheadsArray));
        }

        // Add a check to ensure `this.variableFull` exists and is not empty.
        if (Array.isArray(this.variableFull) && this.variableFull.length > 0) {
          this.variableFull.forEach((voh) =>
            this.recalculateVariableOverhead(voh)
          );
        }
      }),
      catchError((error) => {
        // $&
        this.variableFull = [];

        if (this.selectedProductId != null) {
          this.variableOverheadsMap[this.selectedProductId] = [];
          this.originalVariableOverheadsMap[this.selectedProductId] = [];
        }
        return of([]);
      })
    );

    const fixedOverheads$ = this.fetchFixedOverheads(productId).pipe(
      tap((fixedOverheads: FixedItem[]) => {
        const overheadsArray = Array.isArray(fixedOverheads)
          ? fixedOverheads
          : [];
        this.fixedItems = overheadsArray;

        if (this.selectedProductId == null) return;

        this.fixedOverheadsMap[this.selectedProductId] = JSON.parse(
          JSON.stringify(overheadsArray)
        );

        if (
          !this.originalFixedOverheadsMap[this.selectedProductId] ||
          this.originalFixedOverheadsMap[this.selectedProductId].length === 0
        ) {
          this.originalFixedOverheadsMap[this.selectedProductId] = JSON.parse(
            JSON.stringify(overheadsArray)
          );
        }

        // Add a check to ensure `this.fixedItems` exists and is not empty.
        if (Array.isArray(this.fixedItems) && this.fixedItems.length > 0) {
          this.fixedItems.forEach((item) => this.recalculateFixed(item));
        }
      }),
      catchError((err) => {
        // $&
        this.fixedItems = [];

        if (this.selectedProductId != null) {
          this.fixedOverheadsMap[this.selectedProductId] = [];
          this.originalFixedOverheadsMap[this.selectedProductId] = [];
        }
        return of([]);
      })
    );

    const directLabour$ = this.fetchDirectLabourItems(productId).pipe(
      tap((rawLabourItems: LabourItem[]) => {
        const labourArray = this.withSeededServiceLabour(
          productId,
          Array.isArray(rawLabourItems) ? rawLabourItems : []
        );
        this.labourItems = labourArray;

        if (this.selectedProductId == null) return;

        this.directLaborItemsMap[this.selectedProductId] = JSON.parse(
          JSON.stringify(labourArray)
        );

        if (
          !this.originalDirectLaborItemsMap[this.selectedProductId] ||
          this.originalDirectLaborItemsMap[this.selectedProductId].length === 0
        ) {
          this.originalDirectLaborItemsMap[this.selectedProductId] = JSON.parse(
            JSON.stringify(labourArray)
          );
        }

        // Add a check to ensure `this.labourItems` exists and is not empty.
        if (Array.isArray(this.labourItems) && this.labourItems.length > 0) {
          this.labourItems.forEach((dl) => this.onLabourChange(dl));
        }
      }),
      catchError((err) => {
        // $&
        this.labourItems = [];

        if (this.selectedProductId != null) {
          this.directLaborItemsMap[this.selectedProductId] = [];
          this.originalDirectLaborItemsMap[this.selectedProductId] = [];
        }
        return of([]);
      })
    );

    const indirectLabour$ = this.fetchIndirectLabourItems(productId).pipe(
      tap((indirectLabourItems) => {
        const indirectArray = Array.isArray(indirectLabourItems)
          ? indirectLabourItems
          : [];

        if (this.selectedProductId == null) return;

        this.indirectLabourItems = indirectArray;
        this.indirectLaborItemsMap[this.selectedProductId] = JSON.parse(
          JSON.stringify(indirectArray)
        );

        if (
          !this.originalinDirectLaborItemsMap[this.selectedProductId] ||
          this.originalinDirectLaborItemsMap[this.selectedProductId].length ===
            0
        ) {
          this.originalinDirectLaborItemsMap[this.selectedProductId] =
            JSON.parse(JSON.stringify(indirectArray));
        }

        // Add a check to ensure `this.indirectLabourItems` exists and is not empty.
        if (
          Array.isArray(this.indirectLabourItems) &&
          this.indirectLabourItems.length > 0
        ) {
          this.indirectLabourItems.forEach((dl) => this.onIndirectChange(dl));
        }
      }),
      catchError((err) => {
        // $&
        this.indirectLabourItems = [];

        if (this.selectedProductId != null) {
          this.indirectLaborItemsMap[this.selectedProductId] = [];
          this.originalinDirectLaborItemsMap[this.selectedProductId] = [];
        }
        return of([]);
      })
    );

    const competitors$ = this.fetchCompetitors(productId).pipe(
      tap((competitors: Competitor[]) => {
        const competitorsArray = Array.isArray(competitors) ? competitors : [];
        this.competitors = competitorsArray;

        if (this.selectedProductId == null) return;

        this.competitorPrices[this.selectedProductId] = JSON.parse(
          JSON.stringify(competitorsArray)
        );

        this.competitorPricesMap[this.selectedProductId] = JSON.parse(
          JSON.stringify(competitorsArray)
        );

        if (
          !this.originalCompetitorPricesMap[this.selectedProductId] ||
          this.originalCompetitorPricesMap[this.selectedProductId].length === 0
        ) {
          this.originalCompetitorPricesMap[this.selectedProductId] = JSON.parse(
            JSON.stringify(competitorsArray)
          );
        }
      }),
      catchError((err) => {
        // $&
        this.competitors = [];

        if (this.selectedProductId != null) {
          this.competitorPrices[this.selectedProductId] = [];
          this.competitorPricesMap[this.selectedProductId] = [];
          this.originalCompetitorPricesMap[this.selectedProductId] = [];
        }
        return of([]);
      })
    );

    // Load target history separately since it doesn't return observable
    this.fetchTargetHistory(productId);

    // Combine all the detail fetching operations
    return forkJoin({
      rawMaterials: rawMaterials$,
      variableOverheads: variableOverheads$,
      fixedOverheads: fixedOverheads$,
      directLabour: directLabour$,
      indirectLabour: indirectLabour$,
      competitors: competitors$,
    }).pipe(
      tap(() => {
        // Emit cost data change after all details are loaded
        this.onMarkupPercentChange(true);
        this.emitCostDataChange();

        this.isLoadingData = false;
      }),
      catchError((err) => {
        // $&
        // Still emit cost data change even if some operations failed
        this.emitCostDataChange();
        this.onMarkupPercentChange(true);
        this.isLoadingData = false;

        return of({
          rawMaterials: [],
          variableOverheads: [],
          fixedOverheads: [],
          directLabour: [],
          indirectLabour: [],
          competitors: [],
        });
      })
    );
  }
  private loadFirstProductDetails(productId: number): void {
    this.loadFirstProductDetailsAsync(productId).subscribe(
      () => {
        // $&
      },
      (err) => {
        // $&
      }
    );
  }
  private clearCurrentUIData(): void {
    this.materialItems = [];

    this.variableFull = [];
    this.fixedItems = [];
    this.labourItems = [];
    this.indirectLabourItems = [];
    this.competitors = [];
  }

  // Keep the original loadProducts method for when you need to switch products

  private loadProductsOnly(): void {
    const token = localStorage.getItem('authToken') || '';
    const headers = new HttpHeaders({ Authorization: `Bearer ${token}` });

    // Spec §4: Buy Out raw materials and Service items must be selectable
    // here alongside Finished Goods — /api/Items/Sellable covers all three.
    this.http
      .get<ItemDTO[]>(`${environment.apiUrl}/api/Items/Sellable`, {
        headers,
      })
      .subscribe({
        next: (response) => {
          this.products = response.map((dto) => ({
            itemID: dto.itemID,
            itemName: dto.itemName,
            profitMarginPercent: dto.profitMarginPercent,
            sellingPrice: dto.sellingPrice,
            // Carried through so the dropdown can distinguish a manufactured
            // product from a Service or a Buy Out item (spec §2/§4).
            itemTypeID: (dto as any).itemTypeID,
            isBuyout: (dto as any).isBuyout === true,
          }));

          // A Service stores its delivery time (minutes) in PurchaseQuantity —
          // keep it so the seeded Direct Labour line can carry the real time.
          this.serviceMinutesById.clear();
          response.forEach((dto: any) => {
            if (dto.itemTypeID === 4) {
              this.serviceMinutesById.set(dto.itemID, dto.purchaseQuantity || 0);
            }
          });

          // console.table(this.products, ['itemID', 'itemName']);
          // $&

          // Don't set selected product here anymore - it's handled in the main ngOnInit flow
          // Just emit the product name change if a product is already selected

          // Trigger markup calculation if we have a selected product
        },
        error: (err) => {
          // $&
        },
      });
  }
  private fetchAllProductCostsIsolated(itemType: number): Observable<any> {
    return this.fetchAllProductCosts(itemType, true); // true = isolated mode, don't set UI variables
  }

  // fetch direct and indirect labor
  fetchDirectLabourItems(
    productId: number,
    isIsolated = false
  ): Observable<LabourItem[]> {
    return this.fetchLabourItems(productId, 1, isIsolated) as Observable<
      LabourItem[]
    >;
  }

  fetchIndirectLabourItems(
    productId: number,
    isIsolated = false
  ): Observable<IndirectLabourItem[]> {
    return this.fetchLabourItems(productId, 2, isIsolated) as Observable<
      IndirectLabourItem[]
    >;
  }

  onEmployeeSelect(item: LabourItem | IndirectLabourItem): void {
    const token = localStorage.getItem('authToken') || '';
    const headers = new HttpHeaders({ Authorization: `Bearer ${token}` });

    const empId = item.employeeID;
    if (empId == null) {
      // Clear fields for rows of the matching type with null employeeID
      if (this.isLabourItem(item)) {
        this.propagateEmployeeFields(null, {
          employeeName: '',
          monthlySalary: 0,
          hoursWorkedPm: 0,
          ratePerMinute: 0,
          rateType: '',
          periodDescription: '',
        });
      } else if (this.isIndirectLabourItem(item)) {
        this.propagateIndirectEmployeeFields(null, {
          employeeName: '',
          monthlySalary: 0,
          hoursWorkedPm: 0,
          ratePerMinute: 0,
          rateType: '',
          periodDescription: '',
        });
      }
      this.emitCostDataChange();
      return;
    }

    // Find employee object
    const emp = this.employees.find((e) => e.employeeID === empId);
    const empName = emp ? `${emp.firstName} ${emp.lastName}` : '';

    // 1) Propagate employeeName immediately for correct list
    if (this.isLabourItem(item)) {
      this.propagateEmployeeFields(empId, { employeeName: empName });
    } else if (this.isIndirectLabourItem(item)) {
      this.propagateIndirectEmployeeFields(empId, { employeeName: empName });
    }
    // Do NOT propagate roleDescription yet; wait for Role fetch.

    // 2) Fetch salary records
    this.http
      .get<EmployeeSalaryDTO[]>(
        `${environment.apiUrl}/api/EmployeeSalaries/GetByEmployeeID/${empId}`,
        { headers }
      )
      .subscribe({
        next: (salaries: EmployeeSalaryDTO[]) => {
          const now = new Date();
          let chosen: EmployeeSalaryDTO | undefined = salaries.find((s) => {
            const start = new Date(s.startDate);
            const end = s.endDate ? new Date(s.endDate) : null;
            return start <= now && (!end || now <= end);
          });
          if (!chosen && salaries.length) {
            chosen = salaries.reduce((prev, cur) => {
              const prevDate = new Date(prev.startDate);
              const curDate = new Date(cur.startDate);
              return curDate > prevDate ? cur : prev;
            });
          }

          const monthly = chosen ? chosen.amount : 0;
          const hrs = chosen ? chosen.hoursWorked : 0;
          const rpm = hrs > 0 ? monthly / (hrs * 60) : 0;

          const propagateSalaryFields = (rateTypeDesc: string) => {
            const base = {
              monthlySalary: monthly,
              hoursWorkedPm: hrs,
              ratePerMinute: +rpm.toFixed(4),
              rateType: rateTypeDesc,
              periodDescription: rateTypeDesc,
            };
            if (this.isLabourItem(item)) {
              this.propagateEmployeeFields(empId, base);
            } else {
              this.propagateIndirectEmployeeFields(empId, base);
            }
            this.emitCostDataChange();
          };

          if (chosen) {
            // Fetch periodType description
            this.http
              .get<PeriodTypeDTO>(
                `${environment.apiUrl}/api/PeriodTypes/GetById/${chosen.periodTypeID}`,
                { headers }
              )
              .subscribe({
                next: (pt) => {
                  propagateSalaryFields(pt.description);
                },
                error: () => {
                  propagateSalaryFields('');
                },
              });
          } else {
            // No salary record found
            propagateSalaryFields('');
          }
        },
        error: () => {
          // On error fetching salaries: propagate zeros, name already set
          const base = {
            monthlySalary: 0,
            hoursWorkedPm: 0,
            ratePerMinute: 0,
            rateType: '',
            periodDescription: '',
          };
          if (this.isLabourItem(item)) {
            this.propagateEmployeeFields(empId, base);
          } else {
            this.propagateIndirectEmployeeFields(empId, base);
          }
          this.emitCostDataChange();
        },
      });

    // 3) Fetch Role to set roleDescription once
    if (emp) {
      this.http
        .get<Role>(
          `${environment.apiUrl}/api/Role/GetById?RoleId=${emp.roleID}`,
          { headers }
        )
        .subscribe({
          next: (role) => {
            if (this.isLabourItem(item)) {
              this.propagateEmployeeFields(empId, {
                roleDescription: role.roleName,
              });
            } else {
              this.propagateIndirectEmployeeFields(empId, {
                roleDescription: role.roleName,
              });
            }
          },
          error: () => {
            // Skip propagating empty roleDescription
          },
        });
    }
  }
  propagateEmployeeFields(
    employeeID: number | null,
    fields: {
      employeeName?: string;
      roleDescription?: string;
      monthlySalary?: number;
      hoursWorkedPm?: number;
      ratePerMinute?: number;
      rateType?: string;
      periodDescription?: string;
    }
  ) {
    this.filteredLabourItems.forEach((item) => {
      if (this.isLabourItem(item) && item.employeeID === employeeID) {
        if (fields.employeeName !== undefined) {
          item.employeeName = fields.employeeName;
        }
        if (fields.roleDescription !== undefined) {
          item.roleDescription = fields.roleDescription;
        }
        if (fields.monthlySalary !== undefined) {
          item.monthlySalary = fields.monthlySalary;
        }
        if (fields.hoursWorkedPm !== undefined) {
          item.hoursWorkedPm = fields.hoursWorkedPm;
        }
        if (fields.ratePerMinute !== undefined) {
          item.ratePerMinute = +fields.ratePerMinute.toFixed(4);
        }
        if (fields.rateType !== undefined) {
          item.rateType = fields.rateType;
        }
        // Recalculate unitCost for direct labour
        const rpm = item.ratePerMinute ?? 0;
        const batchSize = Math.max(1, item.batchSize ?? 1);
        const minutes = item.minutesPerJob ?? 0;
        item.unitCost = +((rpm * minutes) / batchSize).toFixed(2);
      }
    });
  }
  private isIndirectLabourItem(
    item: LabourItem | IndirectLabourItem
  ): item is IndirectLabourItem {
    return (item as IndirectLabourItem).minutesPerActivity !== undefined;
  }
  propagateIndirectEmployeeFields(
    employeeID: number | null,
    fields: {
      employeeName?: string;
      roleDescription?: string;
      monthlySalary?: number;
      hoursWorkedPm?: number;
      ratePerMinute?: number;
      rateType?: string;
      periodDescription?: string;
    }
  ) {
    this.filteredIndirectLabourItems.forEach((item) => {
      if (this.isIndirectLabourItem(item) && item.employeeID === employeeID) {
        if (fields.employeeName !== undefined) {
          item.employeeName = fields.employeeName;
        }
        if (fields.roleDescription !== undefined) {
          item.roleDescription = fields.roleDescription;
        }
        if (fields.monthlySalary !== undefined) {
          item.monthlySalary = fields.monthlySalary;
        }
        if (fields.hoursWorkedPm !== undefined) {
          item.hoursWorkedPm = fields.hoursWorkedPm;
        }
        if (fields.ratePerMinute !== undefined) {
          item.ratePerMinute = +fields.ratePerMinute.toFixed(4);
        }
        if (fields.rateType !== undefined) {
          item.rateType = fields.rateType;
        }
        // Recalculate unitCost for indirect labour
        const rpm = item.ratePerMinute ?? 0;
        const batchSize = Math.max(1, item.batchSize ?? 1);
        const minutes = item.minutesPerActivity ?? 0;
        item.unitCost = +((rpm * minutes) / batchSize).toFixed(2);
      }
    });
  }

  recalculateMaterial(item: RawMaterial) {
    // 1) price per *purchase-unit* := total price / total qty
    item.purchasePricePerUnit = item.purchaseQuantity
      ? +(item.purchasePrice / item.purchaseQuantity).toFixed(4)
      : 0;

    // 2) convert usageQuantity into *purchase-unit*
    const pu = this.getConversionFactor(item.purchaseUnit);
    const uu = this.getConversionFactor(item.usageUnit);
    const usageInPurchaseUnits = item.usageQuantity * (uu / pu);

    // 3) cost per finished unit
    item.unitCost =
      item.batchSize > 0
        ? +(
            (item.purchasePricePerUnit * usageInPurchaseUnits) /
            item.batchSize
          ).toFixed(2)
        : 0;
  }
  recalculateVariableOverhead(
    item: VariableOverhead | VariableOverheadItem
  ): void {
    // Use nullish coalescing operator (??) to provide a default of 0 if null
    const purchasePrice = item.purchasePrice ?? 0;
    const purchaseQuantity = item.purchaseQuantity ?? 0;

    // DON'T reassign these back to the item unless you're intentionally changing them
    // item.purchasePrice = purchasePrice; // Remove this line
    // item.purchaseQuantity = purchaseQuantity; // Remove this line

    const usageQuantity = item.usageQuantity ?? 0;
    const batchSize = item.batchSize ?? 0;

    // Calculate purchasePricePerUnit safely
    item.purchasePricePerUnit =
      purchaseQuantity > 0 ? +(purchasePrice / purchaseQuantity).toFixed(4) : 0;

    const pu = this.conversionFactors[item.purchaseUnit!] || 1;
    const uu = this.conversionFactors[item.usageUnit!] || 1;

    const usageInPurchaseUnits = usageQuantity * (uu / pu);

    item.unitCost =
      batchSize > 0
        ? +(
            (item.purchasePricePerUnit * usageInPurchaseUnits) /
            batchSize
          ).toFixed(2)
        : 0;
  }

  fetchTargetHistory(productId: number): void {
    const token = localStorage.getItem('authToken') || '';
    const headers = new HttpHeaders({ Authorization: `Bearer ${token}` });

    this.http
      .get<ItemTargetHistoryDTO[]>(
        `${environment.apiUrl}/api/ItemTargetHistory/GetByItemId?itemId=${productId}`,
        { headers }
      )
      .subscribe({
        next: (allHistories) => {
          const filtered = allHistories.filter((h) => h.itemID === productId);
          const latest =
            filtered.sort(
              (a, b) =>
                new Date(b.validFrom).getTime() -
                new Date(a.validFrom).getTime()
            )[0] || null;
          const cost = latest ? latest.targetCost : null;
          this.targetCostHistory[productId] = cost;
          this.targetTotalCost = cost;

          // Keep the dirty-detection baseline in sync with the loaded value so
          // Target Cost is not perpetually flagged as unsaved (it was hard-coded
          // to null on product load).
          const existing = this.originalProductValues.get(productId) || {
            targetCost: null,
            profitMarginPercent: null,
          };
          this.originalProductValues.set(productId, {
            ...existing,
            targetCost: cost,
          });
        },
        error: (err) => {
          // $&
          this.targetCostHistory[productId] = null;
          this.targetTotalCost = null;

          const existing = this.originalProductValues.get(productId) || {
            targetCost: null,
            profitMarginPercent: null,
          };
          this.originalProductValues.set(productId, {
            ...existing,
            targetCost: null,
          });
        },
      });
  }

  // on adding new items in the labor table
  // on adding new items in the labour table
  addNewLabour() {
    const isEditing = this.labourItems.some((item) => item.isEditing);
    if (isEditing) {
      this.showMessageModal = true;
      this.alertInfo =
        'Please save all inline edits before adding a new entry.';
      return;
    }
    const isPositive = this.checkTargetTotalCost(this.targetTotalCost);
    if (!isPositive) {
      this.showMessageModal = true;
      this.alertInfo = this.totalTargetCostAlert;
      return;
    }
    this.labourItems.unshift({
      inventoryId: 0,
      monthlySalary: 0,
      hoursWorkedPm: 0,
      ratePerMinute: 0,
      minutesPerJob: 0,
      isEditing: true,
      unitCost: 0,
      isNew: true,
      employeeName: '',
      roleDescription: '',
      activity: '',
      labourRateID: 0,
      itemOperationID: 0,
      employeeID: null,
      roleID: null,
      rateType: '',
      currency: '',
      effectiveFromDate: '',
      effectiveToDate: null,
      createdAt: '',
      updatedAt: '',
      operationTypeID: 0,
      operationID: 0,
      operationname: undefined,
      batchSize: 1,
    });
  }

  saveNewItem(item: IndirectLabourItem) {
    if (item.roleDescription && item.roleDescription.trim().length) {
      item.isNew = false;
    }
    // Add any additional save logic here
    // Monthly budget will auto-update through existing bindings
  }
  saveNewIndirectItem(item: IndirectLabourItem) {
    if (item.roleDescription && item.roleDescription.trim().length) {
      item.isNew = false;
      // make sure the rest of your usual sync logic still happens:
    }
  }
  // addfixed
  addNewFixed(): void {
    const isEditing = this.fixedItems.some((item) => item.isEditing);
    if (isEditing) {
      this.showMessageModal = true;
      this.alertInfo =
        'Please save all inline edits before adding a new entry.';
      return;
    }
    const isPositive = this.checkTargetTotalCost(this.targetTotalCost);
    if (!isPositive) {
      this.showMessageModal = true;
      this.alertInfo = this.totalTargetCostAlert;
      return;
    }
    // Changed to void as it doesn't return anything
    let initialBatchSize = 0;
    let initialMinutesPerOrder = 0;

    // Option A: Prefill from the *most recent* existing item (if any)
    if (this.fixedItems && this.fixedItems.length > 0) {
      const existingItem = this.fixedItems[0];
      initialBatchSize = existingItem.batchSize || 0;
      initialMinutesPerOrder = existingItem.minutesPerOrder || 0;
    }

    // Ensure _hoursOpenPerMonth is not null before using it,
    // though the ngOnInit chaining should guarantee it's set by now.
    // A fallback to 0 is good practice if it somehow isn't.
    const currentHoursOpenPerMonth =
      this._hoursOpenPerMonth !== null ? this._hoursOpenPerMonth : 0;

    this.fixedItems.unshift({
      itemId: null,
      itemName: '',
      finishedGoodID: this.selectedProductId || null,
      isNew: true,
      isEditing: true,
      // FIX: Use the correctly stored class property
      hoursOpenPerMonth: currentHoursOpenPerMonth,
      ratePerMinute: 0,
      minutesPerOrder: initialMinutesPerOrder,
      unitCost: 0,
      costPerMonth: 0,
      overheadCostPoolID: 0,
      description: null,
      overheadAllocationRateID: 0,
      currencyCode: '',
      rateType: '',
      itemOrderDurationID: 0,
      batchSize: initialBatchSize,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      overheadCostItemID: null,
    });

    // You might want to trigger onFOHChange for this new item if it sets up further calculations/UI.
    // For example:
    // this.onFOHChange(this.fixedItems[0]);
  }

  /** called on blur of the new‐item input */
  saveNewFixed(item: FixedItem) {
    if (item.itemName && item.itemName.trim().length) {
      item.isNew = false;
    }
  }
  /** Index of the row the user is currently working on */
  activeRowIndex: number | null = null;

  /** Convenience: the LabourItem for the active row (if any) */
  get activeItem(): LabourItem | null {
    const list = this.currentLabourList;
    return this.activeRowIndex !== null && list[this.activeRowIndex]
      ? list[this.activeRowIndex]
      : null;
  }
  /** If the active row is “applyToAll”, use its quantity; else null */

  /** Text for the Cost header */
  get costHeaderText(): string {
    return 'Cost/Each';
  }
  getUnitDisplay(uomId?: number | null): string {
    if (uomId == null) return '';
    const uomCode = this.getUomCodeById(uomId);
    if (uomCode === 'unit') return 'u';
    return (uomCode || '').replace(/2$/, '²');
  }
  async loadUnitOfMeasures(): Promise<void> {
    const token =
      localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
    const headers = new HttpHeaders({ Authorization: `Bearer ${token}` });

    try {
      const data = await firstValueFrom(
        this.http
          .get<UnitOfMeasure[]>(
            `${environment.apiUrl}/api/UnitOfMeasure/GetAll`,
            { headers }
          )
          .pipe(
            catchError((err) => {
              // $&
              return throwError(() => err); // propagate the error, don’t swallow
            })
          )
      );

      this.unitOfMeasures = data;
      this.uomMap = new Map(data.map((uom) => [uom.uomid, uom]));
    } catch (err) {
      // now _this_ catch actually runs if HTTP failed
      // you can handle or rethrow
      // $&
      throw err;
    }
  }

  getUOMIDByCode(unitCode: string): number | null {
    if (!unitCode) return null;
    const uom = this.unitOfMeasures.find((uomItem) => uomItem.uomCode === unitCode);
    return uom ? uom.uomid : null;
  }

  private getUomIdFromDto(uom: UnitOfMeasureDTO): number | null {
    return (uom as any).uomID ?? (uom as any).uomid ?? null;
  }

  // Group UOMs into compatibility families (weight, volume, length, count, area, other)
  private getUOMFamily(uom: UnitOfMeasureDTO): 'weight' | 'volume' | 'length' | 'count' | 'area' | 'other' {
    const code = (uom.uomCode || '').toLowerCase();
    const name = (uom.uomName || '').toLowerCase();

    const weight = ['kg', 'g', 'gram', 'kilogram', 'lb', 'pound', 'oz', 'ounce', 'mg'];
    if (weight.some((k) => code.includes(k) || name.includes(k))) return 'weight';

    const volumeCodeExact = ['l', 'lt', 'ml'];
    const volumeNameTokens = ['liter', 'litre', 'milliliter', 'millilitre', 'gallon'];
    const isVolume =
      volumeCodeExact.some((k) => code === k) ||
      volumeNameTokens.some((k) => code.includes(k) || name.includes(k));
    if (isVolume) return 'volume';

    const areaCodes = ['mm2', 'cm2', 'm2', 'mm²', 'cm²', 'm²'];
    const areaNames = ['square millimeter', 'square centimeter', 'square meter', 'square metre'];
    if (areaCodes.some((k) => code === k) || areaNames.some((k) => name.includes(k))) return 'area';

    const length = ['m', 'meter', 'metre', 'cm', 'centimeter', 'mm', 'millimeter', 'inch', 'ft', 'foot'];
    if (length.some((k) => code === k || code.includes(k) || name.includes(k))) return 'length';

    const count = ['ea', 'each', 'unit', 'pcs', 'piece', 'pc'];
    if (count.some((k) => code === k || code.includes(k) || name.includes(k))) return 'count';

    return 'other';
  }

  /**
   * Return UOMs compatible with the selected purchase unit. If none found, return all.
   */
  getCompatibleUOMs(baseUnitCode: string | null | undefined): UnitOfMeasureDTO[] {
    if (!this.unitsOfMeasure || this.unitsOfMeasure.length === 0) return [];
    if (!baseUnitCode) return this.unitsOfMeasure;

    const normalizedCode = baseUnitCode.toLowerCase();
    const baseUom = this.unitsOfMeasure.find((u) => (u.uomCode || '').toLowerCase() === normalizedCode);
    if (!baseUom) return this.unitsOfMeasure;

    const family = this.getUOMFamily(baseUom);
    const compatible = this.unitsOfMeasure.filter((u) => this.getUOMFamily(u) === family);

    // Ensure millimeter is available for length family even if backend omitted it
    if (family === 'length') {
      const hasMm = compatible.some((u) => (u.uomCode || '').toLowerCase() === 'mm');
      if (!hasMm) {
        compatible.push({ uomCode: 'mm', uomName: 'Millimeter', uomid: null } as any);
      }
    }

    return compatible;
  }

  onPurchaseUnitChange(item: RawMaterial, unitCode: string) {
    item.purchaseUnit = unitCode;
    const purchaseId = this.getUOMIDByCode(unitCode);
    if (purchaseId != null) {
      item.purchaseUOMID = purchaseId;
    }

    const compatible = this.getCompatibleUOMs(unitCode);
    const hasCurrentUsage = compatible.some((u) => (u.uomCode || '').toLowerCase() === (item.usageUnit || '').toLowerCase());
    if (!hasCurrentUsage) {
      const fallback = compatible[0];
      if (fallback) {
        item.usageUnit = fallback.uomCode;
        const fallbackId = this.getUomIdFromDto(fallback);
        if (fallbackId != null) {
          item.componentUOMID = fallbackId;
        }
      }
    }
    this.onMaterialChange(item);
  }

  onUsageUnitChange(item: RawMaterial, unitCode: string) {
    item.usageUnit = unitCode;
    const uomId = this.getUOMIDByCode(unitCode);
    if (uomId != null) {
      item.componentUOMID = uomId;
    }
    this.onMaterialChange(item);
  }

  getAllUOMs(): UnitOfMeasureDTO[] {
    return this.unitsOfMeasure || [];
  }

  /**
   * Full-plan only: user picked an existing inventory raw material from the
   * name dropdown — link it and auto-fill purchase price + UOM from inventory.
   */
  onMaterialNameSelect(item: RawMaterial, selectedItemId: number): void {
    const src = (this.materialDropdownOptions || []).find((i) => i.itemID === +selectedItemId);
    if (!src) return;

    item.inventoryId = src.itemID!;
    item.itemName = src.itemName || '';
    item.purchasePrice = src.purchasePrice ?? 0;
    item.purchaseQuantity = src.purchaseQuantity ?? 0;
    item.purchaseUOMID = src.purchaseUOMID;
    item.baseUOMID = src.purchaseUOMID;
    item.purchaseUnit = this.getUOMCodeByID(src.purchaseUOMID);
    item.itemCode = src.itemCode || '';
    item.description = src.description || '';
    item.categoryID = src.categoryID!;
    item.itemTypeID = src.itemTypeID!;

    this.onMaterialChange(item);
  }

  getUOMCodeByID(uomID: number | null): string {
    if (!uomID || uomID === 0) return '';
    const uom = this.unitOfMeasures.find((u) => u.uomid === uomID);
    return uom ? uom.uomCode : '';
  }
  ensureUOMMapping(items: VariableOverhead[]): void {
    items.forEach((item) => {
      // If uomID is not set but usageUnit is available, try to map it
      if ((!item.uomID || item.uomID === 0) && item.usageUnit) {
        item.uomID = this.getUOMIDByCode(item.usageUnit);
      }
      if (
        (!item.purchaseUOMID || item.purchaseUOMID === 0) &&
        item.purchaseUnit
      ) {
        item.purchaseUOMID = this.getUOMIDByCode(item.purchaseUnit);
      }
      if (!item.uomid && item.uomID) {
        item.uomid = item.uomID;
      }
      if (!item.uomID && item.uomid) {
        item.uomID = item.uomid;
      }
    });
  }
  setActiveRow(index: number) {
    this.activeRowIndex = index;
  }
  /** click pencil */
  startEditFixed(index: number): void {
    const item = this.fixedItems[index];
    item.isEditing = true;
  }

  startEditFixedItem(item: FixedItem): void {
    if (item.isPlaceholder) {
      const newItem: FixedItem = {
        ...item,
        isPlaceholder: false,
        isNew: false,
        isEditing: true,
      };
      this.fixedItems.unshift(newItem);
      if (this.selectedProductId != null) {
        this.fixedOverheadsMap[this.selectedProductId] = this.fixedItems;
      }
      if (newItem.overheadCostItemID) {
        this.getOverheadCostAndConvert(newItem, newItem.overheadCostItemID);
      }
    } else {
      item.isEditing = true;
    }
  }

  async removeFixedOverheadItem(item: FixedItem): Promise<void> {
    if (item.isPlaceholder) return;
    const index = this.fixedItems.findIndex((f) => f === item || (f.itemId != null && f.itemId === item.itemId));
    if (index !== -1) {
      await this.removeFixedOverhead(index);
    }
  }

  /** save both new & edits */
  saveFixedRow(item: FixedItem): void {
    if (!item.itemName.trim()) {
      return; // you could show validation here
    }

    // Finalize flags
    item.isNew = false;
    item.isEditing = false;

    const index = this.fixedItems.findIndex(
      (m) => m === item || (item.itemId != null && m.itemId === item.itemId)
    );

    if (index !== -1) {
      this.fixedItems[index] = { ...item };
      if (this.selectedProductId != null) {
        this.fixedOverheadsMap[this.selectedProductId] = this.fixedItems;
      }
    }

    // Re-apply allocation → overheadCost
    this.onFOHChange(item);

    // Push update
    this.emitCostDataChange();

    const pname = this.selectedProduct; // e.g. "Classic Beef Burger & Chips"
    const pid = this.selectedProductId; // e.g. 13

    if (!pname || pid == null) {
      console.warn(
        '[RawMaterialsComponent] No product selected; cannot update.'
      );
      return;
    }
    const updatedList: CombinedCostItem[] = this.getCombinedCosts();
    this.allProductCosts[pname] = updatedList;
    this.allProductCosts = { ...this.allProductCosts };
    this.allProductCostsLoaded.emit(this.allProductCosts);
  }

  /**
   * Promote every visible Fixed Overhead placeholder row into a real, new item
   * so the next save creates it (same conversion as startEditFixedItem). Shared
   * by the desktop "Save All" banner and the mobile "Save All" button so both
   * platforms attach every visible overhead to the product in one click.
   */
  promoteAllFixedPlaceholders(): void {
    const pid = this.selectedProductId;
    if (pid == null) return;

    this.mergedDesktopFixedItems.forEach((row) => {
      if (!row.isPlaceholder) return;
      const alreadyReal = this.fixedItems.some(
        (f) => !f.isPlaceholder && f.overheadCostItemID === row.overheadCostItemID
      );
      if (alreadyReal) return;
      this.fixedItems.unshift({
        ...row,
        isPlaceholder: false,
        isNew: true, // flag so getAddedFixedOverheads picks it up for creation
        isEditing: false,
      });
    });

    this.fixedItems
      .filter((item) => !item.isPlaceholder)
      .forEach((item) => {
        item.isEditing = false;
      });

    this.fixedOverheadsMap[pid] = [...this.fixedItems];
  }

  saveAllFixedRows(): void {
    const pname = this.selectedProduct;
    const pid = this.selectedProductId;
    if (!pname || pid == null) return;

    this.promoteAllFixedPlaceholders();

    // Persist via the full, proven save path (creates new + updates existing).
    this.saveChanges(pid, 'manual').then(() => {
      this.fohBatchChangePending = false;
    });
  }

  onQuantityChange(item: LabourItem): void {
    this.emitCostDataChange();
  }
  private isLabourItem(
    item: LabourItem | IndirectLabourItem
  ): item is LabourItem {
    return (item as LabourItem).minutesPerJob !== undefined;
  }
  onRoleChange(item: LabourItem | IndirectLabourItem): void {
    const newRole = item.roleDescription || '';
    const empId = item.employeeID;
    if (empId == null) {
      return;
    }
    if (this.isLabourItem(item)) {
      // propagate to other direct rows
      this.propagateEmployeeFields(empId, { roleDescription: newRole });
    } else if (this.isIndirectLabourItem(item)) {
      // propagate to other indirect rows
      this.propagateIndirectEmployeeFields(empId, { roleDescription: newRole });
    }
    this.emitCostDataChange();
  }
  // sync cost allocation
  onLabourChange(item: LabourItem): void {
    // Once every labour line has someone (and therefore a rate) behind it, the
    // "this service needs a rate" banner has served its purpose.
    if (this.serviceLabourNeedsRate) {
      this.serviceLabourNeedsRate = this.labourItems.some(
        (l) => l.employeeID == null || !l.ratePerMinute
      );
    }

    // 1) Guard and normalize inputs:
    const hrs = Math.max(0, item.hoursWorkedPm ?? 0);
    const salary = Math.max(0, item.monthlySalary ?? 0);
    const batchSize = Math.max(1, item.batchSize ?? 1); // avoid divide-by-zero
    if (item.batchSize <= 0) {
      item.unitCost = 0;
      return;
    }
    // 2) Recalculate ratePerMinute
    if (hrs > 0) {
      item.ratePerMinute = +(salary / (hrs * 60)).toFixed(4);
    } else {
      item.ratePerMinute = 0;
    }

    // 3) Recalculate unitCost for direct labour (minutesPerJob)
    const minutes = item.minutesPerJob ?? 0;
    const totalCost = item.ratePerMinute! * minutes;
    item.unitCost = +(totalCost / batchSize).toFixed(2);
    // 4) Propagate salary/hours (and optionally roleDescription) to other direct-labour rows
    const empId = item.employeeID;
    if (empId != null) {
      this.filteredLabourItems.forEach((other) => {
        // Only propagate to other direct LabourItem rows with same employeeID
        if (
          other !== item &&
          this.isLabourItem(other) &&
          other.employeeID === empId
        ) {
          // Propagate salary & hours & ratePerMinute
          other.monthlySalary = salary;
          other.hoursWorkedPm = hrs;
          other.ratePerMinute = item.ratePerMinute;

          // Recalculate unitCost for that other direct-labour row
          const m2 = (other as LabourItem).minutesPerJob ?? 0;
          const tot2 = other.ratePerMinute! * m2;
          other.unitCost = +(tot2 / Math.max(1, other.batchSize ?? 1)).toFixed(
            2
          );

          // If desired, also propagate roleDescription when salary/hours changed:
          if (item.roleDescription !== undefined) {
            other.roleDescription = item.roleDescription;
          }
        }
      });
    }

    // 5) Emit changes (e.g., notify parent or recalc summary)
    this.emitCostDataChange();
  }

  /** Called when user edits Unit Cost directly */

  initializeForm(): void {
    this.editForm = this.fb.group({
      itemName: ['', [Validators.required, Validators.minLength(3)]],
      quantity: [0, [Validators.required, Validators.min(1)]],
      unitCost: [0, [Validators.required, Validators.min(0.01)]],
    });
  }
  private initForms(): void {
    this.editForm = this.fb.group({
      itemName: ['', [Validators.required, Validators.minLength(3)]],
      quantity: [1, [Validators.required, Validators.min(1)]],
      unitCost: [0, [Validators.min(0)]], // only for RawMaterial
      allocation: [0, [Validators.min(0), Validators.max(100)]], // for FixedItem
      budgetedCost: [0, [Validators.min(0)]], // for FixedItem
    });

    this.competitorForm = this.fb.group({
      name: ['', Validators.required],
      price: [null, [Validators.required, Validators.min(0)]],
    });
  }
  addNewCompetitor(): void {
    const isPositive = this.checkTargetTotalCost(this.targetTotalCost);
    if (!isPositive) {
      this.showMessageModal = true;
      this.alertInfo = this.totalTargetCostAlert;
      return;
    }
    // 1) Make sure the array exists
    if (this.selectedProductId == null) return;
    this.ensureCompetitorList(this.selectedProductId);
    const isEditing = this.competitorPrices[this.selectedProductId].some(
      (item) => item.isEditing
    );
    if (isEditing) {
      this.showMessageModal = true;
      this.alertInfo =
        'Please save all inline edits before adding a new entry.';
      return;
    }
    // 2) Push the new “blank” competitor
    this.competitorPrices[this.selectedProductId].push({
      name: '',
      price: 0,
      isNew: true,
      isEditing: true,
      competitorID: 0,
      itemCompetitorID: 0,
      itemID: 0,
      website: '', // Added
      isActive: true, // Added with a default of true
      createdAt: new Date().toISOString(), // Added with current date
      updatedAt: new Date().toISOString(), // Added with current date
      targetMarketSegment: '', // Added
      primarySalesChannel: '', // Added
      geographicFocus: '', // Added
    });

    // 3) If you’re using OnPush, or want to re-trigger change-detect,
    //    either reassign the array reference:
    this.competitorPrices = {
      ...this.competitorPrices,
      [this.selectedProductId]: [
        ...this.competitorPrices[this.selectedProductId],
      ],
    };

    // 3b) Mirror into competitorPricesMap, which is what the save diff reads.
    // Pushing only into competitorPrices meant a new row was visible in the UI
    // but invisible to saving unless saveCompetitorRow happened to fire.
    this.syncCompetitorMapFromPrices(this.selectedProductId);

    // 4) And tell the parent / totals grid that things changed:
    this.emitCostDataChange();
  }

  /**
   * Keep competitorPricesMap (the save diff's source) aligned with
   * competitorPrices (what the table renders). These two drifting apart is the
   * reason captured competitors could never reach the backend.
   */
  private syncCompetitorMapFromPrices(productId: number): void {
    this.competitorPricesMap[productId] = (
      this.competitorPrices[productId] || []
    ).map((c) => ({ ...c }));
  }

  // CRUD for competitor
  addCompetitor(): void {
    if (this.competitorForm.invalid) return;
    if (this.selectedProductId == null) return;
    this.competitorPrices[this.selectedProductId].push(
      this.competitorForm.value
    );
    this.syncCompetitorMapFromPrices(this.selectedProductId);
    this.competitorForm.reset();
  }

  private ensureCompetitorList(pid: number): void {
    if (!this.competitorPrices[pid]) this.competitorPrices[pid] = [];
  }

  setActiveTab(tab: Tab): void {
    this.tabs.forEach((t) => (t.active = false));
    tab.active = true;
    this.currentCategory = tab.id;
    this.searchTerm = '';
    this.emitCostDataChange();
  }
  isActive(tabId: Category): boolean {
    return this.currentCategory === tabId;
  }

  openAddModal(): void {
    this.isNewRecord = true;
    this.selectedItem = null;
    this.originalItem = null;
    this.editForm.reset({
      itemName: '',
      quantity: 1,
      unitCost: 0,
    });
    this.showEditModal = true;
  }
  onMaterialChange(item: RawMaterial) {
    this.recalculateMaterial(item);
    this.emitCostDataChange();
  }
  onVariableOHChange(item: VariableOverheadItem): void {
    const purchasePrice = item.purchasePrice ?? 0;
    const purchaseQuantity = item.purchaseQuantity ?? 0;
    const usageQuantity = item.usageQuantity ?? 0; // <--- This is the value from the item
    const batchSize = item.batchSize ?? 0;
    item.purchasePricePerUnit =
      purchaseQuantity > 0 ? +(purchasePrice / purchaseQuantity).toFixed(4) : 0;
    const purchaseUnitCode = this.getUomCodeById(item.purchaseUOMID);
    const usageUnitCode = this.getUomCodeById(item.uomID);
    const pu = this.getConversionFactor(purchaseUnitCode);
    const uu = this.getConversionFactor(usageUnitCode);
    
    // Debug logging to track the conversion
    if (item.itemName?.includes('Packaging')) {
      console.log('Packaging Debug:', {
        itemName: item.itemName,
        purchaseUnitCode,
        usageUnitCode,
        pu,
        uu,
        usageQuantity,
        purchasePricePerUnit: item.purchasePricePerUnit,
        calculation: `${usageQuantity} * (${uu} / ${pu}) = ${usageQuantity * (uu / pu)}`
      });
    }
    
    if (pu === 0) {
      console.warn(
        'WARNING: Purchase unit conversion factor (pu) is zero. Cannot calculate usageInPurchaseUnits.'
      );
      item.unitCost = 0;
      if (!item.isNew) {
        item.isModified = true;
      }
      this.emitCostDataChange();
      return; // Exit early
    }

    const usageInPurchaseUnits = usageQuantity * (uu / pu);
    // Highlight the direct cause if usageQuantity is zero
    if (usageQuantity === 0) {
      console.warn(
        'NOTE: Usage Quantity is 0, so Usage in Purchase Units will be 0, leading to a 0 Unit Cost.'
      );
    }

    if (batchSize > 0) {
      const rawUnitCost =
        ((item.purchasePricePerUnit ?? 0) * usageInPurchaseUnits) / batchSize;
      item.unitCost = +rawUnitCost.toFixed(2);
      
      // Debug logging for final cost
      if (item.itemName?.includes('Packaging')) {
        console.log('Packaging Final Cost:', {
          usageInPurchaseUnits,
          batchSize,
          rawUnitCost,
          unitCost: item.unitCost
        });
      }
    } else {
      // $&
      item.unitCost = 0;
    }

    if (!item.isNew) {
      item.isModified = true;
    }
    this.emitCostDataChange();
  }
  getUomCodeById(uomId: number | null | undefined): string {
    if (uomId == null) {
      // This check correctly handles both null and undefined
      return '';
    }
    // Assuming `unitsOfMeasure` is `unitOfMeasures` as per previous code.
    // Using optional chaining `?.` is safer here as `unitsOfMeasure` might not be loaded yet.
    const uom = this.unitOfMeasures.find((u) => u.uomid === uomId);
    return uom ? uom.uomCode : '';
  }

  /** Returns a friendly code for dropdown display; e.g., "each" -> "ea" */
  getUomDisplay(code: string | null | undefined): string {
    if (!code) return '';
    const lower = code.toLowerCase();
    if (lower === 'each') return 'ea';
    return code.replace(/2$/, '²');
  }
  // RawMaterialsComponent
  getQtyLabel(qty: number, unitOrId?: string | number | null): string {
    // Option 1: Log only for items that have an inventoryId (means it's a real item, not a new, unsaved one)
    // And optionally, only if it's one of your debugItemIds
    const currentItemId = (this as any).item?.inventoryId; // Assuming 'this.item' refers to the current item being processed in the loop
    // THIS MIGHT NOT BE RELIABLE IF CALLED OUTSIDE A LOOP CONTEXT
    // A better way is to pass item.inventoryId to getQtyLabel if you need it for filtering.
    // For now, let's just log if unitOrId is problematic.

    let unitCode = '';
    if (typeof unitOrId === 'number' && unitOrId !== null) {
      unitCode = this.getUomCodeById(unitOrId).replace(/2$/, '²');
      if (!unitCode) {
        // Log only if the UOM code wasn't found for a given ID
        console.warn(
          `getQtyLabel: No UOM code found for ID ${unitOrId}. Item ID: ${
            currentItemId || 'N/A'
          }`
        );
      }
    } else if (typeof unitOrId === 'string' && unitOrId !== null) {
      unitCode = unitOrId.replace(/2$/, '²');
    } else {
      // This is a key scenario to log: unitOrId is null/undefined
      console.warn(
        `getQtyLabel: unitOrId is null or undefined. Item ID: ${
          currentItemId || 'N/A'
        }`
      );
    }

    if (unitCode === 'unit') {
      return qty === 1 ? 'unit' : 'units';
    }
    return (unitCode || 'units').replace(/2$/, '²');
  }

  loadVariableOverheads(): void {
    const headers = this.getAuthHeaders();

    // Create individual observables with their own error handling
    const items$ = this.http
      .get<ItemDTO[]>(`${environment.apiUrl}/api/Items/ByType/3`, { headers })
      .pipe(
        catchError((error) => {
          if (error.status === 404) {
            console.warn(
              'No items found for type 3 - this is normal if no variable overhead items exist'
            );
            return of([]); // Return an empty array
          } else {
            console.error('An error occurred during items$ call:', error);
            return of([]); // Also return empty array for other errors
          }
        })
      );

    const rawMaterials$ = this.http
      .get<ItemDTO[]>(`${environment.apiUrl}/api/Items/ByType/1`, { headers })
      .pipe(
        catchError((error) => {
          if (error.status === 404) {
            console.warn('No raw material items found');
            return of([]);
          }
          console.error('Error fetching raw materials for dropdown:', error);
          return of([]);
        })
      );

    const history$ = this.http
      .get<ItemSupplierPriceHistoryDTO[]>(
        `${environment.apiUrl}/api/ItemSupplierPriceHistory/GetAll`,
        { headers }
      )
      .pipe(
        catchError((error) => {
          if (error.status === 404) {
            console.warn(
              'No price history found - this is normal for new systems'
            );
            return of([]);
          } else {
            console.error('An error occurred during history$ call:', error);
            return of([]);
          }
        })
      );

    const supplierLocations$ = this.http
      .get<SupplierLocationsDTO[]>(
        `${environment.apiUrl}/api/SupplierLocations/GetAll`,
        { headers }
      )
      .pipe(
        catchError((error) => {
          if (error.status === 404) {
            console.warn(
              'No supplier locations found - this is normal for new systems'
            );
            return of([]);
          } else {
            console.error(
              'An error occurred during supplierLocations$ call:',
              error
            );
            return of([]);
          }
        })
      );

    const suppliers$ = this.http
      .get<SupplierDTO[]>(`${environment.apiUrl}/api/Suppliers/GetAll`, {
        headers,
      })
      .pipe(
        catchError((error) => {
          if (error.status === 404) {
            console.warn('No suppliers found - this is normal for new systems');
            return of([]); // Return empty array to prevent breaking the forkJoin
          } else {
            console.error('An error occurred during suppliers$ call:', error);
            return of([]); // Return empty array for other errors as well
          }
        })
      );

    const unitsOfMeasure$ = this.http
      .get<any[]>(`${environment.apiUrl}/api/UnitOfMeasure/GetAll`, { headers })
      .pipe(
        catchError((error) => {
          if (error.status === 404) {
            console.warn(
              'No units of measure found - this is normal for new systems'
            );
            return of([]);
          } else {
            console.error(
              'An error occurred during unitsOfMeasure$ call:',
              error
            );
            return of([]);
          }
        })
      );

    const categories$ = this.http
      .get<CategoryDTO[]>(`${environment.apiUrl}/api/Category/GetAll`, {
        headers,
      })
      .pipe(
        catchError((error) => {
          if (error.status === 404) {
            console.warn(
              'No categories found - this is normal for new systems'
            );
            return of([]);
          } else {
            console.error('An error occurred during categories$ call:', error);
            return of([]);
          }
        })
      );

    // Combine all observables
    forkJoin({
      items: items$,
      rawMaterials: rawMaterials$,
      history: history$,
      supplierLocations: supplierLocations$,
      suppliers: suppliers$,
      unitsOfMeasure: unitsOfMeasure$,
      categories: categories$,
    })
      .pipe(
        map(
          ({
            items,
            rawMaterials,
            history,
            supplierLocations,
            suppliers,
            unitsOfMeasure,
            categories,
          }) => {
            // Safely process items - ensure we have an array
            this.items = Array.isArray(items)
              ? items.map((item) => ({
                  ...item,
                  // Ensure itemName is always a string for .trim()
                  itemName: item.itemName ? item.itemName.trim() : '',
                  // Handle itemTypeID being undefined if ItemDTO allows it and VariableOverhead requires number
                  itemTypeID: item.itemTypeID ?? 0, // Provide a default if ItemDTO.itemTypeID can be undefined
                }))
              : [];

            this.suppliers = Array.isArray(suppliers) ? suppliers : [];
            this.supplierLocations = Array.isArray(supplierLocations)
              ? supplierLocations
              : [];

            // Map `any[]` UOMs to your UnitOfMeasureDTO[] - safely handle empty array
            this.unitsOfMeasure = Array.isArray(unitsOfMeasure)
              ? (unitsOfMeasure.map((uom: any) => ({
                  uomID: uom.uomid,
                  uomCode: uom.uomCode,
                  uomName: uom.uomName,
                })) as UnitOfMeasureDTO[])
              : [];

            this.categories = Array.isArray(categories) ? categories : [];
            this.allItemPriceHistory = Array.isArray(history) ? history : [];

            // Populate the latestItemPriceHistoryMap - but only if we have history data
            if (this.allItemPriceHistory.length > 0) {
              this.allItemPriceHistory
                .sort(
                  (a, b) =>
                    new Date(b.validFrom).getTime() -
                    new Date(a.validFrom).getTime()
                )
                .forEach((h) => {
                  if (!this.latestItemPriceHistoryMap.has(h.itemID)) {
                    this.latestItemPriceHistoryMap.set(h.itemID, h);
                  }
                });
            }

            // Filter items for variable overhead dropdown - safely handle empty items array
            this.itemsForVariableOverheadDropdown = this.items.filter(
              (item) => item.itemTypeID === 3
            );

            this.materialDropdownOptions = Array.isArray(rawMaterials)
              ? rawMaterials.map((item) => ({
                  ...item,
                  itemName: item.itemName ? item.itemName.trim() : '',
                  itemTypeID: item.itemTypeID ?? 1,
                }))
              : [];

            this.variableOverheadItems = []; // Set to empty or populate as needed from BOVOs

            return [];
          }
        ),
        catchError((error) => {
          // This block is less likely to be reached now
          console.error(
            'An unhandled error occurred after forkJoin. Initializing data to empty arrays.',
            error
          );
          // Initialize all arrays to empty to prevent undefined errors
          this.items = [];
          this.suppliers = [];
          this.supplierLocations = [];
          this.unitsOfMeasure = [];
          this.categories = [];
          this.allItemPriceHistory = [];
          this.itemsForVariableOverheadDropdown = [];
          this.materialDropdownOptions = [];
          this.variableOverheadItems = [];

          return of([]);
        })
      )
      .subscribe({
        next: () => {
          // Trigger change detection to update the view after data is loaded
          // The mock CDR will not do anything in this example
          if (this.cdr) {
            this.cdr.detectChanges();
          }
        },
        error: (error) => {
          console.error(
            'Final error in loadVariableOverheads subscription:',
            error
          );
          // Ensure UI state is updated even on error
          if (this.cdr) {
            this.cdr.detectChanges();
          }
        },
      });
  }
  // Update addNewMaterial() to include default units

  onProductChange(itemID: number): void {
    this.isSaving = false;
    this.selectedProductChange.emit({
      selectedProductName: this.selectedProduct,
    });
    this.selectedProductId = itemID;

    const prod = this.products.find((p) => p.itemID === itemID);
    if (!prod) {
      this.materialItems = [];
      this.originalMaterialItemsMap[itemID] = [];
      this.emitCostDataChange();
      return;
    }
    this.selectedProduct = prod.itemName;
    this.markupPercent = prod.profitMarginPercent || 0;
    this.displayValue = (prod.profitMarginPercent || 0).toString();

    // Store the original profit margin percent for save comparison
    if (!this.originalProductValues.has(itemID)) {
      this.originalProductValues.set(itemID, {
        targetCost: null,
        profitMarginPercent: prod.profitMarginPercent || 0,
      });
    } else {
      // Update the existing entry with the current profit margin percent
      const existing = this.originalProductValues.get(itemID)!;
      existing.profitMarginPercent = prod.profitMarginPercent || 0;
    }

    // Ensure parent Markup Price card is updated when product changes
    this.onMarkupPercentChange(true);

    // --- RAW MATERIALS HANDLING (Crucial Update for Deep Copy) ---
    if (this.materialItemsMap[itemID]) {
      this.materialItems = this.materialItemsMap[itemID];
      if (
        !this.originalMaterialItemsMap[itemID] ||
        this.originalMaterialItemsMap[itemID].length === 0
      ) {
        this.originalMaterialItemsMap[itemID] =
          this.materialItems?.map((item) => ({ ...item })) ?? [];
      }
      // Added a null-check here
      this.materialItems?.forEach((mat) => this.recalculateMaterial(mat));
      this.emitCostDataChange();
    } else {
      this.fetchRawMaterials(itemID).subscribe(
        (materials: RawMaterial[]) => {
          this.materialItemsMap[itemID] = materials ?? [];
          this.materialItems = this.materialItemsMap[itemID];

          this.originalMaterialItemsMap[itemID] =
            materials?.map((item) => ({
              ...item,
            })) ?? [];

          // Added a null-check here
          this.materialItems?.forEach((mat) => this.recalculateMaterial(mat));
          this.emitCostDataChange();
        },
        (error) => {
          // $&
          this.materialItemsMap[itemID] = [];
          this.originalMaterialItemsMap[itemID] = [];
          this.materialItems = [];
          this.emitCostDataChange();
        }
      );
    }

    // ... (rest of your onProductChange code for other sections like fixed, competitors, labour, variable) ...

    this.fohBatchChangePending = false;
    this.fetchFixedOverheads(this.selectedProductId).subscribe({
      next: (fixedOverheads: FixedItem[]) => {
        this.fixedItems = fixedOverheads ?? [];
        if (this.selectedProductId == null) return;
        this.fixedOverheadsMap[this.selectedProductId] = JSON.parse(
          JSON.stringify(fixedOverheads ?? [])
        );
        if (
          !this.originalFixedOverheadsMap[this.selectedProductId] ||
          this.originalFixedOverheadsMap[this.selectedProductId].length === 0
        ) {
          this.originalFixedOverheadsMap[this.selectedProductId] = JSON.parse(
            JSON.stringify(fixedOverheads ?? [])
          );
        }
        // Added a null-check here
        this.fixedItems?.forEach((item) => this.recalculateFixed(item));
        this.emitCostDataChange();
      },
      error: (err) => {
        // $&
        this.fixedItems = [];
        if (this.selectedProductId == null) return;
        this.fixedOverheadsMap[this.selectedProductId] = [];
        this.originalFixedOverheadsMap[this.selectedProductId] = [];
        this.emitCostDataChange();
      },
    });

    this.fetchCompetitors(this.selectedProductId).subscribe({
      next: (competitors: Competitor[]) => {
        this.competitors = competitors ?? [];
        if (this.selectedProductId == null) {
          return;
        }
        this.competitorPrices[this.selectedProductId] = JSON.parse(
          JSON.stringify(competitors ?? [])
        );
        this.competitorPricesMap[this.selectedProductId] = JSON.parse(
          JSON.stringify(competitors ?? [])
        );
        if (
          !this.originalCompetitorPricesMap[this.selectedProductId] ||
          this.originalCompetitorPricesMap[this.selectedProductId].length === 0
        ) {
          this.originalCompetitorPricesMap[this.selectedProductId] = JSON.parse(
            JSON.stringify(competitors ?? [])
          );
        }
        this.emitCostDataChange();
      },
      error: (err) => {
        // $&
        this.competitors = [];
        if (this.selectedProductId == null) return;
        this.competitorPrices[this.selectedProductId] = [];
        this.competitorPricesMap[this.selectedProductId] = [];
        this.originalCompetitorPricesMap[this.selectedProductId] = [];
        this.emitCostDataChange();
      },
    });

    this.fetchDirectLabourItems(this.selectedProductId).subscribe({
      next: (rawLabourItems: LabourItem[]) => {
        if (this.selectedProductId == null) return;
        const labourItems = this.withSeededServiceLabour(
          this.selectedProductId,
          Array.isArray(rawLabourItems) ? rawLabourItems : []
        );
        // Assign a NEW deep copy to the UI-bound property
        this.labourItems = structuredClone(labourItems);
        this.directLaborItemsMap[this.selectedProductId] =
          structuredClone(labourItems);
        if (
          !this.originalDirectLaborItemsMap[this.selectedProductId] ||
          this.originalDirectLaborItemsMap[this.selectedProductId].length === 0
        ) {
          this.originalDirectLaborItemsMap[this.selectedProductId] =
            structuredClone(labourItems);
        }
        this.labourItems?.forEach((dl) => this.onLabourChange(dl));
        if (
          this.pendingServiceRatePromptFor === this.selectedProductId &&
          this.serviceLabourNeedsRate
        ) {
          const prod = this.products.find((p) => p.itemID === this.selectedProductId);
          this.serviceNeedsRatePopupProductName = prod?.itemName ?? '';
          this.showServiceNeedsRatePopup = true;
        }
        this.pendingServiceRatePromptFor = null;
        this.emitCostDataChange();
      },
      error: (err) => {
        // $&
        this.labourItems = [];
        this.pendingServiceRatePromptFor = null;
        if (this.selectedProductId == null) return;
        this.directLaborItemsMap[this.selectedProductId] = [];
        this.originalDirectLaborItemsMap[this.selectedProductId] = [];
        this.emitCostDataChange();
      },
    });

    this.fetchIndirectLabourItems(this.selectedProductId).subscribe({
      next: (indirectLabourItems) => {
        if (this.selectedProductId == null) return;
        // Assign a NEW deep copy to the UI-bound property
        this.indirectLabourItems = structuredClone(indirectLabourItems);
        this.indirectLaborItemsMap[this.selectedProductId] =
          structuredClone(indirectLabourItems);
        if (
          !this.originalinDirectLaborItemsMap[this.selectedProductId] ||
          this.originalinDirectLaborItemsMap[this.selectedProductId].length ===
            0
        ) {
          this.originalinDirectLaborItemsMap[this.selectedProductId] =
            structuredClone(indirectLabourItems);
        }
        this.indirectLabourItems?.forEach((dl) => this.onIndirectChange(dl));
        this.emitCostDataChange();
      },
      error: (err) => {
        // $&
        this.indirectLabourItems = [];
        if (this.selectedProductId == null) return;
        this.indirectLaborItemsMap[this.selectedProductId] = [];
        this.originalinDirectLaborItemsMap[this.selectedProductId] = [];
        this.emitCostDataChange();
      },
    });

    this.fetchVariableOverheads(this.selectedProductId).subscribe({
      next: (variableOverheads: VariableOverhead[]) => {
        this.variableFull = variableOverheads ?? [];
        if (this.selectedProductId == null) return;
        this.variableOverheadsMap[this.selectedProductId] = JSON.parse(
          JSON.stringify(variableOverheads ?? [])
        );
        if (
          !this.originalVariableOverheadsMap[this.selectedProductId] ||
          this.originalVariableOverheadsMap[this.selectedProductId].length === 0
        ) {
          this.originalVariableOverheadsMap[this.selectedProductId] =
            JSON.parse(JSON.stringify(variableOverheads ?? []));
        }
        // Added a null-check here
        this.variableFull?.forEach((voh) =>
          this.recalculateVariableOverhead(voh)
        );
        this.emitCostDataChange();
      },
      error: (error) => {
        // $&
        this.variableFull = [];
        if (this.selectedProductId == null) return;
        this.variableOverheadsMap[this.selectedProductId] = [];
        this.originalVariableOverheadsMap[this.selectedProductId] = [];
        this.emitCostDataChange();
      },
    });

    this.fetchTargetHistory(itemID);
    // Ensure AI suggestions are loaded whenever a product is fetched/selected
    this.loadAiSuggestionForProduct(itemID);
    this.productNameChange.emit(this.selectedProduct);
  }

  // edit overhead cost using input

  // Update search term and emit updated data.
  onSearchChange(event: any): void {
    this.searchTerm = event.target.value.toLowerCase();
    this.emitCostDataChange();
  }

  // Update sort option and emit updated data.
  onSortChange(event: any): void {
    this.sortBy = event.target.value;
    this.emitCostDataChange();
  }
  onSharedBatchChange(item: FixedItem): void {
    this.onFOHChange(item);
    this.fohBatchChangePending = true;
  }

  onFOHChange(changedItem: FixedItem): void {
    // 1. Capture the new common values from the item that just changed
    const newCommonMinutesPerOrder = changedItem.minutesPerOrder;
    const newCommonBatchSize = changedItem.batchSize;

    // 2. Propagate these new common values to ALL other fixed items
    this.fixedItems.forEach((item) => {
      // We want to update ALL items, including the one that just changed,
      // to ensure consistency for batchSize and minutesPerOrder.
      // The changedItem already has the new value, so setting it again is harmless.
      item.minutesPerOrder = newCommonMinutesPerOrder;
      item.batchSize = newCommonBatchSize;

      // 3. After propagating the common values, recalculate dependent costs for EACH item
      this.calculateFixedItemCosts(item);
    });

    // 4. Trigger global updates/emissions only once after all items are processed
    this.emitCostDataChange();

    const pname = this.selectedProduct;
    const pid = this.selectedProductId;

    if (!pname || pid == null) {
      console.warn(
        '[RawMaterialsComponent] No product selected; cannot update.'
      );
      return;
    }

    const updatedList: CombinedCostItem[] = this.getCombinedCosts();
    this.allProductCosts[pname] = updatedList;
    this.allProductCosts = { ...this.allProductCosts }; // Trigger change detection for allProductCosts object
    this.allProductCostsLoaded.emit(this.allProductCosts);
  }
  private calculateFixedItemCosts(item: FixedItem): void {
    // Recalculate ratePerMinute based on its own specific values
    item.ratePerMinute =
      item.hoursOpenPerMonth > 0
        ? +(item.costPerMonth / (item.hoursOpenPerMonth * 60)).toFixed(4)
        : 0;

    // Recalculate unitCost based on the (potentially updated) minutesPerOrder, ratePerMinute, and batchSize
    item.unitCost =
      item.minutesPerOrder && item.ratePerMinute && item.batchSize > 0
        ? +(
            (item.minutesPerOrder * item.ratePerMinute) /
            item.batchSize
          ).toFixed(4)
        : 0;
  }

  getCount(tabId: string): number {
    if (this.selectedProductId == null) return 0;
    switch (tabId) {
      case 'materials':
        return this.rawMaterials.length;
      case 'labour':
        return this.labourItems.length;
      case 'indirect-labour':
        return this.indirectLabourItems.length; // ← new
      case 'fixed':
        return this.fixedOverheadNames?.length || this.fixedItems.length;
      case 'variable':
        return this.variableData.length;
      case 'competitor':
        return (this.competitorPrices[this.selectedProductId]?.length - 1) || 0;
      default:
        return 0;
    }
  }
  fetchLabourItems(
    productId: number,
    operationTypeId: number = 1,
    isIsolated: boolean = false
  ): Observable<LabourItem[] | IndirectLabourItem[]> {
    const cacheKey = this.getLabourCacheKey(productId, operationTypeId);

    // --- Check Cache and Apply Isolation Logic ---
    if (this.labourItemsCache.has(cacheKey)) {
      // Return a deep copy from the cache to prevent reference sharing
      const cachedData = structuredClone(this.labourItemsCache.get(cacheKey)!);
      return of(cachedData);
    }

    const token = localStorage.getItem('authToken') || '';
    const headers = new HttpHeaders({ Authorization: `Bearer ${token}` });

    return forkJoin({
      allItemOperations: this.http.get<ItemOperation[]>(
        `${environment.apiUrl}/api/ItemOperation`,
        { headers }
      ),
      operations: this.http.get<Operation[]>(
        `${environment.apiUrl}/api/Operation/GetByOperationType/${operationTypeId}`,
        { headers }
      ),
      labourRates: this.http.get<LabourRate[]>(
        `${environment.apiUrl}/api/LabourRates/GetByOperationType/${operationTypeId}`,
        { headers }
      ),
    }).pipe(
      switchMap(({ allItemOperations, operations, labourRates }) => {
        const relevantOps = allItemOperations.filter(
          (op) =>
            op.finishedGoodItemID === productId &&
            operations.some((o) => o.operationID === op.operationID)
        );

        if (!relevantOps.length) {
          return of([]);
        }

        const labourRateMap = new Map<number, LabourRate>();
        labourRates.forEach((lr) => {
          labourRateMap.set(lr.labourRateID, lr);
        });

        const employeeIds = Array.from(
          new Set(
            labourRates
              .map((lr) => lr.employeeID)
              .filter((id): id is number => id !== null && id !== undefined)
          )
        );

        const employeeRequests$ = employeeIds.length
          ? forkJoin(
              employeeIds.map((id) =>
                this.http.get<Employee>(
                  `${environment.apiUrl}/api/Employees/GetById/${id}`,
                  { headers }
                )
              )
            )
          : of([] as Employee[]);

        const salaryRequests$ = employeeIds.length
          ? forkJoin(
              employeeIds.map((id) =>
                this.http.get<EmployeeSalaryDTO[]>(
                  `${environment.apiUrl}/api/EmployeeSalaries/GetByEmployeeID/${id}`,
                  { headers }
                )
              )
            )
          : of([] as EmployeeSalaryDTO[][]);

        return forkJoin({
          employees: employeeRequests$,
          salariesArray: salaryRequests$,
        }).pipe(
          switchMap(({ employees, salariesArray }) => {
            const employeeMap = new Map<number, Employee>();
            employees.forEach((emp) => {
              employeeMap.set(emp.employeeID, emp);
            });

            const salaryMap = new Map<number, EmployeeSalaryDTO>();
            employeeIds.forEach((empId, idx) => {
              const salaryList = salariesArray[idx] || [];
              if (!salaryList.length) {
                return;
              }
              const now = new Date();
              let chosen: EmployeeSalaryDTO | null = null;
              for (const s of salaryList) {
                const start = new Date(s.startDate);
                const end = s.endDate ? new Date(s.endDate) : null;
                if (start <= now && (end === null || now <= end)) {
                  chosen = s;
                  break;
                }
              }
              if (!chosen) {
                chosen = salaryList.reduce((prev, cur) => {
                  const prevDate = new Date(prev.startDate);
                  const curDate = new Date(cur.startDate);
                  return curDate > prevDate ? cur : prev;
                });
              }
              if (chosen) {
                salaryMap.set(empId, chosen);
              }
            });

            const periodTypeIds = Array.from(
              new Set(Array.from(salaryMap.values()).map((s) => s.periodTypeID))
            );

            const periodTypeRequests$ = periodTypeIds.length
              ? forkJoin(
                  periodTypeIds.map((ptId) =>
                    this.http.get<PeriodTypeDTO>(
                      `${environment.apiUrl}/api/PeriodTypes/GetById/${ptId}`,
                      { headers }
                    )
                  )
                )
              : of([] as PeriodTypeDTO[]);

            const roleIds = Array.from(
              new Set(employees.map((e) => e.roleID).filter((id) => !!id))
            );
            const roleRequests$ = roleIds.length
              ? forkJoin(
                  roleIds.map((id) =>
                    this.http.get<Role>(
                      `${environment.apiUrl}/api/Role/GetById?RoleId=${id}`,
                      { headers }
                    )
                  )
                )
              : of([] as Role[]);

            return forkJoin({
              periodTypes: periodTypeRequests$,
              roles: roleRequests$,
            }).pipe(
              map(({ periodTypes, roles }) => {
                const periodTypeMap = new Map<number, string>();
                periodTypes.forEach((pt) => {
                  periodTypeMap.set(pt.periodTypeID, pt.description);
                });
                const roleMap = new Map<number, Role>();
                roles.forEach((role) => {
                  roleMap.set(role.roleID, role);
                });

                const operationMap = new Map<number, Operation>();
                operations.forEach((op) =>
                  operationMap.set(op.operationID, op)
                );

                // 🌟 Create and return a new array directly
                if (operationTypeId === 1) {
                  return relevantOps
                    .map((op) => {
                      const lr = labourRateMap.get(op.labourRateIDDefault);
                      if (!lr || lr.employeeID == null) return null;
                      const empId = lr.employeeID;
                      const salary = salaryMap.get(empId);
                      const amount = salary?.amount ?? 0;
                      const hoursWorked = salary?.hoursWorked ?? 0;
                      const ratePerMinute =
                        hoursWorked > 0 ? amount / hoursWorked / 60 : 0;
                      const minutesPerJob = op.standardRunTimeMinutesPerUnit;
                      const batchSize = op.batchSize;

                      const employee = employeeMap.get(empId) || null;
                      const role = employee
                        ? roleMap.get(employee.roleID)
                        : null;

                      const periodTypeID = salary?.periodTypeID;
                      const rateTypeDesc =
                        periodTypeID != null
                          ? periodTypeMap.get(periodTypeID) || 'N/A'
                          : 'N/A';

                      return {
                        inventoryId: productId,
                        batchSize,
                        operationID: op.operationID,
                        activity:
                          operationMap.get(op.operationID)?.operationName ||
                          'N/A',
                        monthlySalary: amount,
                        hoursWorkedPm: hoursWorked,
                        ratePerMinute,
                        minutesPerJob,
                        unitCost: ratePerMinute * minutesPerJob,
                        employeeName: employee
                          ? `${employee.firstName} ${employee.lastName}`
                          : 'N/A',
                        roleDescription: role?.roleName || 'N/A',
                        isNew: false,
                        labourRateID: lr.labourRateID,
                        itemOperationID: op.itemOperationID,
                        employeeID: empId,
                        roleID: employee?.roleID ?? null,
                        rateType: rateTypeDesc,
                        currency: salary?.currency || 'N/A',
                        effectiveFromDate: salary
                          ? salary.startDate
                          : new Date().toISOString(),
                        effectiveToDate: salary?.endDate ?? null,
                        createdAt: new Date().toISOString(),
                        updatedAt: new Date().toISOString(),
                        operationTypeID: lr.operationTypeID,
                        isEditing: false,
                      } as LabourItem;
                    })
                    .filter(
                      (item): item is LabourItem => item !== null
                    ) as LabourItem[];
                } else if (operationTypeId === 2) {
                  return relevantOps
                    .map((op) => {
                      const lr = labourRateMap.get(op.labourRateIDDefault);
                      if (!lr || lr.employeeID == null) return null;
                      const empId = lr.employeeID;
                      const salary = salaryMap.get(empId);
                      const amount = salary?.amount ?? 0;
                      const hoursWorked = salary?.hoursWorked ?? 0;
                      const ratePerMinute =
                        hoursWorked > 0 ? amount / hoursWorked / 60 : 0;
                      const minutesPerActivity =
                        op.standardRunTimeMinutesPerUnit;
                      const batchSize = op.batchSize;

                      const employee = employeeMap.get(empId) || null;
                      const role = employee
                        ? roleMap.get(employee.roleID)
                        : null;

                      const periodTypeID = salary?.periodTypeID;
                      const rateTypeDesc =
                        periodTypeID != null
                          ? periodTypeMap.get(periodTypeID) || 'N/A'
                          : 'N/A';

                      return {
                        isEditing: false,
                        isNew: false,
                        inventoryId: productId,
                        activity:
                          operationMap.get(op.operationID)?.operationName ||
                          'N/A',
                        operationname:
                          operationMap.get(op.operationID)?.operationName ||
                          'N/A',
                        monthlySalary: amount,
                        hoursWorkedPm: hoursWorked,
                        ratePerMinute,
                        minutesPerActivity,
                        unitCost: ratePerMinute * minutesPerActivity,
                        batchSize,
                        employeeName: employee
                          ? `${employee.firstName} ${employee.lastName}`
                          : 'N/A',
                        roleDescription: role?.roleName || 'N/A',
                        labourRateID: lr.labourRateID,
                        itemOperationID: op.itemOperationID,
                        employeeID: empId,
                        roleID: employee?.roleID ?? null,
                        rateType: rateTypeDesc,
                        currency: salary?.currency || 'N/A',
                        effectiveFromDate: salary
                          ? salary.startDate
                          : new Date().toISOString(),
                        effectiveToDate: salary?.endDate ?? null,
                        createdAt: new Date().toISOString(),
                        updatedAt: new Date().toISOString(),
                        operationTypeID: lr.operationTypeID,
                        operationID: op.operationID,
                      } as IndirectLabourItem;
                    })
                    .filter(
                      (item): item is IndirectLabourItem => item !== null
                    ) as IndirectLabourItem[];
                } else {
                  return [];
                }
              })
            );
          })
        );
      }),
      tap((labourItems) => {
        const cacheKey = this.getLabourCacheKey(productId, operationTypeId);
        // Store a deep copy in the cache
        this.labourItemsCache.set(cacheKey, structuredClone(labourItems));
      }),
      catchError((err) => {
        const cacheKey = this.getLabourCacheKey(productId, operationTypeId);
        console.error(
          `Error fetching labour items for key "${cacheKey}":`,
          err
        );
        return throwError(() => err);
      })
    );
  }

  private getLabourCacheKey(
    productId: number,
    operationTypeId: number
  ): string {
    return `${productId}_${operationTypeId}`;
  }

  clearLabourItemsCache(): void {
    this.labourItemsCache.clear();
  }

  clearMaterialItemsCache(): void {
    this.rawMaterialsCache.clear();
  }

  clearCompetitorsCache(): void {
    this.competitorsCache.clear();
  }

  fetchCompetitors(productId: number): Observable<Competitor[]> {
    // --- Caching Logic: Check Cache First ---
    if (this.competitorsCache.has(productId)) {
      // Return a deep copy to prevent external modification of cached data
      return of(
        JSON.parse(JSON.stringify(this.competitorsCache.get(productId)!))
      );
    }

    const token = localStorage.getItem('authToken') || '';
    const headers = new HttpHeaders({ Authorization: `Bearer ${token}` });

    // 1. Fetch item-specific competitor records (link between item and competitor, and competitor's price for this item)
    const competitorItemLinkReq = this.http.get<any[]>(
      `${environment.apiUrl}/api/ItemCompetitor/item/${productId}/competitors`,
      { headers }
    );

    // Also fetch ItemTargetHistory to get targetSellingPrice for Company Target entry
    const targetHistoryReq = this.http.get<ItemTargetHistoryDTO[]>(
      `${environment.apiUrl}/api/ItemTargetHistory/GetByItemId?itemId=${productId}`,
      { headers }
    ).pipe(
      catchError(() => of([])) // Return empty array on error
    );

    return forkJoin({
      itemCompetitorResponse: competitorItemLinkReq,
      targetHistory: targetHistoryReq,
    }).pipe(
      switchMap(({ itemCompetitorResponse, targetHistory }) => {
        // Get latest targetSellingPrice from ItemTargetHistory
        const latestHistory = targetHistory
          .filter((h) => h.itemID === productId)
          .sort((a, b) => new Date(b.validFrom).getTime() - new Date(a.validFrom).getTime())[0];
        let targetSellingPrice = latestHistory?.targetSellingPrice ?? 0;

        // Fallback: if ItemTargetHistory doesn't have a price, use Items.sellingPrice
        if (targetSellingPrice === 0) {
          const product = this.products.find((p) => p.itemID === productId);
          targetSellingPrice = product?.sellingPrice ?? 0;
        }

        // If no competitor entries, return empty array initially
        if (!itemCompetitorResponse || itemCompetitorResponse.length === 0) {
          // No existing competitors, so we'll just add the company price entry
          const companySellingPriceEntry: Competitor = {
            competitorID: 0, // Or a specific ID for "Company Selling Price" if you have one
            itemCompetitorID: 0, // Will be 0 or null as it's a new entry
            itemID: productId,
            name: 'Company Target Selling Price',
            price: targetSellingPrice, // Use targetSellingPrice from ItemTargetHistory
            isNew: targetSellingPrice === 0, // Mark as new if no price exists
            isEditing: false,
            website: '',
            targetMarketSegment: '',
            notes: '',
            isActive: true,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            primarySalesChannel: '',
            geographicFocus: '',
            fullTarget: latestHistory ?? undefined,
          };
          return of([companySellingPriceEntry]); // Return an observable of an array containing only this new entry
        }

        // --- Prepare for fetching master competitor details ---
        type ItemCompetitorRec = {
          itemCompetitorID: number;
          competitorID: number;
          priceFromItemCompetitor: number; // The price specific to this item-competitor link
        };

        const itemCompetitorList: ItemCompetitorRec[] =
          itemCompetitorResponse.map((c: any) => ({
            itemCompetitorID: c.itemCompetitorID,
            competitorID: c.competitorID,
            priceFromItemCompetitor: c.competitorPrice ?? c.price ?? 0, // Price from the item-competitor table
          }));

        // Fetch master details for each competitorID
        const masterCalls: Observable<any>[] = itemCompetitorList.map((rec) => {
          if (rec.competitorID == null) {
            // $&
            return of(null); // Return null for invalid IDs to keep forkJoin array length consistent
          }
          return this.http
            .get<any>(
              `${environment.apiUrl}/api/Competitors/GetById?CompetitorsId=${rec.competitorID}`,
              { headers }
            )
            .pipe(
              tap((master) => {
                if (!master) {
                  console.warn(
                    `Competitor master GET returned empty for ID ${rec.competitorID}`
                  );
                }
              }),
              catchError((error) => {
                console.error(
                  `Error fetching Competitor master for ID ${rec.competitorID}:`,
                  error
                );
                return of(null); // Return null on error so the main stream doesn't break
              })
            );
        });

        // Use forkJoin to fetch all master competitor details in parallel
        return forkJoin(masterCalls).pipe(
          map((masterResponses: any[]) => {
            const result: Competitor[] = [];
            let companyPriceFound = false; // Flag to track if "Company Target Selling Price" is found

            itemCompetitorList.forEach((rec, idx) => {
              const master = masterResponses[idx]; // master will be null if a request failed or ID was null

              // Determine name: prefer master.competitorName/name, then a fallback
              let name = '';
              if (master && (master.competitorName || master.name)) {
                name = master.competitorName ?? master.name;
              } else {
                console.warn(
                  `No name available for competitor index ${idx}, record:`,
                  rec,
                  'master response:',
                  master
                );
                name = 'Unknown Competitor'; // Provide a fallback name
              }

              // The price should come from the ItemCompetitor table (rec.priceFromItemCompetitor)
              const price = rec.priceFromItemCompetitor;

              const comp: Competitor = {
                competitorID: rec.competitorID,
                itemCompetitorID: rec.itemCompetitorID,
                itemID: productId,
                name,
                price,
                isNew: false,
                isEditing: false,
                // Master fields only if master is not null:
                website: master?.website ?? '',
                targetMarketSegment: master?.targetMarketSegment ?? '',
                notes: master?.notes ?? '', // Assuming 'notes' can come from master
                isActive: master?.isActive ?? true,
                createdAt: master?.createdAt ?? new Date().toISOString(),
                updatedAt: master?.updatedAt ?? new Date().toISOString(),
                competitorTier: master?.competitorTier, // Assuming 'competitorTier' can come from master
                primarySalesChannel: master?.primarySalesChannel ?? '',
                geographicFocus: master?.geographicFocus ?? '',
              };

              // Check if this is the "Company Target Selling Price" entry
              if (name.toLowerCase().includes('company target selling price')) {
                // Use targetSellingPrice from ItemTargetHistory as authoritative source
                comp.price = targetSellingPrice;
                comp.fullTarget = latestHistory ?? undefined;
                comp.isNew = targetSellingPrice === 0;
                // This will be inserted at the beginning of the array
                result.unshift(comp);
                companyPriceFound = true; // Set flag to true
              } else {
                // Regular competitor entries
                result.push(comp);
              }
            });

            // --- Add "Company Target Selling Price" if not found ---
            if (!companyPriceFound) {
              const companySellingPriceEntry: Competitor = {
                competitorID: 0, // Or a specific ID for "Company Selling Price" if you have one
                itemCompetitorID: 0, // Will be 0 or null as it's a new entry
                itemID: productId,
                name: 'Company Target Selling Price',
                price: targetSellingPrice, // Use targetSellingPrice from ItemTargetHistory
                isNew: targetSellingPrice === 0, // Mark as new if no price exists
                isEditing: false,
                website: '',
                targetMarketSegment: '',
                notes: '',
                isActive: true,
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
                primarySalesChannel: '',
                geographicFocus: '',
                fullTarget: latestHistory ?? undefined,
              };
              result.unshift(companySellingPriceEntry); // Prepend to the beginning
            }

            return result;
          })
        );
      }),
      // --- Caching Logic: Store in Cache on Success ---
      tap((finalCompetitors) => {
        this.competitorsCache.set(
          productId,
          JSON.parse(JSON.stringify(finalCompetitors))
        );
      }),
      // --- Error Handling for the entire stream ---
      catchError((err) => {
        console.error(
          `Error fetching competitors for product ID ${productId}:`,
          err
        );
        return throwError(() => err); // Re-throw the error
      })
    );
  }
  /**
   * True while the selected Service is showing a seeded labour line that still
   * needs an employee/rate chosen. Drives the banner in the template.
   */
  serviceLabourNeedsRate = false;

  /**
   * A Service has no materials — its cost is the time it takes multiplied by
   * someone's rate. The time is already known (it is the Service's
   * PurchaseQuantity, in minutes), so seed a Direct Labour line carrying that
   * time and leave the employee/rate for the user to pick. Without this the
   * calculator opens completely empty for a Service and there is nothing to
   * cost against.
   *
   * Only seeds when the Service has no labour lines saved yet, so it never
   * fights with work the user has already done.
   */
  private withSeededServiceLabour(
    productId: number,
    labourItems: LabourItem[]
  ): LabourItem[] {
    const product = this.products.find((p) => p.itemID === productId);
    const isService = product?.itemTypeID === 4;

    if (!isService) {
      if (productId === this.selectedProductId) this.serviceLabourNeedsRate = false;
      return labourItems;
    }

    if (labourItems.length > 0) {
      // Already has labour — only flag it if a line is still missing its rate.
      if (productId === this.selectedProductId) {
        this.serviceLabourNeedsRate = labourItems.some(
          (l) => l.employeeID == null || !l.ratePerMinute
        );
      }
      return labourItems;
    }

    const minutes = this.serviceMinutesById.get(productId) ?? 0;
    const seeded: LabourItem = {
      inventoryId: productId,
      monthlySalary: 0,
      hoursWorkedPm: 0,
      ratePerMinute: 0,
      minutesPerJob: minutes,
      isEditing: true,
      unitCost: 0,
      isNew: true,
      employeeName: '',
      roleDescription: '',
      activity: product?.itemName ?? 'Service',
      labourRateID: 0,
      itemOperationID: 0,
      employeeID: null,
      roleID: null,
      rateType: '',
      currency: '',
      effectiveFromDate: '',
      effectiveToDate: null,
      createdAt: '',
      updatedAt: '',
      operationTypeID: 0,
      operationID: 0,
      operationname: undefined,
      batchSize: 1,
    };

    if (productId === this.selectedProductId) this.serviceLabourNeedsRate = true;
    return [seeded];
  }

  /** Service delivery time in minutes, keyed by item id. */
  private serviceMinutesById = new Map<number, number>();

  /**
   * Builds the single direct-material line for a Buy Out item.
   *
   * A Buy Out is bought and resold untouched, so it is its own only component
   * and its purchase price is the whole material cost. This mirrors the
   * self-referencing BOM the backend creates, but works off the item directly
   * so the tab is populated even when that BOM row has not been created yet.
   */
  private buildBuyoutMaterialLine(
    productId: number,
    isIsolated: boolean
  ): Observable<any[]> {
    const token = localStorage.getItem('authToken') || '';
    const headers = new HttpHeaders({ Authorization: `Bearer ${token}` });

    return this.http
      .get<any>(`${environment.apiUrl}/api/Items/GetById?ItemsId=${productId}`, { headers })
      .pipe(
        map((item) => {
          if (!item) return [] as RawMaterial[];

          const purchaseQuantity = item.purchaseQuantity || 1;
          const purchasePrice = item.purchasePrice || 0;
          const unitCost = purchaseQuantity > 0 ? purchasePrice / purchaseQuantity : purchasePrice;
          const uomCode = this.getAllUOMs?.().find((u: any) => u.uomid === item.purchaseUOMID)?.uomCode ?? '';

          const line: RawMaterial = {
            inventoryId: item.itemID,
            itemName: item.itemName,
            purchaseQuantity,
            purchasePrice,
            purchasePricePerUnit: unitCost,
            purchaseUnit: uomCode,
            usageUnit: uomCode,
            unitCost,
            isNew: false,
            isEditing: false,
            itemCode: item.itemCode ?? '',
            description: item.description ?? '',
            itemTypeID: item.itemTypeID,
            baseUOMID: item.purchaseUOMID,
            lowStockThreshold: item.lowStockThreshold ?? 0,
            isActive: item.isActive !== false,
            createdAt: item.createdAt ?? '',
            updatedAt: item.updatedAt ?? '',
            sellingPrice: item.sellingPrice ?? 0,
            profitMarginPercent: item.profitMarginPercent ?? 0,
            targetCost: 0,
            valuationMethodCode: item.valuationMethodCode ?? 'FIFO',
            categoryID: item.categoryID,
            sku: item.itemCode ?? '',
            // No stored BOM row backs this line.
            bomComponentID: 0,
            bomHeaderID: 0,
            componentUOMID: item.purchaseUOMID,
            usageQuantity: 1,
            batchSize: 1,
            scrapFactor: null,
            notes: 'Buy Out item — sold as purchased.',
            purchaseUOMID: item.purchaseUOMID,
          } as RawMaterial;

          const lines = [line];
          if (!isIsolated && productId === this.selectedProductId) {
            this.materialItems = lines.map((l) => ({ ...l }));
            this.originalMaterialItemsMap[productId] = lines.map((l) => ({ ...l }));
            this.materialItemsMap[productId] = lines.map((l) => ({ ...l }));
            this.emitCostDataChange();
          }
          this.rawMaterialsCache.set(productId, lines.map((l) => ({ ...l })));
          return lines;
        }),
        catchError(() => of([] as RawMaterial[]))
      );
  }

  fetchRawMaterials(
    productId: number,
    isIsolated: boolean = false
  ): Observable<RawMaterial[]> {
    // A Service has no materials by definition - its self-BOM exists only so
    // ProductionOrders.BOMHeaderIDUsed (NOT NULL) is satisfiable, and must not
    // be surfaced as a direct material line. Its cost comes from the seeded
    // Direct Labour line instead.
    const selectedForType = this.products.find((p) => p.itemID === productId);
    if (selectedForType?.itemTypeID === 4) {
      if (!isIsolated && productId === this.selectedProductId) {
        this.materialItems = [];
        this.emitCostDataChange();
      }
      return of([] as RawMaterial[]);
    }

    // Caching Logic: Check Cache First
    // $&
    if (this.rawMaterialsCache.has(productId)) {
      // Return a deep copy to prevent external modification of cached data
      const cachedData =
        this.rawMaterialsCache.get(productId)?.map((item) => ({ ...item })) ||
        [];

      // Only update UI data if NOT isolated AND this is the selected product
      if (!isIsolated && productId === this.selectedProductId) {
        // $&
        this.materialItems = cachedData;
        this.originalMaterialItemsMap[productId] = cachedData.map((item) => ({
          ...item,
        }));
        this.materialItemsMap[productId] = cachedData.map((item) => ({
          ...item,
        }));
      }
      return of(cachedData);
    }

    const token = localStorage.getItem('authToken') || '';
    const headers = new HttpHeaders({ Authorization: `Bearer ${token}` });

    return this.http
      .get<BOMHeader[]>(`${environment.apiUrl}/api/BOMHeaders/GetAll`, {
        headers,
      })
      .pipe(
        // 1) Find BOMHeader
        switchMap((bomHeaders) => {
          const bomHeader = bomHeaders.find(
            (h) => h.finishedGoodItemID === productId
          );
          if (!bomHeader?.bomHeaderID) {
            // A Buy Out item is a raw material sold as-is, so its own purchase
            // cost IS its direct material cost. Its BOM is a self-referencing
            // placeholder that may not exist yet, so build the material line
            // straight off the item rather than showing an empty tab.
            const selected = this.products.find((p) => p.itemID === productId);
            if (selected?.isBuyout) {
              return this.buildBuyoutMaterialLine(productId, isIsolated);
            }

            // CRITICAL FIX: Ensure UI is cleared and empty array is returned.
            if (!isIsolated && productId === this.selectedProductId) {
              this.materialItems = [];
              this.emitCostDataChange();
            }
            return of([] as any[]); // Return an empty array if no BOM header
          }
          return forkJoin([
            of(bomHeader),
            this.http.get<BOMComponent[]>(
              `${environment.apiUrl}/api/BOMComponents/GetAll`,
              { headers }
            ),
          ]);
        }),
        // 2) Filter components
        switchMap(([bomHeader, allComponents]) => {
          // Add a null check for allComponents, as it might be null or undefined
          const components =
            allComponents?.filter(
              (c: BOMComponent) => c.bomHeaderID === bomHeader.bomHeaderID
            ) ?? [];

          if (!components.length) {
            // CRITICAL FIX: Ensure UI is cleared and empty array is returned.
            if (!isIsolated && productId === this.selectedProductId) {
              this.materialItems = [];
              this.emitCostDataChange();
            }
            return of([] as any[]);
          }
          // 3) Fetch each Item record
          return forkJoin([
            of(components),
            forkJoin(
              components.map((component: BOMComponent) =>
                this.http
                  .get<Item>(
                    `${environment.apiUrl}/api/Items/GetById?ItemsId=${component.rawMaterialItemID}`,
                    { headers }
                  )
                  .pipe(map((item) => ({ component, item })))
              )
            ),
          ]);
        }),
        // 4) Build rawMaterials skeleton and populate purchase data from Item
        switchMap(([components, itemPairs]) => {
          const rawMaterials: RawMaterial[] =
            itemPairs?.map(
              ({
                component,
                item,
              }: {
                component: BOMComponent;
                item: Item;
              }) => {
                // Add null checks for 'item' and its properties
                const safeItem = item ?? ({} as Item);
                const safeComponent = component ?? ({} as BOMComponent);
                const purchaseQuantity = safeItem.purchaseQuantity ?? 0;
                const purchasePrice = safeItem.purchasePrice ?? 0;

                return {
                  inventoryId: safeComponent.rawMaterialItemID,
                  itemName: safeItem.itemName || '',
                  purchaseQuantity: purchaseQuantity,
                  purchasePrice: purchasePrice,
                  purchasePricePerUnit:
                    purchaseQuantity > 0 ? purchasePrice / purchaseQuantity : 0,
                  usageQuantity: safeComponent.quantityRequired ?? 0,
                  batchSize:
                    safeComponent.batchSize && safeComponent.batchSize > 0
                      ? safeComponent.batchSize
                      : 1,
                  purchaseUnit: '',
                  usageUnit: '',
                  unitCost: 0,
                  isNew: false,
                  isEditing: false,
                  itemCode: safeItem.itemCode || '',
                  description: safeItem.description || '',
                  itemTypeID: safeItem.itemTypeID!,
                  baseUOMID: safeItem.purchaseUOMID!,
                  lowStockThreshold: safeItem.lowStockThreshold ?? 0,
                  isActive: safeItem.isActive ?? true,
                  createdAt: safeItem.createdAt || new Date().toISOString(),
                  updatedAt: new Date().toISOString(),
                  sellingPrice: safeItem.sellingPrice ?? 0,
                  profitMarginPercent: safeItem.profitMarginPercent ?? 0,
                  targetCost: safeItem.purchasePrice ?? 0,
                  valuationMethodCode: safeItem.valuationMethodCode || '',
                  categoryID: safeItem.categoryID!,
                  sku: safeItem.itemCode || '',
                  bomComponentID: safeComponent.bomComponentID,
                  bomHeaderID: safeComponent.bomHeaderID,
                  componentUOMID: safeComponent.componentUOMID,
                  scrapFactor: safeComponent.scrapFactor,
                  notes: safeComponent.notes || '',
                  supplierPriceHistoryId: undefined,
                  currencyCode: undefined,
                  purchaseUOMID: safeItem.purchaseUOMID,
                  uomConversionFactor: undefined,
                  supplierItemCode: undefined,
                  supplierLocationID: undefined,
                  leadTimeDays: undefined,
                  validFrom: undefined,
                  validTo: undefined,
                  historyCreatedAt: undefined,
                  userIDEntered: undefined,
                };
              }
            ) ?? [];
          return of(rawMaterials);
        }),
        // 5) Enrich with UOM details
        switchMap((rawMaterials) => {
          const uomIds = Array.from(
            new Set(
              rawMaterials
                .flatMap((rm) => [rm.purchaseUOMID, rm.componentUOMID])
                .filter((id): id is number => id != null)
            )
          );
          if (uomIds.length === 0) {
            return of(rawMaterials); // Return materials directly if no UOMs to fetch
          }
          const uomRequests = uomIds.map((uomid) =>
            this.http.get<{ uomid: number; uomCode: string; uomName: string }>(
              `${environment.apiUrl}/api/UnitOfMeasure/GetById?UnitOfMeasureId=${uomid}`,
              { headers }
            )
          );
          return forkJoin(uomRequests).pipe(
            map((uoms) => {
              const uomMap = new Map<number, string>();
              uoms.forEach((u) => uomMap.set(u.uomid, u.uomCode));

              rawMaterials.forEach((rm) => {
                if (rm.purchaseUOMID != null) {
                  rm.purchaseUnit = uomMap.get(rm.purchaseUOMID) || '';
                }
                if (rm.componentUOMID != null) {
                  rm.usageUnit = uomMap.get(rm.componentUOMID) || '';
                }
              });
              return rawMaterials;
            })
          );
        }),
        // Caching Logic: Store in Cache on Success
        tap((materials) => {
          const materialsToCache = materials ?? [];
          this.rawMaterialsCache.set(
            productId,
            materialsToCache.map((item) => ({ ...item }))
          );

          // Only update UI-bound arrays if NOT isolated AND this is the selected product
          if (!isIsolated && productId === this.selectedProductId) {
            this.materialItems = materialsToCache;
            this.originalMaterialItemsMap[productId] = materialsToCache.map(
              (item) => ({ ...item })
            );
            this.materialItemsMap[productId] = materialsToCache.map((item) => ({
              ...item,
            }));
          }
        }),
        catchError((err) => {
          console.error(
            `Error fetching raw materials for product ${productId}:`,
            err
          );
          return of([]); // Return an empty array on error to prevent crashes
        })
      );
  }

  clearRawMaterialsCache(): void {
    this.rawMaterialsCache.clear();
  }

  clearFixedOverheadsCache(): void {
    this.fixedOverheadsCache.clear();
  }
  fetchFixedOverheads(
    productId: number,
    isIsolated: boolean = false
  ): Observable<FixedItem[]> {
    const cacheKey = productId;

    if (this.fixedOverheadsCache.has(cacheKey)) {
      const cachedData = JSON.parse(
        JSON.stringify(this.fixedOverheadsCache.get(cacheKey)!)
      );

      if (!isIsolated && productId === this.selectedProductId) {
        this.fixedItems = cachedData;
      }
      return of(cachedData);
    }

    if (this._hoursOpenPerMonth === null) {
      console.error(
        'Hours open per month has not been initialized. Call initializeHoursOpenPerMonth() first.'
      );
      return throwError(() => new Error('Hours open per month not available.'));
    }

    const hoursOpenPerMonth = this._hoursOpenPerMonth;
    const token = localStorage.getItem('authToken') || '';
    const headers = new HttpHeaders({ Authorization: `Bearer ${token}` });

    return this.http
      .get<ItemOrderDuration[]>(
        `${environment.apiUrl}/api/ItemOrderDuration/GetByItemId?itemId=${productId}`,
        { headers }
      )
      .pipe(
        map((durations) => {
          if (durations.length > 0) {
            return {
              minutesPerOrder: durations[0].totalTimeMinutes,
              batchSize: durations[0].batchSize,
              itemOrderDurationID: durations[0].itemOrderDurationID,
            };
          } else {
            // console.warn(
            //   'Order duration not found, using default 0 minutes per order for product',
            //   productId
            // );
            return {
              minutesPerOrder: 0,
              itemOrderDurationID: 0,
              batchSize: 0,
            };
          }
        }),
        switchMap((orderDurationData) =>
          this.http
            .get<OverheadCostPool[]>(
              `${environment.apiUrl}/api/OverheadCostPool/GetByFinishedGoodId`,
              {
                headers,
                params: new HttpParams().set(
                  'finishedGoodId',
                  String(productId)
                ),
              }
            )
            .pipe(
              switchMap((matchedPools) => {
                if (!matchedPools || matchedPools.length === 0) {
                  // console.warn(
                  //   'No overhead cost pools found for product ID:',
                  //   productId
                  // );
                  return of([]);
                }

                // Prepare an array of observables for fetching allocation rates for ALL overhead cost items
                const allocationRateRequests = matchedPools.flatMap((pool) =>
                  pool.overheadCostItems.map((item) =>
                    this.http
                      .get<OverheadAllocationRateDTO[]>(
                        `${environment.apiUrl}/api/OverheadAllocationRate/GetByCostItemId/${item.overheadCostItemID}`,
                        { headers }
                      )
                      .pipe(
                        catchError((err) => {
                          console.error(
                            `Error fetching allocation rates for cost item ${item.overheadCostItemID}:`,
                            err
                          );
                          return of([] as OverheadAllocationRateDTO[]);
                        })
                      )
                  )
                );

                // Run all allocation rate requests in parallel and wait for all to complete
                return forkJoin(allocationRateRequests).pipe(
                  map((allRatesArrays) => {
                    // Assign allocation rates back to overhead cost items
                    let index = 0;
                    for (const pool of matchedPools) {
                      for (const item of pool.overheadCostItems) {
                        item.overheadAllocationRates =
                          allRatesArrays[index] || [];
                        index++;
                      }
                    }

                    // Map the pools and items to FixedItem[]
                    const fixedItems: FixedItem[] = [];
                    const now = new Date();

                    matchedPools.forEach((pool) => {
                      pool.overheadCostItems.forEach((item) => {
                        const validRates = item.overheadAllocationRates
                          .filter((rate) => new Date(rate.validFrom) <= now)
                          .sort(
                            (a, b) =>
                              new Date(b.validFrom).getTime() -
                              new Date(a.validFrom).getTime()
                          );

                        const currentRate =
                          validRates.length > 0 ? validRates[0] : null;

                        const rateValue = currentRate?.rateValue ?? 0;
                        const rpm =
                          hoursOpenPerMonth > 0
                            ? rateValue / (hoursOpenPerMonth * 60)
                            : 0;
                        const unitCost =
                          rpm * orderDurationData.minutesPerOrder;

                        fixedItems.push({
                          itemId: pool.overheadCostPoolID,
                          finishedGoodID: productId,
                          itemName: item.overheadName,
                          overheadCostPoolID: pool.overheadCostPoolID,
                          overheadCostItemID: item.overheadCostItemID,
                          description: item.description,
                          costPerMonth: rateValue,
                          hoursOpenPerMonth,
                          ratePerMinute: parseFloat(rpm.toFixed(6)),
                          minutesPerOrder: orderDurationData.minutesPerOrder,
                          itemOrderDurationID:
                            orderDurationData.itemOrderDurationID,
                          unitCost: parseFloat(unitCost.toFixed(6)),

                          isNew: false,
                          isEditing: false,
                          overheadAllocationRateID:
                            currentRate?.overheadAllocationRateID ?? 0,
                          currencyCode: currentRate?.currencyCode || 'N/A',
                          batchSize: orderDurationData.batchSize,
                          createdAt: pool.createdAt,
                          updatedAt: pool.updatedAt,
                          rateType: '',
                        });
                      });
                    });

                    return fixedItems;
                  })
                );
              }),
              catchError((err) => {
                // $&
                return of([]);
              })
            )
        ),
        tap((items) => {
          this.fixedOverheadsCache.set(
            cacheKey,
            JSON.parse(JSON.stringify(items))
          );

          if (!isIsolated && productId === this.selectedProductId) {
            this.fixedItems = JSON.parse(JSON.stringify(items));
          }
        }),
        catchError((err) => {
          console.error(
            `Error fetching fixed overheads for product ID ${cacheKey}:`,
            err
          );
          return throwError(() => err);
        })
      );
  }

  recalculateFixed(item: FixedItem): void {
    // 1) Recalculate ratePerMinute := costPerMonth / (hoursOpenPerMonth × 60)
    item.ratePerMinute =
      item.hoursOpenPerMonth > 0
        ? +(item.costPerMonth / (item.hoursOpenPerMonth * 60)).toFixed(4)
        : 0;

    // 2) unit cost = (ratePerMinute × minutesPerOrder) ÷ batchSize
    const totalCost = item.ratePerMinute * (item.minutesPerOrder || 0);
    const batchSize = item.batchSize && item.batchSize > 0 ? item.batchSize : 1;

    item.unitCost = +(totalCost / batchSize).toFixed(2);

    this.emitCostDataChange();
  }
  clearVariableOverheadsCache(): void {
    this.variableOverheadsCache.clear();
  }
  fetchVariableOverheads(
    productId: number,
    isIsolated: boolean = false
  ): Observable<VariableOverhead[]> {
    const cacheKey = productId;
    const shouldLog = productId === 26;

    if (this.variableOverheadsCache.has(cacheKey)) {
      const cachedObservable = this.variableOverheadsCache.get(cacheKey)!;

      if (!isIsolated && productId === this.selectedProductId) {
        // Subscribe to get the actual data for UI updates
        cachedObservable.pipe(take(1)).subscribe((cachedData) => {
          this.variableFull = Array.isArray(cachedData) ? cachedData : [];
          if (this.variableFull.length > 0) {
            this.variableFull.forEach((voh) =>
              this.recalculateVariableOverhead(voh)
            );
          }
          this.emitCostDataChange();
        });
      }

      return cachedObservable;
    }

    const headers = this.getAuthHeaders();

    const fetchObservable = this.http
      .get<BOVOHeader>(
        `${environment.apiUrl}/api/BOVOHeader/GetLatestByFinishedGoodItem/${productId}`,
        { headers }
      )
      .pipe(
        switchMap((bovoHeader: BOVOHeader) => {
          if (!bovoHeader?.bovoHeaderID) {
            if (shouldLog) {
              console.warn(
                'No matching BOVO header found for product:',
                productId
              );
            }
            return of({ vohs: [], uomIds: [] });
          }

          return this.http
            .get<BOVOComponent[]>(
              `${environment.apiUrl}/api/BOVOComponent/header/${bovoHeader.bovoHeaderID}`,
              { headers }
            )
            .pipe(
              switchMap((components: BOVOComponent[]) => {
                if (!Array.isArray(components) || !components.length) {
                  if (shouldLog) {
                    console.warn(
                      `No BOVO components found for header ${bovoHeader.bovoHeaderID}`
                    );
                  }
                  return of({ vohs: [], uomIds: [] });
                }

                const vohCalls = components.map((component) =>
                  this.http
                    .get<Item>(
                      `${environment.apiUrl}/api/Items/GetById?ItemsId=${component.variableOverheadItemID}`,
                      { headers }
                    )
                    .pipe(
                      map((item: Item): VariableOverhead => {
                        const voh: VariableOverhead = {
                          inventoryId: component.variableOverheadItemID,
                          usageQuantity: component.usageQuantity,
                          batchSize: component.batchSize,
                          usageUnit: '',
                          purchaseUnit: '',
                          purchasePrice: item.purchasePrice,
                          purchaseQuantity: item.purchaseQuantity,
                          purchasePricePerUnit: item.purchaseQuantity
                            ? item.purchasePrice / item.purchaseQuantity
                            : 0,
                          unitCost: 0,
                          monthlyCost: component.monthlyCost ?? null,
                          estimatedSales: component.estimatedSales ?? null,
                          isNew: false,
                          bovoComponentID: component.bovoComponentID,
                          bovoHeaderID: component.bovoHeaderID,
                          variableOverheadItemID:
                            component.variableOverheadItemID,
                          unitsToMake: component.batchSize,
                          uomid: component.uomid || item.purchaseUOMID || null,
                          notes: component.notes || '',
                          itemName: item.itemName,
                          itemCode: item.itemCode || '',
                          description: item.description || '',
                          itemTypeID: item.itemTypeID ?? 0,
                          lowStockThreshold: item.lowStockThreshold,
                          isActive: item.isActive,
                          createdAt: item.createdAt,
                          updatedAt: item.updatedAt,
                          valuationMethodCode: item.valuationMethodCode,
                          categoryID: item.categoryID,
                          invalidLowStockThreshold: false,
                          overhead: null,
                          supplierID: item.supplierID,
                          uomID: component.uomid || item.purchaseUOMID || null,
                          overheadCostPoolID: 0,
                          supplierPriceHistoryId: undefined,
                          currencyCode: '',
                          purchaseUOMID: item.purchaseUOMID,
                          uomConversionFactor: undefined,
                          supplierItemCode: '',
                          supplierLocationID: undefined,
                          leadTimeDays: undefined,
                          validFrom: '',
                          validTo: undefined,
                          historyCreatedAt: '',
                          userIDEntered: undefined,
                        };
                        return voh;
                      }),
                      catchError((itemError) => {
                        console.error(
                          `Failed to fetch item ${component.variableOverheadItemID}:`,
                          itemError
                        );
                        return of(null); // Return null for failed items
                      })
                    )
                );

                return forkJoin(vohCalls).pipe(
                  map((vohs: (VariableOverhead | null)[]) => {
                    // Filter out null values from failed item fetches
                    const validVohs = vohs.filter(
                      (voh): voh is VariableOverhead => voh !== null
                    );

                    const uomIds = Array.from(
                      new Set(
                        validVohs
                          .flatMap((voh: VariableOverhead) => [
                            voh.uomid,
                            voh.purchaseUOMID,
                          ])
                          .filter((id): id is number => id != null && id !== 0)
                      )
                    );
                    return { vohs: validVohs, uomIds };
                  })
                );
              }),
              catchError((componentError) => {
                console.error(
                  `Failed to fetch components for header ${bovoHeader.bovoHeaderID}:`,
                  componentError
                );
                return of({ vohs: [], uomIds: [] });
              })
            );
        }),
        switchMap(({ vohs, uomIds }) => {
          if (!Array.isArray(uomIds) || !uomIds.length) {
            if (shouldLog) {
              // $&
            }
            return of(vohs || []); // Ensure array
          }

          const uomCalls = uomIds.map((uomid) =>
            this.http
              .get<UnitOfMeasure>(
                `${environment.apiUrl}/api/UnitOfMeasure/GetById?UnitOfMeasureId=${uomid}`,
                { headers }
              )
              .pipe(
                catchError((error) => {
                  // $&
                  return of(null);
                })
              )
          );

          return forkJoin(uomCalls).pipe(
            map((uoms: (UnitOfMeasure | null)[]) => {
              const uomMap = new Map<number, string>();
              uoms.forEach((u) => {
                if (u) {
                  uomMap.set(u.uomid, u.uomCode);
                }
              });

              // Ensure vohs is an array before processing
              if (Array.isArray(vohs)) {
                vohs.forEach((voh: VariableOverhead) => {
                  if (voh.uomid != null && voh.uomid !== 0) {
                    voh.usageUnit = uomMap.get(voh.uomid) || '';
                    if (shouldLog && !voh.usageUnit) {
                      console.warn(
                        `⚠️ No UOM code found for usage UOM ID ${voh.uomid}`
                      );
                    }
                  }

                  if (voh.purchaseUOMID != null && voh.purchaseUOMID !== 0) {
                    voh.purchaseUnit = uomMap.get(voh.purchaseUOMID) || '';
                    if (shouldLog && !voh.purchaseUnit) {
                      console.warn(
                        `⚠️ No UOM code found for purchase UOM ID ${voh.purchaseUOMID}`
                      );
                    }
                  }
                });
              }
              return vohs || [];
            })
          );
        }),
        tap((finalVoHs: VariableOverhead[]) => {
          if (!isIsolated && productId === this.selectedProductId) {
            this.variableFull = Array.isArray(finalVoHs) ? finalVoHs : [];
            if (this.variableFull.length > 0) {
              this.variableFull.forEach((voh) =>
                this.recalculateVariableOverhead(voh)
              );
            }
            this.emitCostDataChange();
          }
        }),
        catchError((err) => {
          if (err?.status !== 404) {
            console.error(
              `Error fetching variable overheads for product ID ${cacheKey}:`,
              err
            );
          }

          if (!isIsolated && productId === this.selectedProductId) {
            this.variableFull = [];
            this.emitCostDataChange();
          }

          return of([]); // Return empty array instead of throwing error
        }),
        shareReplay(1) // Cache the result with shareReplay
      );

    // Cache the observable
    this.variableOverheadsCache.set(cacheKey, fetchObservable);
    return fetchObservable;
  }
  // check if apply to all is checked

  updateOtherData(): void {
    const pid = this.selectedProductId;

    // Fixed Overheads

    // Direct Labour
  }

  get currentLabourList(): LabourItem[] {
    if (this.currentCategory === 'labour') {
      return this.labourItems;
    }

    return [];
  }
  get currentIndirectLabourList(): IndirectLabourItem[] {
    if (this.currentCategory === 'indirect-labour') {
      return this.indirectLabourItems;
    }
    return [];
  }

  saveNewCompetitor(comp: Competitor & { isNew?: boolean }): void {
    if (comp.name.trim() && comp.price >= 0) {
      comp.isNew = false;
      this.emitCostDataChange();
    }
  }
  startEditCompetitor(index: number): void {
    if (this.selectedProductId == null) return;
    const comp = this.competitorPrices[this.selectedProductId][index];
    comp.isEditing = true;
  }
  onCompetitorChange(comp: Competitor): void {
    // if you want live validation or propagation
    this.emitCostDataChange();
  }
}
